import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import './dpe.css';

const YEAR=2026, API='/api/digital';
const MN=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTHS=['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
const DEF_UNIVERSE=1887226; // Moldova All Adults 18+ (GfK/Nielsen)
const FLIGHT_COLORS=['#4472C4','#ED7D31','#70AD47','#FF0066','#7030A0','#00B0F0','#FFC000','#C00000','#1B5E8C','#44546A','#9C27B0','#009688'];

// ─── DATE UTILS ───────────────────────────────────────────────────────────────
// ISO 8601 week functions — weeks start Monday, W1 contains first Thursday of year
// All arithmetic in UTC to avoid timezone shifts
function _utcDate(y,m,d){return new Date(Date.UTC(y,m,d));}
function _utcDow(d){return(d.getUTCDay()+6)%7;} // Mon=0..Sun=6
function dateToWeek(s){
  if(!s)return null;
  const [y,m,d]=s.split('-').map(Number);
  const dt=_utcDate(y,m-1,d);
  // Shift to nearest Thursday
  const thu=new Date(dt);thu.setUTCDate(dt.getUTCDate()+3-_utcDow(dt));
  // W1 = week containing Jan 4
  const w1=_utcDate(thu.getUTCFullYear(),0,4);
  const w1thu=new Date(w1);w1thu.setUTCDate(w1.getUTCDate()+3-_utcDow(w1));
  const wn=1+Math.round((thu-w1thu)/604800000);
  return Math.max(1,Math.min(53,wn));
}
function weekStartDate(w){
  // W1 Mon = Jan 4 minus its weekday offset
  const jan4=_utcDate(YEAR,0,4);
  const w1mon=new Date(jan4);w1mon.setUTCDate(jan4.getUTCDate()-_utcDow(jan4));
  const mon=new Date(w1mon);mon.setUTCDate(w1mon.getUTCDate()+(w-1)*7);
  return mon.toISOString().slice(0,10);
}
function weekEndDate(w){
  const s=weekStartDate(w);const [y,m,d]=s.split('-').map(Number);
  return _utcDate(y,m-1,d+6).toISOString().slice(0,10);
}
function daysInWeek(w,s,e){const wS=new Date(weekStartDate(w)),wE=new Date(weekEndDate(w)),fS=new Date(s),fE=new Date(e);const a=fS>wS?fS:wS,b=fE<wE?fE:wE;return Math.max(0,Math.min(7,Math.floor((b-a)/86400000)+1));}
function weekMonth(w){const d=new Date(weekStartDate(w));return d.getMonth();}
function fdate(s){if(!s)return'—';const d=new Date(s);return d.getDate()+' '+MN[d.getMonth()];}
const MONTH_WEEKS=(()=>{const mw=Array(12).fill(0).map(()=>[]);for(let w=1;w<=52;w++)mw[weekMonth(w)].push(w);return mw;})();

const OBJECTIVES=[{id:'awareness',label:'Awareness',color:'#4472C4'},{id:'traffic',label:'Traffic',color:'#70AD47'},{id:'views',label:'Views',color:'#ED7D31'},{id:'conversion',label:'Conversion',color:'#FF0066'}];
const AD_TYPES=[{key:'video',label:'Video',color:'#4472C4'},{key:'banner',label:'Banner',color:'#70AD47'},{key:'branding',label:'Branding',color:'#ED7D31'},{key:'nonstandard',label:'Non-Standard',color:'#FF0066'},{key:'social',label:'Social',color:'#7030A0'},{key:'sem',label:'SEM',color:'#00B0F0'}];
const SUMMARY_GROUPS=[{key:'video',label:'Video',types:['video']},{key:'display_std',label:'Display Standard',types:['banner']},{key:'display_ns',label:'Display Non-Standard',types:['branding','nonstandard']},{key:'social',label:'Social',types:['social']},{key:'sem',label:'Search',types:['sem']}];

// ─── DEFAULT PLATFORMS ────────────────────────────────────────────────────────
const DEFAULT_PLATFORMS=[
  {id:'dv360_yt',        seller:'DV 360',     label:'DV 360 YouTube',           type:'video',      base_cpm:1.10,viewability:0.85,audience_comp:0.75, ctr:0.0010,vtr:0.70,weekly_frq:3, monthly_frq:12,real_users_coef:1.8,reach_pool:'yt',     priority:{awareness:10,views:10,traffic:6,conversion:3}},
  {id:'eskimi_video',    seller:'Eskimi',     label:'Eskimi In-Stream',         type:'video',      base_cpm:3.06,viewability:0.75,audience_comp:0.614,ctr:0.0012,vtr:0.70,weekly_frq:4, monthly_frq:16,real_users_coef:2.1,reach_pool:'video',  priority:{awareness:9, views:10,traffic:5,conversion:2}},
  {id:'teads_video',     seller:'Teads',      label:'Teads Out-Stream',         type:'video',      base_cpm:2.90,viewability:0.75,audience_comp:0.724,ctr:0.0008,vtr:0.70,weekly_frq:4, monthly_frq:16,real_users_coef:2.0,reach_pool:'video',  priority:{awareness:9, views:10,traffic:4,conversion:2}},
  {id:'dv360_video',     seller:'DV 360',     label:'DV 360 Out-Stream',        type:'video',      base_cpm:0.60,viewability:0.70,audience_comp:0.65, ctr:0.0008,vtr:0.50,weekly_frq:5, monthly_frq:20,real_users_coef:2.5,reach_pool:'video',  priority:{awareness:7, views:7, traffic:4,conversion:2}},
  {id:'megogo_video',    seller:'Megogo',     label:'Megogo In-Stream',         type:'video',      base_cpm:0,   viewability:0.90,audience_comp:0.70, ctr:0.0010,vtr:0.80,weekly_frq:3, monthly_frq:12,real_users_coef:1.6,reach_pool:'video',  priority:{awareness:8, views:9, traffic:4,conversion:2}},
  {id:'simpals_video',   seller:'Simpals',    label:'Simpals In-Stream',        type:'video',      base_cpm:0,   viewability:0.75,audience_comp:0.65, ctr:0.0010,vtr:0.65,weekly_frq:4, monthly_frq:16,real_users_coef:2.0,reach_pool:'video',  priority:{awareness:6, views:7, traffic:3,conversion:1}},
  {id:'dv360_display',   seller:'DV 360',     label:'DV 360 Display',           type:'banner',     base_cpm:0.40,viewability:0.70,audience_comp:0.64, ctr:0.0008,vtr:null,weekly_frq:7, monthly_frq:28,real_users_coef:3.5,reach_pool:'display',priority:{awareness:6, views:1, traffic:8,conversion:5}},
  {id:'eskimi_display',  seller:'Eskimi',     label:'Eskimi Display',           type:'banner',     base_cpm:0.66,viewability:0.70,audience_comp:0.65, ctr:0.0008,vtr:null,weekly_frq:7, monthly_frq:28,real_users_coef:3.2,reach_pool:'display',priority:{awareness:5, views:1, traffic:7,conversion:4}},
  {id:'simpals_display', seller:'Simpals',    label:'Simpals Display',          type:'banner',     base_cpm:0.45,viewability:0.65,audience_comp:0.65, ctr:0.0008,vtr:null,weekly_frq:7, monthly_frq:28,real_users_coef:2.8,reach_pool:'display',priority:{awareness:5, views:1, traffic:7,conversion:4}},
  {id:'meteofor_display',seller:'Meteofor.md',label:'Meteofor.md Display',      type:'banner',     base_cpm:0.58,viewability:0.65,audience_comp:0.60, ctr:0.0008,vtr:null,weekly_frq:6, monthly_frq:24,real_users_coef:2.5,reach_pool:'display',priority:{awareness:4, views:1, traffic:6,conversion:3}},
  {id:'simpals_bill',    seller:'Simpals',    label:'Simpals Billboard',        type:'banner',     base_cpm:0.87,viewability:0.65,audience_comp:0.65, ctr:0.0010,vtr:null,weekly_frq:6, monthly_frq:24,real_users_coef:2.5,reach_pool:'display',priority:{awareness:5, views:1, traffic:7,conversion:4}},
  {id:'simpals_skin',    seller:'Simpals',    label:'Simpals Fluid Skin',       type:'branding',   base_cpm:3.80,viewability:0.75,audience_comp:0.65, ctr:0.0030,vtr:null,weekly_frq:4, monthly_frq:16,real_users_coef:2.0,reach_pool:'display',priority:{awareness:8, views:1, traffic:5,conversion:2}},
  {id:'meteofor_brand',  seller:'Meteofor.md',label:'Meteofor.md Branding',     type:'branding',   base_cpm:1.67,viewability:0.95,audience_comp:0.664,ctr:0.0025,vtr:null,weekly_frq:3, monthly_frq:12,real_users_coef:1.5,reach_pool:'display',priority:{awareness:8, views:1, traffic:5,conversion:2}},
  {id:'eskimi_rm',       seller:'Eskimi',     label:'Eskimi Rich Media',        type:'nonstandard',base_cpm:0.66,viewability:0.70,audience_comp:0.70, ctr:0.0010,vtr:null,weekly_frq:5, monthly_frq:20,real_users_coef:2.8,reach_pool:'display',priority:{awareness:7, views:1, traffic:6,conversion:3}},
  {id:'simpals_rm',      seller:'Simpals',    label:'Simpals Rich Media',       type:'nonstandard',base_cpm:0.45,viewability:0.65,audience_comp:0.738,ctr:0.0007,vtr:null,weekly_frq:5, monthly_frq:20,real_users_coef:2.5,reach_pool:'display',priority:{awareness:7, views:1, traffic:6,conversion:3}},
  {id:'teads_rm',        seller:'Teads',      label:'Teads Rich Media',         type:'nonstandard',base_cpm:2.90,viewability:0.75,audience_comp:0.72, ctr:0.0008,vtr:null,weekly_frq:5, monthly_frq:20,real_users_coef:2.2,reach_pool:'display',priority:{awareness:6, views:1, traffic:5,conversion:3}},
  {id:'meta_multi',      seller:'Meta',       label:'Meta (FB+IG) Multiformat', type:'social',     base_cpm:0.90,viewability:0.40,audience_comp:0.75, ctr:0.0015,vtr:null,weekly_frq:10,monthly_frq:40,real_users_coef:1.2,reach_pool:'social', priority:{awareness:8, views:3, traffic:9,conversion:9}},
  {id:'ig_reels',        seller:'Instagram',  label:'Instagram Reels',          type:'social',     base_cpm:0.90,viewability:0.40,audience_comp:0.65, ctr:0.0020,vtr:null,weekly_frq:7, monthly_frq:28,real_users_coef:1.2,reach_pool:'social', priority:{awareness:7, views:4, traffic:8,conversion:8}},
  {id:'fb_feed',         seller:'Facebook',   label:'Facebook Feed',            type:'social',     base_cpm:0.90,viewability:0.40,audience_comp:0.70, ctr:0.0015,vtr:null,weekly_frq:8, monthly_frq:32,real_users_coef:1.3,reach_pool:'social', priority:{awareness:7, views:3, traffic:8,conversion:8}},
  {id:'linkedin',        seller:'LinkedIn',   label:'LinkedIn Newsfeed',        type:'social',     base_cpm:0,   viewability:0.50,audience_comp:0.40, ctr:0.0010,vtr:null,weekly_frq:5, monthly_frq:20,real_users_coef:1.5,reach_pool:'social', priority:{awareness:4, views:1, traffic:6,conversion:5}},
  {id:'gads_search',     seller:'Google Ads', label:'Google Ads Search',        type:'sem',        base_cpm:null,viewability:1.00,audience_comp:0.80, ctr:0.0200,vtr:null,weekly_frq:null,monthly_frq:null,real_users_coef:1.0,reach_pool:'display',priority:{awareness:1,views:1,traffic:10,conversion:10},cpc:0.20},
  {id:'gads_perfmax',    seller:'Google Ads', label:'Google Ads Perf. Max',     type:'sem',        base_cpm:null,viewability:1.00,audience_comp:0.75, ctr:0.0100,vtr:null,weekly_frq:null,monthly_frq:null,real_users_coef:1.0,reach_pool:'display',priority:{awareness:2,views:1,traffic:9,conversion:9},cpc:0.15},
];

// Default audiences — now with per-platform audience_comp overrides
const DEFAULT_AUDIENCES=[
  {id:'aud_all',   name:'All Adults 18+',        universe:1887226, platComp:{}},
  {id:'aud_women', name:'Women | Urban | 18-45',  universe:280000, platComp:{dv360_yt:0.72,eskimi_video:0.58,meta_multi:0.78,ig_reels:0.70,fb_feed:0.72}},
  {id:'aud_young', name:'All | 18-35',            universe:310000, platComp:{dv360_yt:0.68,meta_multi:0.72,ig_reels:0.72,fb_feed:0.65}},
  {id:'aud_men',   name:'Men | 18-54',            universe:251000, platComp:{dv360_yt:0.78,eskimi_video:0.65,meta_multi:0.71,ig_reels:0.58}},
];
// platComp = {platformId: audience_comp_override} — specific to this audience

// ─── REACH / CALC ─────────────────────────────────────────────────────────────
function smartAllocate(obj,budget,cat){
  const c=cat||DEFAULT_PLATFORMS,o=obj||'awareness';
  const avail=c.filter(p=>p.active!==false&&(p.base_cpm>0||(p.cpc&&p.cpc>0))).sort((a,b)=>(b.priority[o]||0)-(a.priority[o]||0));
  if(!avail.length)return[];
  const maxP=budget<3000?3:budget<8000?4:budget<20000?5:6;

  // For Awareness: rank all platforms by cost-per-real-user (CPM / viewability / audience_comp / real_users_coef)
  // Lower cost-per-real-user = better. Video capped at 50% to maintain format diversity.
  if(o==='awareness'){
    // Cost per real user index: lower is better
    function cpru(p){
      const cpm=p.base_cpm||0;
      if(cpm<=0)return Infinity;
      const view=p.viewability||0.7;
      const ac=p.audience_comp||0.65;
      const ruc=p.real_users_coef||2.0;
      return cpm/(view*ac*ruc);
    }
    const nonSem=avail.filter(p=>p.type!=='sem'&&(p.viewability||0)>=0.40);
    const videos=nonSem.filter(p=>p.type==='video').sort((a,b)=>cpru(a)-cpru(b)).slice(0,2);
    const nonVideo=nonSem.filter(p=>p.type!=='video').sort((a,b)=>cpru(a)-cpru(b)).slice(0,maxP-videos.length);
    const top=[...videos,...nonVideo];
    if(!top.length)return avail.slice(0,maxP).map((p,i,arr)=>({platformId:p.id,shr:i<arr.length-1?Math.floor(100/arr.length):100-Math.floor(100/arr.length)*(arr.length-1),discount:0,frq_override:null}));
    // Budget share proportional to inverse cpru (better efficiency = more budget)
    const scores=top.map(p=>1/(cpru(p)||0.001));
    // Video share capped at 50%
    const videoIdxs=top.map((p,i)=>p.type==='video'?i:-1).filter(i=>i>=0);
    const totalScore=scores.reduce((s,v)=>s+v,0)||1;
    let rawShares=scores.map(s=>s/totalScore*100);
    // Cap video total at 50%
    const videoTotal=videoIdxs.reduce((s,i)=>s+rawShares[i],0);
    if(videoTotal>50){
      const scale=50/videoTotal;
      let freed=0;
      videoIdxs.forEach(i=>{const orig=rawShares[i];rawShares[i]=orig*scale;freed+=orig-rawShares[i];});
      const nonVideoIdxs=top.map((_,i)=>i).filter(i=>!videoIdxs.includes(i));
      const nvTotal=nonVideoIdxs.reduce((s,i)=>s+rawShares[i],0)||1;
      nonVideoIdxs.forEach(i=>{rawShares[i]+=freed*(rawShares[i]/nvTotal);});
    }
    const shRaw=top.map((p,i)=>({platformId:p.id,shr:Math.round(rawShares[i]),discount:0,frq_override:null}));
    const sum=shRaw.reduce((s,x)=>s+x.shr,0);if(sum!==100&&shRaw.length>0)shRaw[0].shr+=(100-sum);
    return capByInventory(shRaw,top,budget,avail,maxP,o);
  }

  // Other objectives: priority-based allocation
  const top=avail.slice(0,maxP);
  const tot=top.reduce((s,p)=>s+(p.priority[o]||0),0)||1;
  const shRaw=top.map(p=>({platformId:p.id,shr:Math.round((p.priority[o]||0)/tot*100),discount:0,frq_override:null}));
  const shSum=shRaw.reduce((s,x)=>s+x.shr,0);if(shSum!==100&&shRaw.length>0)shRaw[0].shr+=(100-shSum);
  return capByInventory(shRaw,top,budget,avail,maxP,o);
}

function capByInventory(shRaw,top,budget,avail,maxP,o){
  let overflow=0;
  const capped=shRaw.map(s=>{
    const plat=top.find(p=>p.id===s.platformId);
    if(!plat||!plat.max_impressions||!plat.base_cpm||plat.base_cpm<=0)return s;
    const impliedBud=(s.shr/100)*budget;
    const impliedImpr=(impliedBud/plat.base_cpm)*1000;
    if(impliedImpr>plat.max_impressions){
      const maxBud=(plat.max_impressions/1000)*plat.base_cpm;
      const maxShr=Math.floor(maxBud/budget*100);
      overflow+=s.shr-maxShr;
      return{...s,shr:maxShr};
    }
    return s;
  });
  if(overflow>0){
    const used=capped.map(s=>s.platformId);
    const extras=avail.filter(p=>!used.includes(p.id)).slice(0,2);
    extras.forEach(ep=>{
      const add=Math.min(overflow,Math.round(overflow/extras.length));
      capped.push({platformId:ep.id,shr:add,discount:0,frq_override:null});
      overflow-=add;
    });
    if(overflow>0&&capped.length>0)capped[0].shr+=overflow;
  }
  const sum=capped.reduce((s,x)=>s+x.shr,0);if(sum!==100&&capped.length>0)capped[0].shr+=(100-sum);
  return capped;
}
// ─── SOLVE FOR TARGET ────────────────────────────────────────────────────────
// Binary search: find minimum budget to hit a target metric for given objective
function solveForTarget(targetVal,metric,obj,cat,univ,audience,flTemplate){
  // metric: 'reach'(RCH fraction)|'views'|'clicks'|'conversions'
  // Returns {budget, platforms, result} or null
  if(!targetVal||targetVal<=0)return null;
  let lo=200,hi=2000000,best=null;
  for(let i=0;i<28;i++){
    const mid=Math.round((lo+hi)/2/100)*100;
    const plats=smartAllocate(obj,mid,cat);
    const fl={...flTemplate,budget:mid,objective:obj,platforms:plats};
    const res=calcFlight(fl,cat,univ,audience);
    const val=metric==='reach'?res.tot.reach:metric==='views'?res.tot.views:metric==='clicks'?res.tot.clicks:res.tot.clicks*(fl.platforms.reduce((s,pe)=>{const p=cat.find(x=>x.id===pe.platformId);return s+(p?.cvr||0)*(pe.shr/100);},0)||0.02);
    if(val>=targetVal){best={budget:mid,platforms:plats,result:res,achievedVal:val};hi=mid;}
    else lo=mid;
    if(hi-lo<100)break;
  }
  return best;
}

function sb2(a,b){return a+b-a*b;}
function sbN(arr){return arr.reduce((a,b)=>sb2(a,b),0);}
function pReach(impr,view,ac,frq,ruc,univ){if(!impr||!frq||frq<=0)return 0;return Math.min(0.98,(impr*view*ac/(ruc||1)/frq)/univ);}
// Gemius-based pool reach: Sainsbury combination of per-platform real user reach
function poolReach(platforms,univ){
  if(!platforms.length||!univ)return 0;
  // Step 1: real users per platform = cookies / RUC × audComp
  // Step 2: per-platform reach = ru_i / universe
  // Step 3: Sainsbury combination across platforms in pool
  const reaches=platforms.map(p=>{
    const ru=(p.cookies||0)/(p.ruc||1)*(p.audComp||0.65);
    return Math.min(0.999,ru/univ);
  });
  return reaches.reduce((acc,r)=>acc+r-acc*r,0);
}
// Pool reach in absolutes (real users) using same Sainsbury logic × universe
function poolRealUsers(platforms,univ){
  return poolReach(platforms,univ)*univ;
}

function calcFlight(fl,cat,univ,audience){
  const c=cat||DEFAULT_PLATFORMS,u=univ||DEF_UNIVERSE;
  const empty={platforms:[],tot:{budget:0,impressions:0,vimpressions:0,views:0,clicks:0,reach:0,ecpm:0,vcpm:0,frequency:0,trackerCost:0,agencyFee:0},weekly:[],nWeeks:0,activeWeeks:[],startW:1,endW:1};
  if(!fl.platforms||!fl.platforms.length)return empty;
  const sd=fl.startDate||weekStartDate(fl.startWeek||1);
  const ed=fl.endDate||weekEndDate((fl.startWeek||1)+(fl.weeks||4)-1);
  const sw=dateToWeek(sd),ew=dateToWeek(ed);
  const wf=[];for(let w=sw;w<=ew;w++){const frac=daysInWeek(w,sd,ed)/7;if(frac>0.14)wf.push({week:w,frac});}
  const totFrac=wf.reduce((s,x)=>s+x.frac,0)||1;
  const tb=fl.budget||0;
  // Resolve weekly budgets respecting weeklyOverrides
  // Overridden weeks get their fixed budget; remaining budget distributed proportionally to free weeks
  const wOvr=fl.weeklyOverrides||{};
  const fixedTotal=wf.reduce((s,_,i)=>s+(wOvr[i]?.budget!=null?wOvr[i].budget:0),0);
  const freeFrac=wf.reduce((s,x,i)=>s+(wOvr[i]?.budget!=null?0:x.frac),0)||1;
  const freePool=Math.max(0,tb-fixedTotal);
  const wkBudgets=wf.map((x,i)=>wOvr[i]?.budget!=null?wOvr[i].budget:(x.frac/freeFrac)*freePool);
  const allTrackers=fl.trackers||[];
  const tCpm=(fl.trackers||[]).reduce((s,t)=>s+(t.type==='cpm'?(t.value||0):0),0);
  const tPct=(fl.trackers||[]).reduce((s,t)=>s+(t.type==='pct'?(t.value||0):0),0)/100;
  const res=(fl.platforms||[]).map(pe=>{
    const p=c.find(x=>x.id===pe.platformId);if(!p)return null;
    // Per-audience audience_comp override
    const audComp=audience?.platComp?.[p.id]??p.audience_comp??0.65;
    const bud=tb*((pe.shr||0)/100),disc=Math.max(0,Math.min(0.90,pe.discount||0));
    const tCpm=(allTrackers||fl.trackers||[]).reduce((s,t)=>s+(t.type==='cpm'&&(t.platforms==='all'||!t.platforms||t.platforms===p.id||t.platforms===pe.platformId)?(t.value||0):0),0);
    const tPct=(allTrackers||fl.trackers||[]).reduce((s,t)=>s+(t.type==='pct'&&(t.platforms==='all'||!t.platforms||t.platforms===p.id||t.platforms===pe.platformId)?(t.value||0):0),0)/100;
    const af=(pe.agency_fee_override!=null?pe.agency_fee_override:(fl.agency_fee||0))/100;
    const netB=af>0&&!fl.agency_on_top?bud/(1+af):bud;
    const afAmt=af>0?(fl.agency_on_top?bud*af:bud-netB):0;
    const tCost=netB*tPct+(netB>0?(netB/(p.base_cpm||1))*tCpm:0);
    const mBud=netB-tCost;
    if(p.type==='sem'){
      const cpc=(pe.cpc_override||p.cpc||0.20)*(1-disc);
      const clicks=cpc>0?mBud/cpc:0,impr=p.ctr>0?clicks/p.ctr:0,frq=pe.frq_override!=null?pe.frq_override:1;
      return{p,pe,bud,mBud,tCost,afAmt,cpm:null,cpc,impr,vimpr:impr,views:0,clicks,reach:pReach(impr,1,audComp,frq,1,u),frq,audComp};
    }
    const nowMonth=new Date().getMonth();
    const seasonMult=(p.seasonal_cpm&&p.seasonal_cpm[nowMonth])||1;
    const cpm=(p.base_cpm||0)*(1-disc)*seasonMult;
    if(!cpm)return{p,pe,bud,mBud,tCost,afAmt,cpm:0,impr:0,vimpr:0,views:0,clicks:0,reach:0,frq:0,audComp};
    const impr=(mBud/cpm)*1000,vimpr=impr*(p.viewability||0.7);
    const views=p.vtr?impr*p.vtr:0,clicks=impr*(p.ctr||0);
    const frq=pe.frq_override!=null?pe.frq_override:(p.weekly_frq||3);
    const nWks=wf.length||1;const uniqueCookies=frq>0?(impr/nWks)/frq:0; // weekly unique cookies = (total_impr/nWeeks)/frq
    const reach=pReach(impr,p.viewability||0.7,audComp,frq,p.real_users_coef||2.0,u);
    return{p,pe,bud,mBud,tCost,afAmt,cpm,impr,vimpr,views,clicks,reach,frq,audComp,uniqueCookies};
  }).filter(Boolean);
  // Gemius formula: per-pool weighted reach, then Sainsbury cross-pool
  const gPools={yt:[],social:[],other:[]};
  res.forEach(r=>{
    const wkCookies=(r.impr&&r.frq>0)?(r.impr/(wf.length||1))/r.frq:0; // weekly cookies
    // vRCH: apply viewability to get viewable cookies
    const vCookies=wkCookies*(r.p.viewability||0.7);
    const entry={cookies:vCookies,ruc:r.p.real_users_coef||2.0,audComp:r.audComp||r.p.audience_comp||0.65};
    if(r.p.reach_pool==='yt')gPools.yt.push(entry);
    else if(r.p.reach_pool==='social')gPools.social.push(entry);
    else gPools.other.push(entry);
  });
  const rYT=poolReach(gPools.yt,u);
  const rSocial=poolReach(gPools.social,u);
  const rOther=poolReach(gPools.other,u);
  const weeklyReach=Math.min(0.98,1-(1-rYT)*(1-rSocial)*(1-rOther));
  // Cumulative reach over all weeks using Sainsbury accumulation
  const nWksTotal=wf.length||1;
  let cumR=0;for(let i=0;i<nWksTotal;i++){cumR=cumR+weeklyReach-cumR*weeklyReach;}
  const reach=Math.min(0.98,cumR);
  const tot={budget:res.reduce((s,r)=>s+r.bud,0),impressions:res.reduce((s,r)=>s+r.impr,0),vimpressions:res.reduce((s,r)=>s+r.vimpr,0),views:res.reduce((s,r)=>s+r.views,0),clicks:res.reduce((s,r)=>s+r.clicks,0),trackerCost:res.reduce((s,r)=>s+r.tCost,0),agencyFee:res.reduce((s,r)=>s+r.afAmt,0),reach};
  tot.ecpm=tot.impressions>0?(tot.budget/tot.impressions)*1000:0;
  tot.vcpm=tot.vimpressions>0?(tot.budget/tot.vimpressions)*1000:0;
  tot.frequency=res.reduce((s,r)=>s+r.frq*(r.impr/(tot.impressions||1)),0);
  tot.uniqueCookies=res.reduce((s,r)=>s+(r.uniqueCookies||0),0);
  tot.taRealUsers=res.reduce((s,r)=>s+(r.uniqueCookies||0)/(r.p.real_users_coef||2.0)*(r.audComp||r.p.audience_comp||0.65),0);
  const weekly=wf.map((x,i)=>{
    const wb=wkBudgets[i];
    // Use per-week platform mix override if set, otherwise use flight-level mix
    const wkPlatforms=wOvr[i]?.platforms||null;
    const wp={yt:[],social:[],other:[]};
    // Build effective platform entries for this week
    const wkRes=wkPlatforms
      ? wkPlatforms.map(pe=>{
          const p=c.find(x=>x.id===pe.platformId);if(!p)return null;
          const audComp=audience?.platComp?.[p.id]??p.audience_comp??0.65;
          return{p,pe,frq:pe.frq_override!=null?pe.frq_override:(p.weekly_frq||3),audComp};
        }).filter(Boolean)
      : res;
    wkRes.forEach(r=>{
      const platBud=wb*((r.pe.shr||0)/100);
      const disc=Math.max(0,Math.min(0.90,r.pe.discount||0));
      const cpm=(r.p.base_cpm||0)*(1-disc);
      const wi=cpm>0?(platBud/cpm)*1000:0;
      const wCookies=(wi&&r.frq>0)?wi/r.frq:0;
      const entry={cookies:wCookies,ruc:r.p.real_users_coef||2.0,audComp:r.audComp||r.p.audience_comp||0.65};
      if(r.p.reach_pool==='yt')wp.yt.push(entry);
      else if(r.p.reach_pool==='social')wp.social.push(entry);
      else wp.other.push(entry);
    });
    const wrYT=poolReach(wp.yt,u);
    const wrSoc=poolReach(wp.social,u);
    const wrOth=poolReach(wp.other,u);
    const wReach=Math.min(0.98,1-(1-wrYT)*(1-wrSoc)*(1-wrOth));
    const wImpr=wkRes.reduce((s,r)=>{const platBud=wb*((r.pe.shr||0)/100);const disc=Math.max(0,Math.min(0.90,r.pe.discount||0));const cpm=(r.p.base_cpm||0)*(1-disc);return s+(cpm>0?(platBud/cpm)*1000:0);},0);
    const wVimpr=wkRes.reduce((s,r)=>{const platBud=wb*((r.pe.shr||0)/100);const disc=Math.max(0,Math.min(0.90,r.pe.discount||0));const cpm=(r.p.base_cpm||0)*(1-disc);const wi=cpm>0?(platBud/cpm)*1000:0;return s+wi*(r.p.viewability||0.7);},0);
    const wViews=wkRes.reduce((s,r)=>{const platBud=wb*((r.pe.shr||0)/100);const disc=Math.max(0,Math.min(0.90,r.pe.discount||0));const cpm=(r.p.base_cpm||0)*(1-disc);const wi=cpm>0?(platBud/cpm)*1000:0;return s+(r.p.vtr?wi*r.p.vtr:0);},0);
    const wClicks=wkRes.reduce((s,r)=>{const platBud=wb*((r.pe.shr||0)/100);const disc=Math.max(0,Math.min(0.90,r.pe.discount||0));const cpm=(r.p.base_cpm||0)*(1-disc);const wi=cpm>0?(platBud/cpm)*1000:0;return s+wi*(r.p.ctr||0);},0);
    return{week:x.week,frac:x.frac,budget:wb,impressions:wImpr,vimpressions:wVimpr,views:wViews,clicks:wClicks,reach:wReach,isOverridden:wOvr[i]?.budget!=null,hasMixOverride:!!wkPlatforms};
  });
  return{platforms:res,tot,weekly,wkBudgets,nWeeks:wf.length,activeWeeks:wf.map(x=>x.week),startW:sw,endW:ew,startDate:sd,endDate:ed,universe:u};
}

// ─── FORMATTERS ───────────────────────────────────────────────────────────────
const fe  =n=>(!isFinite(n)||isNaN(n)||n===0)?'—':'€'+Math.round(n).toLocaleString();
const fe2 =n=>(!isFinite(n)||isNaN(n))?'—':'€'+n.toFixed(2);
const fmil=n=>{if(!n||!isFinite(n))return'—';if(n>=1e6)return(n/1e6).toFixed(1)+'M';if(n>=1000)return Math.round(n/1000)+'K';return Math.round(n)+''};
const fn  =n=>{if(!n||!isFinite(n))return'—';if(n>=1e6)return(n/1e6).toFixed(1)+'M';if(n>=1000)return(n/1000).toFixed(0)+'K';return Math.round(n).toLocaleString()+''};
const fp  =n=>(!isFinite(n)||isNaN(n))?'—':(n*100).toFixed(1)+'%';
const ff  =n=>(!isFinite(n)||isNaN(n)||n===0)?'—':n.toFixed(1);

let _uid=1;function mkId(){return 'f'+(++_uid)+'_'+Date.now();}
function mkFlight(idx,brand,cat){const obj='awareness',budget=10000;return{id:mkId(),label:'Flight '+(idx+1),brand:brand||'Brand 1',color:FLIGHT_COLORS[idx%FLIGHT_COLORS.length],startDate:'',endDate:'',startWeek:1,weeks:4,budget,objective:obj,audienceId:null,platforms:smartAllocate(obj,budget,cat),weeklyOverrides:{},agency_fee:0,agency_on_top:false,trackers:[]};}

const TH={padding:'4px 6px',color:'var(--text3)',fontWeight:600,fontSize:9,border:'1px solid var(--border)',textAlign:'center',whiteSpace:'nowrap',background:'var(--gold-bg)'};
const TD={padding:'3px 6px',border:'1px solid var(--border)',verticalAlign:'middle'};
const TDr={...TD,textAlign:'right'};
const TDc={...TD,textAlign:'center'};

// ─── BRAND HEADER ─────────────────────────────────────────────────────────────
function BrandHeader({brand,onRename,onAddFlight}){
  const[editing,setEditing]=useState(false),[val,setVal]=useState(brand);
  function commit(){setEditing(false);onRename(val);}
  return(<div style={{padding:'6px 10px 2px',fontSize:9,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'.07em',display:'flex',alignItems:'center',gap:4,borderTop:'2px solid var(--border)'}}>
    {editing?<input autoFocus value={val} className="inp" style={{fontSize:9,fontWeight:700,textTransform:'uppercase',flex:1,padding:'1px 4px'}} onChange={e=>setVal(e.target.value)} onBlur={commit} onKeyDown={e=>{if(e.key==='Enter')commit();if(e.key==='Escape'){setVal(brand);setEditing(false);}}}/>
      :<span style={{flex:1,cursor:'pointer'}} onClick={()=>{setVal(brand);setEditing(true);}}>{brand} ✎</span>}
    <button className="btn btn-xs btn-ghost" onClick={onAddFlight}>+ Flight</button>
  </div>);
}

// ─── PLATFORM MIX TABLE ───────────────────────────────────────────────────────
function PlatformMixTable({fl,result,onUpdate,catalogue,mixLocked,setMixLocked}){
  const[freeMode,setFreeMode]=useState(false); // false=auto-rebalance, true=free edit
  function rebal(pls,ci,nv){const a=pls.map((p,i)=>i===ci?{...p,shr:nv}:p),rest=a.filter((_,i)=>i!==ci),rs=rest.reduce((s,p)=>s+(p.shr||0),0)||1,rem=Math.max(0,100-nv);const rb=a.map((p,i)=>i===ci?p:{...p,shr:Math.max(1,Math.round((p.shr||0)/rs*rem))});const sum=rb.reduce((s,p)=>s+p.shr,0);if(sum!==100&&rb.length>1){const fi=rb.findIndex((_,i)=>i!==ci);if(fi>=0)rb[fi].shr+=(100-sum);}return rb;}
  function freeShr(pls,ci,nv){return pls.map((p,i)=>i===ci?{...p,shr:Math.max(0,nv)}:p);}
  function removePl(pi){const pls=fl.platforms.filter((_,j)=>j!==pi);if(!pls.length){onUpdate({platforms:[]});return;}const t=pls.reduce((s,p)=>s+(p.shr||0),0)||1;const rb=pls.map(p=>({...p,shr:Math.round((p.shr||0)/t*100)}));const s=rb.reduce((s,p)=>s+p.shr,0);if(s!==100)rb[0].shr+=(100-s);onUpdate({platforms:rb});}
  function maxImprWarning(pe,p,bud){
    if(!p||!p.max_impressions||!p.base_cpm||p.base_cpm<=0)return null;
    const estImpr=(bud/p.base_cpm)*1000;
    if(estImpr>p.max_impressions)return'⚠ Est. '+Math.round(estImpr/1000)+'K impr > max '+Math.round(p.max_impressions/1000)+'K';
    return null;
  }
  function addPl(id){if(fl.platforms.some(p=>p.platformId===id))return;const n=fl.platforms.length,pls=fl.platforms.map(p=>({...p,shr:Math.round((p.shr||0)*(n/(n+1)))}));const sum=pls.reduce((s,p)=>s+(p.shr||0),0);pls.push({platformId:id,shr:100-sum,discount:0,frq_override:null});onUpdate({platforms:pls});}
  const totalShr=(fl.platforms||[]).reduce((s,p)=>s+(p.shr||0),0);
  const shrOk=Math.abs(totalShr-100)<1;
  return(<div>
    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
      <div style={{display:'flex',alignItems:'center',gap:4,fontSize:9,background:freeMode?'var(--gold-bg)':'var(--surface3)',border:'1px solid '+(freeMode?'var(--gold-border)':'var(--border)'),borderRadius:12,padding:'2px 8px',cursor:'pointer'}} onClick={()=>setFreeMode(f=>!f)}>
        <span style={{fontWeight:700,color:freeMode?'var(--gold-dark)':'var(--text3)'}}>{freeMode?'✎ Free edit':'⟳ Auto-balance'}</span>
        <span style={{fontSize:8,color:'var(--text3)'}}>{freeMode?'(shares independent)':'(others rebalance)'}</span>
      </div>
      {setMixLocked&&<div title={mixLocked?'Mix locked — budget changes keep current shares':'Mix unlocked — budget changes recalculate mix'} onClick={()=>setMixLocked(l=>!l)} style={{display:'flex',alignItems:'center',gap:3,fontSize:9,padding:'2px 8px',borderRadius:12,border:'1px solid '+(mixLocked?'var(--red)':'var(--border)'),background:mixLocked?'#ff000015':'var(--surface3)',color:mixLocked?'var(--red)':'var(--text3)',cursor:'pointer',fontWeight:mixLocked?700:400}}>
        {mixLocked?'🔒 Mix locked':'🔓 Mix unlocked'}
      </div>}
      <div style={{fontSize:10,fontWeight:700,color:shrOk?'var(--text3)':totalShr>100?'var(--red)':'var(--amber)',marginLeft:4}}>
        Total: {totalShr}%{!shrOk&&<span style={{fontWeight:400,fontSize:9,marginLeft:4}}>{totalShr>100?'⚠ Over 100%':'⚠ Under 100% — remaining budget unallocated'}</span>}
      </div>
    </div>
    <div style={{overflowX:'auto'}}><table style={{width:'100%',borderCollapse:'collapse',fontSize:11,minWidth:480}}>
      <thead><tr>
        <th style={{...TH,textAlign:'left'}}>Platform</th>
        <th style={{...TH,width:54}}>Shr%</th><th style={{...TH,width:58}}>CPM €</th>
        <th style={{...TH,width:46}}>Disc%</th><th style={{...TH,width:52}}>Avg FRQ/wk</th><th style={{...TH,width:36}} title='Agency fee % per platform'>AF%</th>
        <th style={{...TH,width:64}}>Budget</th><th style={{...TH,width:52}}>Impr.</th><th style={{...TH,width:18}}/>
      </tr></thead>
      <tbody>{(()=>{
        // Deduplicate: remove entries where platformId resolves to same label as an earlier entry
        const seen=new Set();
        return (fl.platforms||[]).filter(pe=>{
          const p=catalogue.find(x=>x.id===pe.platformId)||DEFAULT_PLATFORMS.find(x=>x.id===pe.platformId);
          if(!p)return false;
          const key=p.label.toLowerCase().trim();
          if(seen.has(key))return false;
          seen.add(key);return true;
        });
      })().map((pe,pi)=>{
        const p=catalogue.find(x=>x.id===pe.platformId)||DEFAULT_PLATFORMS.find(x=>x.id===pe.platformId);if(!p)return null;
        const pr=result?.platforms?.find(r=>r.p?.id===pe.platformId);
        const typ=AD_TYPES.find(t=>t.key===p.type);
        const eff=p.base_cpm?p.base_cpm*(1-(pe.discount||0)):null;
        const frqV=pe.frq_override!=null?pe.frq_override:(p.weekly_frq||3);
        return(<tr key={pe.platformId}>
          <td style={TD}><div style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:7,height:7,borderRadius:'50%',background:typ?.color||'#ccc',flexShrink:0,display:'inline-block'}}/><span style={{fontSize:10,fontWeight:500}}>{p.label}</span></div></td>
          <td style={TDc}><input className="mono-inp" type="number" min={0} max={freeMode?999:100} value={pe.shr||0} style={{width:'100%',textAlign:'center',background:freeMode?'var(--gold-bg)':'transparent'}} onFocus={e=>e.target.select()} onChange={e=>onUpdate({platforms:freeMode?freeShr(fl.platforms,pi,+e.target.value):rebal(fl.platforms,pi,Math.max(0,Math.min(100,+e.target.value)))})}/></td>
          <td style={{...TDc,color:'var(--gold-dark)',fontWeight:600,fontSize:10}}>{p.type==='sem'?'CPC':eff?eff.toFixed(2):'manual'}</td>
          <td style={TDc}><input className="mono-inp" type="number" min={0} max={80} step={5} value={Math.round((pe.discount||0)*100)} style={{width:'100%',textAlign:'center'}} onChange={e=>onUpdate({platforms:fl.platforms.map((p2,j)=>j!==pi?p2:{...p2,discount:+e.target.value/100})})}/></td>
          <td style={{...TDc,position:'relative'}}>{(()=>{const w=maxImprWarning(pe,p,fl.budget*(pe.shr/100));return w?<div style={{position:'absolute',top:-16,left:0,right:0,fontSize:8,color:'var(--amber)',whiteSpace:'nowrap',zIndex:1}}>{w}</div>:null;})()}{p.type==='sem'?<span style={{color:'var(--text3)',fontSize:9}}>—</span>:<input className="mono-inp" type="number" min={1} max={30} step={0.5} value={frqV} style={{width:'100%',textAlign:'center',background:pe.frq_override!=null?'#FBF7F2':'transparent'}} title={pe.frq_override!=null?'Override':'Default'} onFocus={e=>e.target.select()} onChange={e=>onUpdate({platforms:fl.platforms.map((p2,j)=>j!==pi?p2:{...p2,frq_override:+e.target.value})})}/>}</td>
          <td style={{...TDc,minWidth:40}} title="Agency fee % for this platform (blank=flight default)"><input className="mono-inp" type="number" inputMode="decimal" step={0.1} min={0} max={100} placeholder={String(fl.agency_fee||0)} key={'af_'+pe.platformId} defaultValue={pe.agency_fee_override!=null?pe.agency_fee_override:''} style={{width:'100%',textAlign:'center',background:pe.agency_fee_override!=null?'var(--gold-bg)':'transparent',fontSize:9}} onFocus={e=>e.target.select()} onBlur={e=>onUpdate({platforms:fl.platforms.map((p2,j)=>j!==pi?p2:{...p2,agency_fee_override:e.target.value===''?null:parseFloat(e.target.value)??null})})}/></td>
          <td style={{...TDc,color:'var(--gold-dark)',fontWeight:600,fontSize:10}}>{fe(pr?.bud)}</td>
          <td style={{...TDc,fontSize:10}}>{fmil(pr?.impr)}</td>
          <td style={{...TDc,padding:2}}><button style={{color:'var(--red)',border:'none',background:'transparent',cursor:'pointer',padding:'0 4px',fontSize:14,fontWeight:700}} onClick={()=>removePl(pi)}>×</button></td>
        </tr>);
      })}</tbody>
    </table></div>
    <div style={{display:'flex',gap:6,marginTop:8}}>
      <select className="sel" style={{flex:1}} value="" onChange={e=>{if(e.target.value)addPl(e.target.value);e.target.value='';}}>
        <option value="">+ Add platform…</option>
        {AD_TYPES.map(t=>({type:t,plats:(()=>{const seen=new Set();return catalogue.filter(p=>p.type===t.key&&p.active!==false).filter(p=>{const k=p.label.toLowerCase().trim();if(seen.has(k))return false;seen.add(k);return true;});})()})).map(({type:t,plats})=>(<optgroup key={t.key} label={t.label}>{plats.map(p=>(<option key={p.id} value={p.id} disabled={fl.platforms.some(x=>x.platformId===p.id)}>{p.label}{p.base_cpm?' (€'+p.base_cpm+')':p.cpc?' (CPC €'+p.cpc+')':' (manual)'}</option>))}</optgroup>))}
      </select>
      <button className="btn btn-ghost btn-sm" onClick={()=>onUpdate({platforms:smartAllocate(fl.objective,fl.budget,catalogue)})}>↺ Re-opt.</button>
    </div>
  </div>);
}

// ─── FLIGHT MODAL ─────────────────────────────────────────────────────────────
// ─── FLIGHT BUDGET / TARGET TOGGLE ──────────────────────────────────────────
function FlightBudgetTarget({fl,onUpdate,catalogue,result,universe,audiences,mixLocked}){
  const[mode,setMode]=React.useState('budget'); // 'budget' | 'target'
  const[targetInput,setTargetInput]=React.useState('');
  const[solving,setSolving]=React.useState(false);
  const[solveResult,setSolveResult]=React.useState(null);

  const OBJ_META={
    awareness:{metric:'reach',label:'Target RCH%',unit:'%',min:1,max:95,step:1,toVal:v=>v/100,fromVal:v=>Math.round(v*100)},
    views:    {metric:'views',label:'Target Views',unit:'views',min:1000,max:10000000,step:1000,toVal:v=>v,fromVal:v=>Math.round(v)},
    traffic:  {metric:'clicks',label:'Target Clicks',unit:'clicks',min:100,max:1000000,step:100,toVal:v=>v,fromVal:v=>Math.round(v)},
    conversion:{metric:'conversions',label:'Target Conversions',unit:'conv.',min:10,max:100000,step:10,toVal:v=>v,fromVal:v=>Math.round(v)},
  };
  const meta=OBJ_META[fl.objective]||OBJ_META.awareness;
  const univ=universe?.universe||1887226;
  const audObj=universe||null;

  function runSolve(){
    const raw=parseFloat(targetInput);
    if(!raw||raw<=0)return;
    setSolving(true);setSolveResult(null);
    setTimeout(()=>{
      const targetVal=meta.toVal(raw);
      const sol=solveForTarget(targetVal,meta.metric,fl.objective,catalogue,univ,audObj,fl);
      setSolving(false);
      if(sol){setSolveResult({budget:sol.budget,achievedVal:meta.fromVal(sol.achievedVal)});}
      else{setSolveResult({error:'Cannot reach target with available platforms'});}
    },30);
  }

  function applyResult(){
    if(!solveResult||solveResult.error)return;
    const plats=smartAllocate(fl.objective,solveResult.budget,catalogue);
    onUpdate({budget:solveResult.budget,platforms:plats});
    setSolveResult(null);setTargetInput('');setMode('budget');
  }

  if(mode==='budget') return(
    <div style={{marginBottom:12}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:4}}>
        <div className="lbl" style={{margin:0}}>Budget (EUR net media)</div>
        <button className="btn btn-ghost btn-xs" style={{fontSize:9,padding:'1px 6px',color:'var(--gold-dark)',border:'1px solid var(--gold-border)'}} onClick={()=>{setMode('target');setSolveResult(null);}}>⇄ Plan by {meta.label.replace('Target ','')}</button>
      </div>
      <input className="inn" type="number" min={0} step={500} value={fl.budget} style={{width:'100%'}} onChange={e=>onUpdate(mixLocked?{budget:Math.max(0,+e.target.value)}:{budget:Math.max(0,+e.target.value),platforms:smartAllocate(fl.objective,+e.target.value,catalogue)})}/>
      <input type="range" min={500} max={200000} step={500} value={fl.budget} style={{width:'100%',marginTop:4}} onChange={e=>onUpdate(mixLocked?{budget:+e.target.value}:{budget:+e.target.value,platforms:smartAllocate(fl.objective,+e.target.value,catalogue)})}/>
    </div>
  );

  return(
    <div style={{marginBottom:12,padding:'10px 12px',background:'var(--gold-bg)',border:'1px solid var(--gold-border)',borderRadius:6}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
        <span style={{fontWeight:700,fontSize:11,color:'var(--gold-dark)'}}>🎯 {meta.label}</span>
        <button className="btn btn-ghost btn-xs" style={{fontSize:9,padding:'1px 6px'}} onClick={()=>{setMode('budget');setSolveResult(null);}}>⇄ Plan by Budget</button>
      </div>
      <div style={{display:'flex',gap:6,alignItems:'center',marginBottom:8}}>
        <input className="inn" type="number" min={meta.min} max={meta.max} step={meta.step} value={targetInput} placeholder={meta.unit==='%'?'e.g. 30':'e.g. '+(meta.min*10)} style={{flex:1}} onChange={e=>setTargetInput(e.target.value)}
          onKeyDown={e=>{if(e.key==='Enter')runSolve();}}/>
        <span style={{fontSize:11,color:'var(--text3)',minWidth:30}}>{meta.unit}</span>
        <button className="btn btn-primary btn-sm" onClick={runSolve} disabled={solving||!targetInput} style={{whiteSpace:'nowrap'}}>{solving?'⏳ Solving…':'Calculate'}</button>
      </div>
      {solveResult&&!solveResult.error&&(
        <div style={{background:'var(--surface2)',borderRadius:6,padding:'8px 10px',border:'1px solid var(--gold-border)'}}>
          <div style={{fontSize:10,color:'var(--text3)',marginBottom:4}}>Optimal plan found:</div>
          <div style={{display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
            <div><span style={{fontSize:9,color:'var(--text3)'}}>Min. Budget </span><span style={{fontWeight:800,fontSize:13,color:'var(--gold-dark)'}}>€{solveResult.budget.toLocaleString()}</span></div>
            <div><span style={{fontSize:9,color:'var(--text3)'}}>Achieved </span><span style={{fontWeight:800,fontSize:13,color:'var(--gold-dark)'}}>{solveResult.achievedVal}{meta.unit}</span></div>
            <button className="btn btn-primary btn-sm" style={{marginLeft:'auto'}} onClick={applyResult}>Apply →</button>
          </div>
        </div>
      )}
      {solveResult?.error&&<div style={{fontSize:10,color:'var(--red)',padding:'6px 0'}}>{solveResult.error}</div>}
      <div style={{fontSize:9,color:'var(--text3)',marginTop:6}}>Current budget: <b>€{fl.budget.toLocaleString()}</b> · Current result: <b>{result?.tot?meta.fromVal(result.tot[meta.metric]==='reach'?result.tot.reach:result.tot[meta.metric]):0}{meta.unit}</b></div>
    </div>
  );
}

// ─── WEEKLY MIX MODAL ────────────────────────────────────────────────────────
function WeeklyMixModal({weekIdx,fl,result,onUpdate,catalogue,onClose}){
  const rw=result?.weekly?.[weekIdx];
  const wkOvr=fl.weeklyOverrides?.[weekIdx]||{};
  const basePlatforms=wkOvr.platforms||fl.platforms;
  const[platforms,setPlatforms]=useState(()=>basePlatforms.map(p=>({...p})));
  const[freeMode,setFreeMode]=useState(false);

  function rebal(pls,ci,nv){const a=pls.map((p,i)=>i===ci?{...p,shr:nv}:p),rest=a.filter((_,i)=>i!==ci),rs=rest.reduce((s,p)=>s+(p.shr||0),0)||1,rem=Math.max(0,100-nv);const rb=a.map((p,i)=>i===ci?p:{...p,shr:Math.max(1,Math.round((p.shr||0)/rs*rem))});const sum=rb.reduce((s,p)=>s+p.shr,0);if(sum!==100&&rb.length>1){const fi=rb.findIndex((_,i)=>i!==ci);if(fi>=0)rb[fi].shr+=(100-sum);}return rb;}
  function freeShr(pls,ci,nv){return pls.map((p,i)=>i===ci?{...p,shr:Math.max(0,nv)}:p);}

  function apply(){
    onUpdate({weeklyOverrides:{...(fl.weeklyOverrides||{}),[weekIdx]:{...wkOvr,platforms}}});
    onClose();
  }
  function reset(){
    const ovr={...(fl.weeklyOverrides||{})};
    if(ovr[weekIdx]){delete ovr[weekIdx].platforms;if(!ovr[weekIdx].budget)delete ovr[weekIdx];}
    onUpdate({weeklyOverrides:ovr});
    onClose();
  }

  const totalShr=platforms.reduce((s,p)=>s+(p.shr||0),0);
  const shrOk=Math.abs(totalShr-100)<1;
  const wb=rw?.budget||0;

  return(<div className="modal-overlay" onClick={ev=>{if(ev.target===ev.currentTarget)onClose();}}>
    <div className="modal-box" style={{maxWidth:520}} onClick={e=>e.stopPropagation()}>
      <div className="modal-hdr">
        <span style={{fontWeight:700,fontSize:13}}>✎ Platform Mix — W{rw?.week} {rw?'('+fdate(weekStartDate(rw.week))+')':''}</span>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
      </div>
      <div style={{padding:'12px 16px'}}>
        <div style={{fontSize:10,color:'var(--text3)',marginBottom:10}}>Week budget: <b>{fe(wb)}</b> · Override the platform split for this week only. Other weeks are unaffected.</div>
        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
          <div style={{display:'flex',alignItems:'center',gap:4,fontSize:9,background:freeMode?'var(--gold-bg)':'var(--surface3)',border:'1px solid '+(freeMode?'var(--gold-border)':'var(--border)'),borderRadius:12,padding:'2px 8px',cursor:'pointer'}} onClick={()=>setFreeMode(f=>!f)}>
            <span style={{fontWeight:700,color:freeMode?'var(--gold-dark)':'var(--text3)'}}>{freeMode?'✎ Free edit':'⟳ Auto-balance'}</span>
          </div>
          <div style={{fontSize:10,fontWeight:700,color:shrOk?'var(--text3)':totalShr>100?'var(--red)':'var(--amber)'}}>Total: {totalShr}%</div>
        </div>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
          <thead><tr><th style={TH}>Platform</th><th style={{...TH,width:60}}>Shr%</th><th style={{...TH,width:80}}>Budget €</th><th style={{...TH,width:60}}>Impr.</th><th style={{...TH,width:64}}>FRQ/wk</th></tr></thead>
          <tbody>{platforms.map((pe,pi)=>{
            const p=catalogue.find(x=>x.id===pe.platformId)||DEFAULT_PLATFORMS.find(x=>x.id===pe.platformId);if(!p)return null;
            const typ=AD_TYPES.find(t=>t.key===p.type);
            const platBud=wb*(pe.shr||0)/100;
            const disc=pe.discount||0;
            const cpm=(p.base_cpm||0)*(1-disc);
            const impr=cpm>0?(platBud/cpm)*1000:0;
            const frqVal=pe.frq_override!=null?pe.frq_override:(p.weekly_frq||3);
            const hasfrqOvr=pe.frq_override!=null;
            return(<tr key={pe.platformId}>
              <td style={TD}><div style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:6,height:6,borderRadius:'50%',background:typ?.color||'#ccc',display:'inline-block'}}/><span style={{fontSize:10}}>{p.label}</span></div></td>
              <td style={TDc}><input className="mono-inp" type="number" min={0} max={freeMode?999:100} value={pe.shr||0} style={{width:'100%',textAlign:'center',background:freeMode?'var(--gold-bg)':'transparent'}} onFocus={e=>e.target.select()} onChange={e=>setPlatforms(freeMode?freeShr(platforms,pi,+e.target.value):rebal(platforms,pi,Math.max(0,Math.min(100,+e.target.value))))}/></td>
              <td style={{...TDc,color:'var(--gold-dark)',fontSize:10,fontWeight:600}}>{fe(platBud)}</td>
              <td style={{...TDc,fontSize:10}}>{fmil(impr)}</td>
              <td style={TDc}>{p.type!=='sem'?<input className="mono-inp" type="number" min={1} max={30} step={0.5} value={frqVal} style={{width:'100%',textAlign:'center',background:hasfrqOvr?'var(--gold-bg)':'transparent',fontSize:10}} title={hasfrqOvr?'FRQ override':'Default FRQ'} onFocus={e=>e.target.select()} onChange={e=>setPlatforms(platforms.map((p2,j)=>j!==pi?p2:{...p2,frq_override:+e.target.value}))} onDoubleClick={()=>setPlatforms(platforms.map((p2,j)=>j!==pi?p2:{...p2,frq_override:null}))}/>:<span style={{color:'var(--text3)',fontSize:9}}>—</span>}</td>
            </tr>);
          })}</tbody>
        </table>
        <div style={{fontSize:9,color:'var(--text3)',marginTop:4}}>💡 Double-click FRQ to reset to platform default</div>
      </div>
      <div className="modal-footer">
        {wkOvr.platforms&&<button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={reset}>✕ Reset to flight default</button>}
        <div style={{flex:1}}/>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={apply}>Apply to W{rw?.week}</button>
      </div>
    </div>
  </div>);
}

function FlightModal({fl,result,onClose,onUpdate,onClone,onRemove,canRemove,catalogue,audiences}){
  const tot=result&&result.tot;
  const obj=OBJECTIVES.find(o=>o.id===fl.objective)||OBJECTIVES[0];
  const[wkMixModal,setWkMixModal]=useState(null);
  const[mixLocked,setMixLocked]=useState(false);
  function setDates(s,e){
    // Guard: only process valid ISO dates (YYYY-MM-DD)
    if(!s||!e||!/^\d{4}-\d{2}-\d{2}$/.test(s)||!/^\d{4}-\d{2}-\d{2}$/.test(e))return;
    const sw=dateToWeek(s),ew=dateToWeek(e);
    const safeE=ew>=sw?e:weekEndDate(sw);
    const safeEw=ew>=sw?ew:sw;
    onUpdate({startDate:s,endDate:safeE,startWeek:sw,weeks:Math.max(1,safeEw-sw+1)});
  }
  return(<div className="modal-overlay" onClick={ev=>{if(ev.target===ev.currentTarget)onClose();}}>
    <div className="modal-box" onClick={e=>e.stopPropagation()}>
      <div className="modal-hdr">
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <div style={{width:10,height:10,borderRadius:'50%',background:fl.color,flexShrink:0}}/>
          <input value={fl.label} onChange={e=>onUpdate({label:e.target.value})} style={{fontWeight:700,fontSize:13,background:'transparent',border:'none',borderBottom:'1px solid var(--border)',outline:'none',color:'var(--text1)',width:160,padding:'0 2px'}} placeholder="Flight name"/>
          <span style={{fontSize:9,color:'var(--text3)'}}>{fl.brand}</span>
          <span style={{fontSize:9,padding:'2px 7px',borderRadius:8,background:obj.color+'22',color:obj.color,fontWeight:700}}>{obj.label}</span>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>✕ Close</button>
      </div>
      <div className="modal-body">
        <div className="modal-col modal-col-left">
          <div style={{marginBottom:12}}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:6}}>
              <div>
                <div className="lbl">Start date</div>
                <input className="inp" type="date" key={'sd-'+fl.id} defaultValue={fl.startDate||''}
                  onBlur={e=>{var sd=e.target.value;if(!sd||sd.length!==10)return;var sw=dateToWeek(sd)||fl.startWeek;var ew=fl.endDate?dateToWeek(fl.endDate):null;var wks=ew&&ew>=sw?ew-sw+1:fl.weeks;onUpdate({startDate:sd,startWeek:sw,weeks:wks});}}/>
              </div>
              <div>
                <div className="lbl">End date</div>
                <input className="inp" type="date" key={'ed-'+fl.id} defaultValue={fl.endDate||''}
                  onBlur={e=>{var ed=e.target.value;if(!ed||ed.length!==10)return;var ew=dateToWeek(ed)||fl.startWeek+fl.weeks-1;var wks=Math.max(1,ew-fl.startWeek+1);onUpdate({endDate:ed,weeks:wks});}}/>
              </div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
              <div>
                <div className="lbl">Start week (override)</div>
                <input className="inn" type="number" min={1} max={52} value={fl.startWeek} style={{width:'100%'}} onChange={e=>onUpdate({startWeek:Math.min(52,Math.max(1,+e.target.value)),startDate:''})}/>
              </div>
              <div>
                <div className="lbl">Duration (weeks)</div>
                <input className="inn" type="number" min={1} max={52} value={fl.weeks} style={{width:'100%'}} onChange={e=>onUpdate({weeks:Math.min(52,Math.max(1,+e.target.value)),endDate:''})}/>
              </div>
            </div>
          </div>
          <div style={{marginBottom:12}}>
            <div className="lbl">Target Audience</div>
            <select className="sel" style={{width:'100%'}} value={fl.audienceId||''} onChange={e=>onUpdate({audienceId:e.target.value||null})}>
              <option value="">— plan default (All Adults 18+) —</option>
              {(audiences||[]).map(a=><option key={a.id} value={a.id}>{a.name} ({a.universe.toLocaleString()})</option>)}
            </select>
            {fl.audienceId&&audiences&&(()=>{const a=audiences.find(x=>x.id===fl.audienceId);const ovrCount=Object.keys(a?.platComp||{}).length;return ovrCount>0&&<div style={{fontSize:9,color:'var(--gold-dark)',marginTop:2}}>⚡ {ovrCount} platform audience_comp overrides active</div>;})()}
          </div>
          <div style={{marginBottom:12}}>
            <div className="lbl">Objective</div>
            <div style={{display:'flex',gap:3}}>{OBJECTIVES.map(o=>(<button key={o.id} className="btn btn-sm" onClick={()=>onUpdate(mixLocked?{objective:o.id}:{objective:o.id,platforms:smartAllocate(o.id,fl.budget,catalogue)})} style={{flex:1,background:fl.objective===o.id?o.color:'var(--surface2)',color:fl.objective===o.id?'#fff':'var(--text2)',border:'1px solid '+(fl.objective===o.id?o.color:'var(--border)'),fontSize:10}}>{o.label}</button>))}</div>
          </div>
          <FlightBudgetTarget fl={fl} onUpdate={onUpdate} catalogue={catalogue} result={result} universe={fl.audienceId&&audiences?audiences.find(a=>a.id===fl.audienceId):null} audiences={audiences} mixLocked={mixLocked}/>
          <div style={{marginBottom:12,padding:'10px 12px',background:'var(--surface3)',borderRadius:6,border:'1px solid var(--border)'}}>
            <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:8}}>
              <div style={{flex:1}}><div className="lbl">Agency fee %</div><input className="inn" type="text" inputMode="decimal" value={fl.agency_fee||0} style={{width:'100%'}} onFocus={e=>e.target.select()} onChange={e=>onUpdate({agency_fee:parseFloat(e.target.value)||0})}/></div>
              <div style={{paddingTop:16}}><label style={{display:'flex',alignItems:'center',gap:4,fontSize:10,cursor:'pointer',whiteSpace:'nowrap'}}><input type="checkbox" checked={!!fl.agency_on_top} onChange={e=>onUpdate({agency_on_top:e.target.checked})}/>On top</label></div>
            </div>
            <div className="lbl" style={{marginBottom:4}}>3rd party trackers</div>
            {(fl.trackers||[]).map((tr,ti)=>(<div key={ti} style={{display:'flex',gap:4,alignItems:'center',marginBottom:4}}>
              <input className="inn" value={tr.name||''} placeholder="Gemius/DV/IAS…" style={{flex:1,fontSize:10}} onChange={e=>{const t=[...(fl.trackers||[])];t[ti]={...t[ti],name:e.target.value};onUpdate({trackers:t});}}/>
              <select className="sel" title="Apply to platform" value={tr.platforms||'all'} style={{width:90,fontSize:9,maxWidth:90}} onChange={e=>{const t=[...(fl.trackers||[])];t[ti]={...t[ti],platforms:e.target.value};onUpdate({trackers:t});}}>
                <option value="all">All platforms</option>
                {fl.platforms.map(pe2=>{const pc=catalogue.find(c=>c.id===pe2.platformId)||DEFAULT_PLATFORMS.find(c=>c.id===pe2.platformId);return pc&&pc.type!=='sem'?<option key={pe2.platformId} value={pe2.platformId}>{pc.label}</option>:null;})}
              </select>
              <select className="sel" value={tr.type||'cpm'} style={{width:52,fontSize:10}} onChange={e=>{const t=[...(fl.trackers||[])];t[ti]={...t[ti],type:e.target.value};onUpdate({trackers:t});}}>
                <option value="cpm">CPM</option><option value="pct">%</option>
              </select>
              <input className="inn" type="text" inputMode="decimal" value={tr.value!=null?tr.value:''} placeholder="0.00" style={{width:64,fontSize:10}} onFocus={e=>e.target.select()} onChange={e=>{const t=[...(fl.trackers||[])];t[ti]={...t[ti],value:e.target.value};onUpdate({trackers:t});}} onBlur={e=>{const t=[...(fl.trackers||[])];const v=parseFloat(e.target.value);t[ti]={...t[ti],value:isNaN(v)?0:v};onUpdate({trackers:t});}}/>
              <button style={{color:'var(--red)',border:'none',background:'transparent',cursor:'pointer',fontWeight:700,fontSize:14}} onClick={()=>onUpdate({trackers:(fl.trackers||[]).filter((_,j)=>j!==ti)})}>×</button>
            </div>))}
            <button className="btn btn-xs btn-ghost" onClick={()=>onUpdate({trackers:[...(fl.trackers||[]),{name:'',type:'cpm',value:0}]})}>+ Add tracker</button>
          </div>
          {tot&&tot.impressions>0&&(<div style={{display:'flex',gap:4,flexWrap:'wrap',paddingTop:8,borderTop:'1px solid var(--border)'}}>
            {[{l:'Budget',v:fe(tot.budget)},{l:'Impr.',v:fmil(tot.impressions)},{l:'vImp.',v:fmil(tot.vimpressions)},{l:'eCPM',v:fe2(tot.ecpm)},{l:'vCPM',v:fe2(tot.vcpm)},{l:'vRCH%',v:fp(tot.reach),hi:true},{l:'Avg FRQ/wk',v:ff(tot.frequency)},...(tot.views>0?[{l:'Views',v:fmil(tot.views)}]:[]),{l:'Clicks',v:fmil(tot.clicks)},...(tot.agencyFee>0?[{l:'Agency',v:fe(tot.agencyFee)}]:[]),...(tot.trackerCost>0?[{l:'Trackers',v:fe(tot.trackerCost)}]:[])].map(k=>(
              <div key={k.l} style={{background:k.hi?'var(--gold-bg)':'var(--surface3)',border:'1px solid '+(k.hi?'var(--gold-border)':'var(--border)'),borderRadius:6,padding:'4px 8px',minWidth:52,textAlign:'center'}}>
                <div style={{fontSize:9,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'.04em',marginBottom:1}}>{k.l}</div>
                <div style={{fontSize:12,fontWeight:800,color:k.hi?'var(--gold-dark)':'var(--text)'}}>{k.v}</div>
              </div>
            ))}
          </div>)}
        </div>
        <div className="modal-col modal-col-right">
          <div className="lbl" style={{marginBottom:6}}>Platform mix</div>
          <PlatformMixTable fl={fl} result={result} onUpdate={onUpdate} catalogue={catalogue} mixLocked={mixLocked} setMixLocked={setMixLocked}/>
          {result&&result.nWeeks>0&&result.nWeeks<=26&&(<div style={{marginTop:16}}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
              <div className="lbl" style={{marginBottom:0}}>Weekly distribution</div>
              <button className="btn btn-xs btn-ghost" onClick={()=>onUpdate({weeklyOverrides:{}})}>Reset all</button>
            </div>
            {wkMixModal!=null&&<WeeklyMixModal weekIdx={wkMixModal} fl={fl} result={result} onUpdate={onUpdate} catalogue={catalogue} onClose={()=>setWkMixModal(null)}/>}
            <div style={{overflowX:'auto'}}><table style={{width:'100%',borderCollapse:'collapse',fontSize:10,minWidth:380}}>
              <thead><tr style={{background:'var(--gold-bg)'}}>
                <th style={{...TH,textAlign:'left'}}>Period</th>
                <th style={{...TH,width:34}}>Days</th>
                <th style={{...TH,width:88}}>Budget €</th>
                <th style={{...TH,width:52}}>Impr.</th>
                <th style={{...TH,width:52}}>Clicks</th>
                <th style={{...TH,width:52}}>vRCH%</th>
                <th style={{...TH,width:28}} title="Weekly platform mix override">Mix</th>
              </tr></thead>
              <tbody>{(result.weekly||[]).map((rw,i)=>{
                const ov=fl.weeklyOverrides&&fl.weeklyOverrides[i];
                const isOvr=ov&&ov.budget!=null;
                const isRedist=!isOvr&&Object.keys(fl.weeklyOverrides||{}).length>0;
                const days=Math.round(rw.frac*7);
                return(<tr key={i} style={{background:isOvr?'var(--gold-bg)':isRedist?'var(--surface3)':'transparent'}}>
                  <td style={{...TD,fontSize:9,color:'var(--text3)',fontWeight:600,whiteSpace:'nowrap'}}>
                    {isOvr&&<span style={{color:'var(--gold-dark)',marginRight:3}}>●</span>}
                    {isRedist&&<span style={{color:'var(--text3)',marginRight:3,fontSize:8}}>↺</span>}
                    W{rw.week} · {fdate(weekStartDate(rw.week))}{days<7&&<span style={{color:'#E07000',marginLeft:3}}>({days}d)</span>}
                  </td>
                  <td style={{...TDc,fontSize:9,color:days<7?'#E07000':'var(--text3)'}}>{days}</td>
                  <td style={TDc}><input className="inn" type="number" min={0} step={100} value={Math.round(rw.budget)} style={{width:'100%',fontSize:10,background:isOvr?'var(--gold-bg)':'transparent',fontWeight:isOvr?700:400}}
                    onChange={e=>{const v=+e.target.value;onUpdate({weeklyOverrides:{...(fl.weeklyOverrides||{}),[i]:{...(fl.weeklyOverrides?.[i]||{}),budget:v}}});}}
                    onBlur={e=>{const v=+e.target.value;if(v===0)onUpdate({weeklyOverrides:{...(fl.weeklyOverrides||{}),[i]:{...(fl.weeklyOverrides?.[i]||{}),budget:undefined}}});}}
                  /></td>
                  <td style={{...TDc,fontSize:9,color:isOvr?'var(--gold-dark)':'var(--text2)',fontWeight:isOvr?700:400}}>{fmil(rw.impressions)}</td>
                  <td style={{...TDc,fontSize:9,color:isOvr?'var(--gold-dark)':'var(--text2)',fontWeight:isOvr?700:400}}>{fmil(rw.clicks)}</td>
                  <td style={{...TDc,fontSize:10,color:'var(--gold-dark)',fontWeight:700}}>{fp(rw.reach)}</td>
                  <td style={{...TDc,width:28}}>
                    <button title="Override platform mix for this week" onClick={()=>setWkMixModal(i)} style={{fontSize:11,background:rw.hasMixOverride?'var(--gold-dark)':'transparent',color:rw.hasMixOverride?'#fff':'var(--text3)',border:'1px solid '+(rw.hasMixOverride?'var(--gold-dark)':'var(--border)'),borderRadius:4,cursor:'pointer',padding:'1px 4px',lineHeight:1}}>✎</button>
                  </td>
                </tr>);
              })}</tbody>
            </table></div>
          </div>)}
        </div>
      </div>
      <div className="modal-footer">
        <button className="btn btn-ghost btn-sm" onClick={onClone}>Clone flight</button>
        {canRemove&&<button className="btn btn-danger btn-sm" onClick={onRemove}>Remove</button>}
        <div style={{flex:1}}/>
        <button className="btn btn-primary btn-sm" onClick={onClose}>Done</button>
      </div>
    </div>
  </div>);
}

// ─── FLIGHT CARD ──────────────────────────────────────────────────────────────
function FlightCard({flight,result,isSelected,onSelect,onUpdate,onClone,onRemove,canRemove,catalogue,audiences}){
  const[open,setOpen]=useState(false);
  const tot=result&&result.tot;
  const obj=OBJECTIVES.find(o=>o.id===flight.objective)||OBJECTIVES[0];
  const aud=audiences&&audiences.find(a=>a.id===flight.audienceId);
  return(<div className={'flight-card'+(isSelected?' active':'')} style={{borderLeft:'3px solid '+flight.color}}>
    <div className="fc-header" onClick={()=>{onSelect(flight.id);setOpen(true);}} title="Edit">
      <div style={{flex:1,minWidth:0}}>
        <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:2}}>
          <span style={{fontSize:9,fontWeight:700,color:'var(--text3)',borderRight:'1px solid var(--border)',paddingRight:5,marginRight:2}}>{flight.brand}</span>
          <span style={{fontWeight:600,fontSize:12}}>{flight.label}</span>
          <span style={{fontSize:9,padding:'1px 5px',borderRadius:8,background:obj.color+'22',color:obj.color,fontWeight:700}}>{obj.label}</span>
        </div>
        <div style={{fontSize:10,color:'var(--text3)'}}>{fdate(flight.startDate)} – {fdate(flight.endDate)}{aud&&<span style={{marginLeft:5,color:'var(--gold-dark)'}}> · {aud.name}</span>}</div>
      </div>
      <div style={{textAlign:'right',flexShrink:0}}>
        {tot&&tot.budget>0&&<><div className="mono" style={{fontSize:11,fontWeight:600,color:'var(--gold-dark)'}}>{fe(tot.budget)}</div><div style={{fontSize:10,color:'var(--text3)'}}>{fp(tot.reach)} vRCH</div></>}
      </div>
      <div style={{color:'var(--gold)',fontSize:11,marginLeft:4}}>✎</div>
    </div>
    {open&&<FlightModal fl={flight} result={result} onClose={()=>setOpen(false)} onUpdate={onUpdate} onClone={()=>{onClone();setOpen(false);}} onRemove={()=>{onRemove();setOpen(false);}} canRemove={canRemove} catalogue={catalogue} audiences={audiences}/>}
  </div>);
}

// ─── FLOWCHART ────────────────────────────────────────────────────────────────
function AnnualFlowchart({flights,allResults}){
  const grps=useMemo(()=>{const bO=[],bM={};flights.forEach(f=>{const b=f.brand||'Brand 1';if(!bM[b]){bM[b]={};bO.push(b);}const ob=f.objective||'awareness';if(!bM[b][ob])bM[b][ob]=[];bM[b][ob].push(f);});return{bO,bM};},[flights]);
  return(<div style={{overflowX:'auto'}}>
    <table className="fc-table">
      <thead>
        <tr><th className="row-hdr" style={{width:150,fontSize:9,color:'var(--text3)'}}>METRIC</th>{MONTHS.map((m,mi)=><th key={m} colSpan={MONTH_WEEKS[mi].length} className="fc-month-hdr">{MN[mi]}</th>)}</tr>
        <tr><th className="row-hdr" style={{fontSize:8,color:'var(--text3)'}}>WEEK</th>{Array.from({length:52},(_,i)=>i+1).map(w=><th key={w} style={{fontSize:8,padding:'2px 0',textAlign:'center',color:'var(--text3)',minWidth:22,width:22}}>{w}</th>)}</tr>
      </thead>
      <tbody>
        {grps.bO.map(brand=>{
          const objMap=grps.bM[brand];
          return[
            <tr key={'bh_'+brand}><td colSpan={53} style={{padding:'4px 10px',background:'var(--text)',color:'var(--gold)',fontSize:9,fontWeight:700,letterSpacing:'.09em',textTransform:'uppercase'}}>{brand}</td></tr>,
            ...Object.keys(objMap).flatMap(objId=>{
              const obj=OBJECTIVES.find(o=>o.id===objId)||{label:objId,color:'#888'};
              const objFl=objMap[objId];
              const wB=Array(52).fill(0),wI=Array(52).fill(0),wV=Array(52).fill(0),wC=Array(52).fill(0),wR=Array(52).fill(0);
              objFl.forEach(f=>{const r=allResults[f.id];if(!r)return;r.weekly.forEach(wd=>{const idx=wd.week-1;if(idx<0||idx>51)return;wB[idx]+=wd.budget;wI[idx]+=wd.impressions;wV[idx]+=wd.views;wC[idx]+=wd.clicks;wR[idx]=Math.max(wR[idx],wd.reach||0);});});
              const mxB=Math.max(...wB,1),mxI=Math.max(...wI,1);
              const isAw=objId==='awareness'||objId==='views',isTr=objId==='traffic'||objId==='conversion';
              return[
                <tr key={'oh_'+brand+objId}><td colSpan={53} style={{padding:'2px 10px 2px 18px',background:'var(--surface3)',borderBottom:'1px solid var(--border)'}}><span style={{fontSize:9,fontWeight:700,color:obj.color,textTransform:'uppercase',letterSpacing:'.06em'}}>{obj.label}</span></td></tr>,
                <tr key={'cam_'+brand+objId}><td className="row-hdr row-sub" style={{paddingLeft:12,fontSize:9}}>Campaigns</td>{Array.from({length:52},(_,i)=>i+1).map(w=>{const here=objFl.filter(f=>{const r=allResults[f.id];return r&&r.activeWeeks&&r.activeWeeks.includes(w);});if(!here.length)return<td key={w}><div className="fc-cell empty"/></td>;return<td key={w} style={{padding:1}}><div className="fc-cell" style={{background:here[0].color,borderRadius:2}}/></td>;})}</tr>,
                <tr key={'bud_'+brand+objId}><td className="row-hdr row-sub" style={{paddingLeft:12,fontSize:9}}>Budget / wk</td>{wB.map((v,i)=>{if(!v)return<td key={i}><div className="fc-cell empty"/></td>;const p=v/mxB;return<td key={i} style={{padding:0}}><div className="fc-cell" style={{background:p>.7?'var(--gold-dark)':p>.4?'var(--gold)':'#E8D5BC',color:p>.4?'#fff':'#5A3A10',fontSize:7,fontWeight:700}}>{Math.round(v/1000)+'K'}</div></td>;})}</tr>,
                <tr key={'imp_'+brand+objId}><td className="row-hdr row-sub" style={{paddingLeft:12,fontSize:9}}>Impr / wk</td>{wI.map((v,i)=>{if(!v)return<td key={i}><div className="fc-cell empty"/></td>;const p=v/mxI;return<td key={i} style={{padding:0}}><div className="fc-cell" style={{background:p>.7?'#2A5298':p>.4?'#4472C4':'#96B4E8',color:p>.4?'#fff':'#1A2A50',fontSize:7,fontWeight:700}}>{fmil(v)}</div></td>;})}</tr>,
                <tr key={'rch_'+brand+objId}><td className="row-hdr row-sub" style={{paddingLeft:12,fontSize:9,color:'var(--gold-dark)',fontWeight:600}}>vRCH% / wk</td>{wR.map((v,i)=>{if(!v)return<td key={i}><div className="fc-cell empty"/></td>;const p=Math.min(v,0.99);return<td key={i} style={{padding:0}}><div className="fc-cell" style={{background:p>.35?'#9B7E54':p>.20?'#C19A6B':'#E8D5BC',color:p>.20?'#fff':'#5A3A10',fontSize:7,fontWeight:800}}>{(p*100).toFixed(0)+'%'}</div></td>;})}</tr>,
                isAw&&wV.some(v=>v>0)&&<tr key={'views_'+brand+objId}><td className="row-hdr row-sub" style={{paddingLeft:12,fontSize:9}}>Views / wk</td>{wV.map((v,i)=>{if(!v)return<td key={i}><div className="fc-cell empty"/></td>;const p=v/Math.max(...wV,1);return<td key={i} style={{padding:0}}><div className="fc-cell" style={{background:p>.7?'#4A0E8A':p>.4?'#7030A0':'#C09AD8',color:p>.4?'#fff':'#2A0060',fontSize:7,fontWeight:700}}>{fmil(v)}</div></td>;})}</tr>,
                isTr&&wC.some(v=>v>0)&&<tr key={'clk_'+brand+objId}><td className="row-hdr row-sub" style={{paddingLeft:12,fontSize:9}}>Clicks / wk</td>{wC.map((v,i)=>{if(!v)return<td key={i}><div className="fc-cell empty"/></td>;const p=v/Math.max(...wC,1);return<td key={i} style={{padding:0}}><div className="fc-cell" style={{background:p>.7?'#1B6020':p>.4?'#2E8B57':'#90D4AA',color:p>.4?'#fff':'#0A3010',fontSize:7,fontWeight:700}}>{fmil(v)}</div></td>;})}</tr>,
                <tr key={'ftot_'+brand+objId}><td className="row-hdr" style={{paddingLeft:10,fontStyle:'italic',color:'var(--text3)',fontSize:9}}>€ / flight</td>{(()=>{const cells=[];let w=1;while(w<=52){const f=objFl.find(fl=>{const r=allResults[fl.id];return r&&r.activeWeeks&&r.activeWeeks[0]===w;});if(f){const r=allResults[f.id];const span=Math.min(r.activeWeeks.length,53-w);
if(r.nWeeks>=52){
  MONTHS.map((m,mi)=>{const ws=MONTH_WEEKS[mi];const mBud=ws.reduce((s,wk)=>s+(r.weekly.find(x=>x.week===wk)?.budget||0),0);const mRch=ws.reduce((s,wk)=>s+(r.weekly.find(x=>x.week===wk)?.reach||0),0)/ws.length;cells.push(<td key={'m'+mi} colSpan={ws.length} style={{padding:'2px 3px',textAlign:'center',background:'var(--gold-bg)',borderLeft:'1px solid var(--border)'}}><span style={{fontSize:9,color:f.color,fontWeight:700}}>{mBud>0?fe(mBud):'—'}</span><span style={{fontSize:8,color:'var(--gold-dark)',marginLeft:3}}>{mRch>0?fp(mRch)+' vRCH':''}</span></td>);});w=53;
} else {
  cells.push(<td key={w} colSpan={span} style={{padding:'2px 4px',textAlign:'center',background:'var(--gold-bg)',borderLeft:'1px solid var(--border)',borderRight:'1px solid var(--border)'}}><span style={{fontSize:9,color:f.color,fontWeight:700}}>{fe(r.tot.budget)}</span><span style={{fontSize:8,color:'var(--gold-dark)',marginLeft:4}}>{fp(r.tot.reach)} vRCH</span></td>);w+=span;
}}else{cells.push(<td key={w}/>);w++;}}return cells;})()}</tr>,
                <tr key={'mth_'+brand+objId}><td className="row-hdr" style={{paddingLeft:10,fontStyle:'italic',color:'var(--gold-dark)',fontWeight:600,fontSize:9}}>Monthly €</td>{MONTHS.map((m,mi)=>{const ws=MONTH_WEEKS[mi];const v=ws.reduce((s,w)=>s+(wB[w-1]||0),0);return<td key={m} colSpan={ws.length} style={{textAlign:'center',padding:'2px 4px',background:'var(--gold-bg)',borderLeft:'1px solid var(--border)'}}><span style={{fontSize:9,color:'var(--gold-dark)',fontWeight:700}}>{v>0?fe(v):'—'}</span></td>;})}</tr>,
              ].filter(Boolean);
            }),
          ];
        })}
        {(()=>{const tB=Array(52).fill(0),tI=Array(52).fill(0);flights.forEach(f=>{const r=allResults[f.id];if(!r)return;r.weekly.forEach(wd=>{const idx=wd.week-1;if(idx>=0&&idx<52){tB[idx]+=wd.budget;tI[idx]+=wd.impressions;}});});return[
          <tr key='totalhdr'><td colSpan={53} style={{padding:'4px 10px',background:'var(--text)',color:'var(--gold)',fontSize:9,fontWeight:700,letterSpacing:'.09em',textTransform:'uppercase',borderTop:'3px solid var(--gold)'}}>TOTAL — ALL BRANDS</td></tr>,
          <tr key='totalbud'><td className="row-hdr" style={{fontStyle:'italic',color:'var(--gold-dark)',fontWeight:600,fontSize:9}}>Budget / mo</td>{MONTHS.map((m,mi)=>{const ws=MONTH_WEEKS[mi];const v=ws.reduce((s,w)=>s+(tB[w-1]||0),0);return<td key={m} colSpan={ws.length} style={{textAlign:'center',padding:'2px 4px',background:'var(--gold-bg)',borderLeft:'1px solid var(--border)'}}><span style={{fontSize:9,color:'var(--gold-dark)',fontWeight:700}}>{v>0?fe(v):'—'}</span></td>;})}</tr>,
          <tr key='totalimp'><td className="row-hdr" style={{fontStyle:'italic',color:'#4472C4',fontSize:9}}>Impr / mo</td>{MONTHS.map((m,mi)=>{const ws=MONTH_WEEKS[mi];const v=ws.reduce((s,w)=>s+(tI[w-1]||0),0);return<td key={m} colSpan={ws.length} style={{textAlign:'center',padding:'2px 4px',borderLeft:'1px solid var(--border)'}}><span style={{fontSize:9,color:'#4472C4',fontWeight:700}}>{v>0?fmil(v):'—'}</span></td>;})}</tr>,
        ];})()}
      </tbody>
    </table>
  </div>);
}

// ─── SUMMARY TAB — platforms inside each format group, matching Excel layout ──
// ─── CLIENT MEDIA PLAN EXPORT ─────────────────────────────────────────────────
async function exportClientMediaPlan(flights,allResults,mode,targetId){
  // Load ExcelJS from CDN for full formatting + multi-sheet support
  if(!window._ExcelJS){
    await new Promise((res,rej)=>{
      const s=document.createElement('script');
      s.src='https://cdnjs.cloudflare.com/ajax/libs/exceljs/4.4.0/exceljs.min.js';
      s.onload=res;s.onerror=rej;document.head.appendChild(s);
    }).catch(()=>{});
    if(window.ExcelJS)window._ExcelJS=window.ExcelJS;
  }
  const ExcelJS=window._ExcelJS||window.ExcelJS;
  if(!ExcelJS){alert('ExcelJS could not be loaded. Check your connection.');return;}

  const DARK='1A3A5C',GOLD='C9A84C',LIGHT_GOLD='FFF8E1',LIGHT_BLUE='DBEAF7',MID_BLUE='2E6DA4',WHITE='FFFFFF',GREY='F2F2F2',TEXT_DARK='1A3A5C',AMBER='E07000';
  const VAT=1.20;

  // Style helpers
  function hdr(cell,txt,opts={}){
    cell.value=txt;
    cell.font={name:'Arial',size:opts.sz||9,bold:opts.bold!==false,color:{argb:'FF'+(opts.color||WHITE)}};
    cell.fill={type:'pattern',pattern:'solid',fgColor:{argb:'FF'+(opts.bg||DARK)}};
    cell.alignment={horizontal:opts.align||'center',vertical:'middle',wrapText:!!opts.wrap};
    cell.border={top:{style:'thin',color:{argb:'FFCCCCCC'}},bottom:{style:'thin',color:{argb:'FFCCCCCC'}},left:{style:'thin',color:{argb:'FFCCCCCC'}},right:{style:'thin',color:{argb:'FFCCCCCC'}}};
  }
  function dat(cell,val,opts={}){
    cell.value=val;
    cell.font={name:'Arial',size:opts.sz||9,bold:!!opts.bold,color:{argb:'FF'+(opts.color||'000000')}};
    if(opts.bg)cell.fill={type:'pattern',pattern:'solid',fgColor:{argb:'FF'+opts.bg}};
    cell.alignment={horizontal:opts.align||'left',vertical:'middle'};
    cell.border={top:{style:'thin',color:{argb:'FFCCCCCC'}},bottom:{style:'thin',color:{argb:'FFCCCCCC'}},left:{style:'thin',color:{argb:'FFCCCCCC'}},right:{style:'thin',color:{argb:'FFCCCCCC'}}};
    if(opts.numFmt)cell.numFmt=opts.numFmt;
  }
  function mergeHdr(ws,r1,c1,r2,c2,txt,opts={}){
    ws.mergeCells(r1,c1,r2,c2);
    hdr(ws.getCell(r1,c1),txt,opts);
    // Fill merged cells
    for(let r=r1;r<=r2;r++)for(let c=c1;c<=c2;c++){const cl=ws.getCell(r,c);if(!cl.value&&(r!==r1||c!==c1)){cl.fill={type:'pattern',pattern:'solid',fgColor:{argb:'FF'+(opts.bg||DARK)}};cl.border={top:{style:'thin',color:{argb:'FFCCCCCC'}},bottom:{style:'thin',color:{argb:'FFCCCCCC'}},left:{style:'thin',color:{argb:'FFCCCCCC'}},right:{style:'thin',color:{argb:'FFCCCCCC'}}};}};
  }
  function eu(v){return typeof v==='number'?v:null;}
  function pct(v){return v==null?'—':(+v).toFixed(1)+'%';}
  function fmt(v){return Math.round(v);}

  // Determine which flights to export
  let toExport=[];
  let sheetTitle='';
  if(mode==='flight'){
    const fl=flights.find(f=>f.id===targetId);
    if(fl)toExport=[fl];
    sheetTitle=fl?fl.brand+' — '+fl.label:'Flight';
  } else if(mode==='month'){
    // targetId is month index 0-11
    toExport=flights.filter(fl=>{
      const r=allResults[fl.id];
      return r&&r.weekly.some(wk=>weekMonth(wk.week)===targetId);
    });
    sheetTitle=MN[targetId]+' '+YEAR;
  }
  if(!toExport.length){alert('No data for selected period');return;}

  // ── SUMMARY SHEET (ExcelJS) ───────────────────────────────────────────────
  function buildSummarySheet(wb){
    const ws=wb.addWorksheet('Summary');
    ws.properties.defaultRowHeight=16;
    let row=1;

    // Aggregate
    const groups={};
    SUMMARY_GROUPS.forEach(g=>{groups[g.key]={label:g.label,types:g.types,rows:{},groupReach:0};});
    let totBudAll=0,totTrk=0,totAf=0;
    const campBrands=[],campPeriods=[];
    toExport.forEach(fl=>{
      if(!campBrands.includes(fl.brand))campBrands.push(fl.brand);
      const pd=fdate(fl.startDate)+' – '+fdate(fl.endDate);if(!campPeriods.includes(pd))campPeriods.push(pd);
      const r=allResults[fl.id];if(!r)return;
      totTrk+=r.tot.trackerCost||0;totAf+=r.tot.agencyFee||0;
      r.platforms.forEach(pr=>{
        const t=pr.p?.type||'banner';
        const gk=t==='video'?'video':t==='sem'?'sem':t==='social'?'social':t==='banner'?'display_std':'display_ns';
        const g=groups[gk];if(!g)return;
        const pid=pr.p.id;
        if(!g.rows[pid])g.rows[pid]={pid,label:pr.p.label,seller:pr.p.seller||'',type:t,buyingType:(pr.pe?.discount>0)?'Direct Deal':'Programmatic',budget:0,impr:0,vimpr:0,views:0,clicks:0,reach:0,cpm:pr.cpm||0};
        const rr=g.rows[pid];rr.budget+=pr.bud;rr.impr+=pr.impr;rr.vimpr+=pr.vimpr;rr.views+=pr.views;rr.clicks+=pr.clicks;rr.reach=sbN([rr.reach,pr.reach||0]);
        totBudAll+=pr.bud;
      });
      SUMMARY_GROUPS.forEach(sg=>{const pool=r.platforms.filter(pr=>sg.types.includes(pr.p?.type)).map(pr=>pr.reach||0);groups[sg.key].groupReach=sb2(groups[sg.key].groupReach,sbN(pool));});
    });
    let totImpr=0,totViews=0,totVimpr=0,totClicks=0,totReach=0;
    SUMMARY_GROUPS.forEach(sg=>{const rows=Object.values(groups[sg.key].rows);rows.forEach(r=>{totImpr+=r.impr;totViews+=r.views;totVimpr+=r.vimpr;totClicks+=r.clicks;});totReach=sb2(totReach,groups[sg.key].groupReach);});

    // Column widths
    ws.columns=[{width:32},{width:16},{width:16},{width:14},{width:14},{width:10},{width:18},{width:14},{width:10},{width:14},{width:16},{width:18},{width:8}];

    // Title
    mergeHdr(ws,row,1,row,13,'DIGITAL MEDIA PLAN',{sz:14,bold:true,bg:DARK,color:WHITE,align:'center'});
    ws.getRow(row).height=24;row++;

    // Info block — left: campaign info, right: KPIs
    const infoLeft=[['Client / Brand',campBrands.join(', ')],['Period',campPeriods.join('; ')],['Target Audience','—'],['Total Budget excl. VAT',totBudAll],['Total Budget incl. VAT 20%',totBudAll*VAT]];
    const kpiRight=[['Total Impressions',fmt(totImpr)],['Total Views',fmt(totViews)],['Viewable Impr.',fmt(totVimpr)],['vRCH%',pct(totReach*100)],['Clicks',fmt(totClicks)]];
    for(let i=0;i<Math.max(infoLeft.length,kpiRight.length);i++){
      const [ll='',lv='']= infoLeft[i]||[];
      const [rl='',rv='']= kpiRight[i]||[];
      dat(ws.getCell(row,1),ll,{bold:true,bg:LIGHT_BLUE,color:TEXT_DARK,align:'right'});
      ws.mergeCells(row,2,row,7);dat(ws.getCell(row,2),lv,{bold:true,align:'left'});
      dat(ws.getCell(row,8),'',{});
      dat(ws.getCell(row,9),rl,{bold:true,bg:DARK,color:WHITE,align:'right'});
      ws.mergeCells(row,10,row,13);dat(ws.getCell(row,10),rv,{bold:true,bg:LIGHT_BLUE,align:'center'});
      row++;
    }
    row++; // spacer

    // Table header
    const HDRS=['Platform / Format','Seller','Buying Type','Buying Unit','Paid Units (Impr)','CPM €','Main KPI','KPI abs.','KPI %','Cost/KPI €','Budget excl.VAT','Budget incl.VAT','SHR%'];
    HDRS.forEach((h,i)=>hdr(ws.getCell(row,i+1),h,{bg:DARK,color:WHITE,align:'center',sz:9,wrap:true}));
    ws.getRow(row).height=18;row++;

    // Groups
    SUMMARY_GROUPS.forEach(sg=>{
      const g=groups[sg.key];const prows=Object.values(g.rows);if(!prows.length)return;
      const gBud=prows.reduce((s,r)=>s+r.budget,0);
      const isVideo=sg.key==='video',isSem=sg.key==='sem';
      const gKpi=isVideo?prows.reduce((s,r)=>s+r.views,0):isSem?prows.reduce((s,r)=>s+r.clicks,0):prows.reduce((s,r)=>s+r.vimpr,0);
      const gShr=totBudAll>0?pct(gBud/totBudAll*100):'';
      mergeHdr(ws,row,1,row,12,g.label,{bg:MID_BLUE,color:WHITE,sz:10,bold:true,align:'center'});
      dat(ws.getCell(row,13),gShr,{bg:MID_BLUE,color:WHITE,bold:true,align:'center'});
      ws.getRow(row).height=18;row++;
      prows.forEach((pr,pi)=>{
        const bg=pi%2===0?null:GREY;
        const isV=pr.type==='video',isS=pr.type==='sem';
        const mkpi=isV?pr.views:isS?pr.clicks:pr.vimpr;
        const mkpiLbl=isV?'Views':isS?'Clicks':'Viewable Impr.';
        const costKpi=mkpi>0?pr.budget/mkpi*1000:null;
        const kpiPct=pr.impr>0?pct(mkpi/pr.impr*100):'—';
        const prShr=totBudAll>0?pct(pr.budget/totBudAll*100):'';
        [pr.label,pr.seller||'',pr.buyingType,'CPM',fmt(pr.impr),pr.cpm||0,mkpiLbl,fmt(mkpi),kpiPct,costKpi,pr.budget,pr.budget*VAT,prShr].forEach((v,i)=>{
          const opts={bg,align:i<2?'left':'center',sz:9,bold:i===0};
          if(i===5||i===9||i===10||i===11)opts.numFmt='"€"#,##0.00';
          if(i===4||i===6||i===7)opts.numFmt='#,##0';
          dat(ws.getCell(row,i+1),v,opts);
        });
        row++;
      });
    });

    // Totals
    const grand=totBudAll+totTrk+totAf;
    mergeHdr(ws,row,1,row,4,'Total Online',{bg:DARK,color:WHITE,sz:10,align:'left'});
    dat(ws.getCell(row,5),fmt(totImpr),{bold:true,bg:DARK,color:WHITE,align:'right',numFmt:'#,##0'});
    dat(ws.getCell(row,6),'',{bg:DARK});
    dat(ws.getCell(row,7),'All Formats',{bold:true,bg:DARK,color:WHITE,align:'center'});
    const totKpiAll=Object.values(groups).reduce((s,g)=>{const isV=g.label==='Video',isS=g.label==='Search';return s+Object.values(g.rows).reduce((ss,rr)=>ss+(isV?rr.views:isS?rr.clicks:rr.vimpr),0);},0);
    dat(ws.getCell(row,8),fmt(totKpiAll),{bold:true,bg:DARK,color:WHITE,align:'right',numFmt:'#,##0'});
    dat(ws.getCell(row,9),'',{bg:DARK});
    dat(ws.getCell(row,10),'',{bg:DARK});
    dat(ws.getCell(row,11),totBudAll,{bold:true,bg:DARK,color:WHITE,align:'right',numFmt:'"€"#,##0.00'});
    dat(ws.getCell(row,12),totBudAll*VAT,{bold:true,bg:DARK,color:WHITE,align:'right',numFmt:'"€"#,##0.00'});
    dat(ws.getCell(row,13),'100%',{bold:true,bg:DARK,color:WHITE,align:'center'});
    row++;
    if(totTrk>0){mergeHdr(ws,row,1,row,10,'3rd Party Trackers',{bg:GREY,color:'000000',align:'left'});dat(ws.getCell(row,11),totTrk,{bg:GREY,numFmt:'"€"#,##0.00',align:'right'});dat(ws.getCell(row,12),totTrk*VAT,{bg:GREY,numFmt:'"€"#,##0.00',align:'right'});row++;}
    if(totAf>0){mergeHdr(ws,row,1,row,10,'Agency Fee',{bg:GREY,color:'000000',align:'left'});dat(ws.getCell(row,11),totAf,{bg:GREY,numFmt:'"€"#,##0.00',align:'right'});dat(ws.getCell(row,12),totAf*VAT,{bg:GREY,numFmt:'"€"#,##0.00',align:'right'});row++;}
    row++;
    mergeHdr(ws,row,1,row,10,'TOTAL BUDGET',{bg:DARK,color:WHITE,sz:11,align:'left'});
    dat(ws.getCell(row,11),grand,{bold:true,bg:DARK,color:WHITE,numFmt:'"€"#,##0.00',align:'right'});
    dat(ws.getCell(row,12),grand*VAT,{bold:true,bg:GOLD,color:TEXT_DARK,numFmt:'"€"#,##0.00',align:'right'});
    dat(ws.getCell(row,13),'',{bg:DARK});
  }

  // ── DETAIL SHEET (ExcelJS) ─────────────────────────────────────────────────
  function buildDetailSheet(wb,fl,r,monthFilter,sheetName){
    const ws=wb.addWorksheet(sheetName);
    ws.properties.defaultRowHeight=15;
    const weeks=monthFilter!=null?r.weekly.filter(wk=>weekMonth(wk.week)===monthFilter):r.weekly;
    if(!weeks.length)return;
    const ncols=4+weeks.length;

    // Column widths
    ws.columns=[{width:28},{width:14},{width:12},{width:12},...weeks.map(()=>({width:14}))];

    let row=1;
    // Title
    mergeHdr(ws,row,1,row,ncols,'WEEKLY MEDIA PLAN — '+fl.brand.toUpperCase()+' — '+(fl.label||'').toUpperCase(),{sz:12,bg:DARK,color:WHITE,align:'center'});
    ws.getRow(row).height=22;row++;
    [['Brand',fl.brand],['Period',fdate(fl.startDate)+' – '+fdate(fl.endDate)],['Budget','€'+Math.round(fl.budget).toLocaleString()+' excl. VAT'],['Audience',r.universe?r.universe.toLocaleString()+' people':'—']].forEach(([l,v])=>{
      dat(ws.getCell(row,1),l,{bold:true,bg:LIGHT_BLUE,color:TEXT_DARK,align:'right'});
      ws.mergeCells(row,2,row,ncols);dat(ws.getCell(row,2),v,{bold:true,align:'left'});row++;
    });
    row++;

    // Helper: per-week value
    function wkVal(pr,wi,field){
      const realWi=r.weekly.findIndex(x=>x.week===weeks[wi].week);
      const wb2=r.wkBudgets?.[realWi]??0;
      const wkMix=fl.weeklyOverrides?.[realWi]?.platforms||null;
      const ap=wkMix||fl.platforms;
      const pe=ap.find(x=>x.platformId===pr.p.id);if(!pe)return 0;
      const disc=Math.max(0,Math.min(0.9,pr.pe?.discount||0));
      const cpm=(pr.p.base_cpm||0)*(1-disc);
      const platBud=wb2*((pe.shr||0)/100);
      if(field==='budget')return platBud;
      const impr=cpm>0?(platBud/cpm)*1000:0;
      if(field==='impr')return impr;
      const frq=pe.frq_override!=null?pe.frq_override:(pr.p.weekly_frq||3);
      const ck=frq>0?impr/frq:0;
      if(field==='cookies')return ck;
      const ac=pr.audComp||pr.p?.audience_comp||0.65;
      const ruc=pr.p.real_users_coef||2.0;
      const view=pr.p.viewability||0.7;
      if(field==='taUsers')return ck/ruc*ac;
      if(field==='vRealUsers')return ck/ruc*ac*view;
      return 0;
    }

    // Metrics — EXACT same formulas as METRIC_SECTIONS getRows() in the platform UI
    const nWksAll=r.weekly.length||1;
    function getPlatWkBudEx(pr,realWi){
      const wb2=r.wkBudgets?.[realWi]??0;
      const wkMix=fl.weeklyOverrides?.[realWi]?.platforms||null;
      const ap=wkMix||fl.platforms;
      const pe=ap.find(x=>x.platformId===pr.p.id);if(!pe)return 0;
      return wb2*((pe.shr||0)/100);
    }
    const metrics=[
      {label:'IMPRESSIONS',color:MID_BLUE,numFmt:'#,##0',
        getRow:(pr)=>{
          const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));const cpm=(pr.p.base_cpm||0)*(1-disc);
          return{tot:Math.round(pr.impr),weeks:weeks.map((_,wi)=>{const realWi=r.weekly.findIndex(x=>x.week===weeks[wi].week);const wb2=getPlatWkBudEx(pr,realWi);return Math.round(cpm>0?(wb2/cpm)*1000:0);})};},
        getTot:()=>({tot:Math.round(r.platforms.reduce((s,pr)=>s+pr.impr,0)),weeks:weeks.map((_,wi)=>{const realWi=r.weekly.findIndex(x=>x.week===weeks[wi].week);return Math.round(r.platforms.reduce((s,pr)=>{const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));const cpm=(pr.p.base_cpm||0)*(1-disc);const wb2=getPlatWkBudEx(pr,realWi);return s+(cpm>0?(wb2/cpm)*1000:0);},0));})})},
      {label:'BUDGET (EUR excl. VAT)',color:DARK,numFmt:'"€"#,##0.00',
        getRow:(pr)=>({tot:pr.bud,weeks:weeks.map((_,wi)=>{const realWi=r.weekly.findIndex(x=>x.week===weeks[wi].week);return getPlatWkBudEx(pr,realWi);})}),
        getTot:()=>({tot:r.tot.budget,weeks:weeks.map((_,wi)=>{const realWi=r.weekly.findIndex(x=>x.week===weeks[wi].week);return r.platforms.reduce((s,pr)=>s+getPlatWkBudEx(pr,realWi),0);})})},
      {label:'UNIQUE COOKIES / WK',color:'70AD47',numFmt:'#,##0',
        getRow:(pr)=>{
          const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));const cpm=(pr.p.base_cpm||0)*(1-disc);
          const totCk=Math.round(pr.frq>0?(pr.impr||0)/pr.frq:0);
          return{tot:totCk,weeks:weeks.map((_,wi)=>{const realWi=r.weekly.findIndex(x=>x.week===weeks[wi].week);const wb2=getPlatWkBudEx(pr,realWi);const wi_impr=cpm>0?(wb2/cpm)*1000:0;return Math.round(pr.frq>0?wi_impr/pr.frq:0);})};},
        getTot:()=>{
          const univ2=r.universe||DEF_UNIVERSE;
          const wkVals=weeks.map((_,wi)=>{const realWi=r.weekly.findIndex(x=>x.week===weeks[wi].week);return r.platforms.reduce((s,pr)=>{const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));const cpm=(pr.p.base_cpm||0)*(1-disc);const wb2=getPlatWkBudEx(pr,realWi);const wi_impr=cpm>0?(wb2/cpm)*1000:0;return s+Math.round(pr.frq>0?wi_impr/pr.frq:0);},0);});
          // cookies TOTAL = totalImpressions / weightedAvgFrq (exact same as platform TOTAL row)
          const totI2=r.platforms.reduce((s,pr)=>s+(pr.impr||0),0)||1;
          const avgFrq2=r.platforms.reduce((s,pr)=>s+(pr.frq||0)*(pr.impr||0),0)/totI2||1;
          return{tot:Math.round(totI2/avgFrq2),weeks:wkVals};}},
      {label:'TA REAL USERS / WK',color:'FF0066',numFmt:'#,##0',note:'Cookies ÷ RUC × AudComp',
        getRow:(pr)=>{
          const ac=pr.audComp||pr.p?.audience_comp||0.65;const ruc=pr.p.real_users_coef||2.0;const univ2=r.universe||DEF_UNIVERSE;
          const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));const cpm=(pr.p.base_cpm||0)*(1-disc);
          const baseWkRU=Math.round((pr.uniqueCookies||0)/ruc*ac);const wkRch=Math.min(0.999,univ2>0?baseWkRU/univ2:0);
          let cum2=0;for(let i=0;i<nWksAll;i++){cum2=cum2+wkRch-cum2*wkRch;}
          return{tot:Math.round(Math.min(0.98,cum2)*univ2),weeks:weeks.map((_,wi)=>{const realWi=r.weekly.findIndex(x=>x.week===weeks[wi].week);const wb2=getPlatWkBudEx(pr,realWi);if(wb2===0)return 0;const wi_impr=cpm>0?(wb2/cpm)*1000:0;const wi_ck=pr.frq>0?wi_impr/pr.frq:0;return Math.round(wi_ck/ruc*ac);})};},
        getTot:()=>{
          const univ2=r.universe||DEF_UNIVERSE;
          const wkVals=weeks.map((_,wi)=>{const realWi=r.weekly.findIndex(x=>x.week===weeks[wi].week);const pools={yt:[],social:[],other:[]};r.platforms.forEach(pr=>{const ac=pr.audComp||pr.p?.audience_comp||0.65;const ruc=pr.p.real_users_coef||2.0;const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));const cpm=(pr.p.base_cpm||0)*(1-disc);const wb2=getPlatWkBudEx(pr,realWi);if(wb2===0)return;const wi_impr=cpm>0?(wb2/cpm)*1000:0;const wi_ck=pr.frq>0?wi_impr/pr.frq:0;const pool=pr.p?.reach_pool==='yt'?'yt':pr.p?.reach_pool==='social'?'social':'other';pools[pool].push({cookies:wi_ck,ruc,audComp:ac});});return Math.round(Math.min(0.98,1-(1-poolReach(pools.yt,univ2))*(1-poolReach(pools.social,univ2))*(1-poolReach(pools.other,univ2)))*univ2);});
          const pools={yt:[],social:[],other:[]};r.platforms.forEach(pr=>{const pool=pr.p?.reach_pool==='yt'?'yt':pr.p?.reach_pool==='social'?'social':'other';pools[pool].push({cookies:pr.uniqueCookies||0,ruc:pr.p.real_users_coef||2.0,audComp:pr.audComp||pr.p?.audience_comp||0.65});});
          const wkR=Math.min(0.98,1-(1-poolReach(pools.yt,univ2))*(1-poolReach(pools.social,univ2))*(1-poolReach(pools.other,univ2)));
          let cum2=0;for(let i=0;i<nWksAll;i++){cum2=cum2+wkR-cum2*wkR;}
          return{tot:Math.round(Math.min(0.98,cum2)*univ2),weeks:wkVals};}},
      {label:'VIEWABLE REAL USERS / WK',color:'C00000',numFmt:'#,##0',note:'(Cookies ÷ RUC × AudComp) × Viewability',
        getRow:(pr)=>{
          const ac=pr.audComp||pr.p?.audience_comp||0.65;const ruc=pr.p.real_users_coef||2.0;const view=pr.p.viewability||0.7;const univ2=r.universe||DEF_UNIVERSE;
          const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));const cpm=(pr.p.base_cpm||0)*(1-disc);
          const baseWkVRU=Math.round((pr.uniqueCookies||0)/ruc*ac*view);const wkRch=Math.min(0.999,univ2>0?baseWkVRU/univ2:0);
          let cum2=0;for(let i=0;i<nWksAll;i++){cum2=cum2+wkRch-cum2*wkRch;}
          return{tot:Math.round(Math.min(0.98,cum2)*univ2),weeks:weeks.map((_,wi)=>{const realWi=r.weekly.findIndex(x=>x.week===weeks[wi].week);const wb2=getPlatWkBudEx(pr,realWi);if(wb2===0)return 0;const wi_impr=cpm>0?(wb2/cpm)*1000:0;const wi_ck=pr.frq>0?wi_impr/pr.frq:0;return Math.round(wi_ck/ruc*ac*view);})};},
        getTot:()=>{
          const univ2=r.universe||DEF_UNIVERSE;
          const wkVals=weeks.map((_,wi)=>{const realWi=r.weekly.findIndex(x=>x.week===weeks[wi].week);const pools={yt:[],social:[],other:[]};r.platforms.forEach(pr=>{const ac=pr.audComp||pr.p?.audience_comp||0.65;const ruc=pr.p.real_users_coef||2.0;const view=pr.p.viewability||0.7;const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));const cpm=(pr.p.base_cpm||0)*(1-disc);const wb2=getPlatWkBudEx(pr,realWi);if(wb2===0)return;const wi_impr=cpm>0?(wb2/cpm)*1000:0;const wi_ck=pr.frq>0?wi_impr/pr.frq:0;const pool=pr.p?.reach_pool==='yt'?'yt':pr.p?.reach_pool==='social'?'social':'other';pools[pool].push({cookies:wi_ck*view,ruc,audComp:ac});});return Math.round(Math.min(0.98,1-(1-poolReach(pools.yt,univ2))*(1-poolReach(pools.social,univ2))*(1-poolReach(pools.other,univ2)))*univ2);});
          const pools={yt:[],social:[],other:[]};r.platforms.forEach(pr=>{const view=pr.p.viewability||0.7;const pool=pr.p?.reach_pool==='yt'?'yt':pr.p?.reach_pool==='social'?'social':'other';pools[pool].push({cookies:(pr.uniqueCookies||0)*view,ruc:pr.p.real_users_coef||2.0,audComp:pr.audComp||pr.p?.audience_comp||0.65});});
          const wkR=Math.min(0.98,1-(1-poolReach(pools.yt,univ2))*(1-poolReach(pools.social,univ2))*(1-poolReach(pools.other,univ2)));
          let cum2=0;for(let i=0;i<nWksAll;i++){cum2=cum2+wkR-cum2*wkR;}
          return{tot:Math.round(Math.min(0.98,cum2)*univ2),weeks:wkVals};}},
    ];

    metrics.forEach(metric=>{
      mergeHdr(ws,row,1,row,ncols,metric.label+(metric.note?' — '+metric.note:''),{bg:metric.color,color:WHITE,sz:10,align:'center'});
      ws.getRow(row).height=18;row++;
      hdr(ws.getCell(row,1),'Platform',{align:'left'});hdr(ws.getCell(row,2),'Format');hdr(ws.getCell(row,3),'Seller');hdr(ws.getCell(row,4),'TOTAL');
      weeks.forEach((wk,wi)=>hdr(ws.getCell(row,5+wi),'W'+wk.week+' '+fdate(weekStartDate(wk.week)),{sz:8,wrap:true}));
      ws.getRow(row).height=18;row++;
      let alt=false;
      r.platforms.forEach(pr=>{
        const bg=alt?GREY:null;alt=!alt;
        const d=metric.getRow(pr);
        dat(ws.getCell(row,1),pr.p.label,{bg,bold:true,color:TEXT_DARK,align:'left'});
        dat(ws.getCell(row,2),pr.p.type,{bg,align:'center'});
        dat(ws.getCell(row,3),pr.p.seller||'',{bg,align:'center'});
        dat(ws.getCell(row,4),d.tot,{bg,bold:true,align:'right',numFmt:metric.numFmt});
        d.weeks.forEach((v,wi)=>dat(ws.getCell(row,5+wi),v,{bg,align:'right',numFmt:metric.numFmt}));
        row++;
      });
      const totD=metric.getTot();
      dat(ws.getCell(row,1),'TOTAL',{bold:true,bg:GOLD,color:TEXT_DARK,align:'left'});
      dat(ws.getCell(row,2),'',{bg:GOLD});dat(ws.getCell(row,3),'',{bg:GOLD});
      dat(ws.getCell(row,4),totD.tot,{bold:true,bg:GOLD,color:TEXT_DARK,align:'right',numFmt:metric.numFmt});
      totD.weeks.forEach((v,wi)=>dat(ws.getCell(row,5+wi),v,{bold:true,bg:GOLD,color:TEXT_DARK,align:'right',numFmt:metric.numFmt}));
      row++;row++;
    });

    // Cumulative Reach
    // Compute per-week viewable reach using Sainsbury poolReach
    const univ=r.universe||DEF_UNIVERSE;
    const wkVRUreach=weeks.map((_,wi)=>{
      const pools={yt:[],social:[],other:[]};
      r.platforms.forEach(pr=>{
        const vv=wkVal(pr,wi,'vRealUsers');if(vv===0)return;
        const ruc=pr.p.real_users_coef||2.0;
        const ac=pr.audComp||pr.p?.audience_comp||0.65;
        const view=pr.p.viewability||0.7;
        const pool=pr.p?.reach_pool==='yt'?'yt':pr.p?.reach_pool==='social'?'social':'other';
        // back-calculate cookies from vRealUsers
        const ck=vv/(ac*view/ruc);
        pools[pool].push({cookies:ck*view,ruc,audComp:ac});
      });
      return Math.min(0.98,1-(1-poolReach(pools.yt,univ))*(1-poolReach(pools.social,univ))*(1-poolReach(pools.other,univ)));
    });

    mergeHdr(ws,row,1,row,8,'CUMULATIVE REACH PROGRESSION',{bg:MID_BLUE,color:WHITE,sz:10,align:'center'});row++;
    ['Week','Date','Days Active','+RCH%/wk','Cum. RCH%','Est. TA Users','+vRCH%/wk','Cum. vRCH%'].forEach((h,i)=>hdr(ws.getCell(row,i+1),h,{sz:8,wrap:true}));
    ws.getRow(row).height=20;row++;
    // Est. TA Users = Sainsbury accumulation of taUsers weekly TOTAL row values
    // Last week overridden with exact TOTAL column value for consistency
    const taUsersTotD=metrics.find(m=>m.label.startsWith('TA REAL USERS')).getTot();
    const taUsersCumWk=[];let cumTA2=0;const univ2=r.universe||DEF_UNIVERSE;
    taUsersTotD.weeks.forEach((wkAbs,wi)=>{
      const wkR=Math.min(0.999,wkAbs/univ2);
      cumTA2=Math.min(0.98,cumTA2+wkR-cumTA2*wkR);
      const isLast=wi===taUsersTotD.weeks.length-1;
      taUsersCumWk.push(isLast?taUsersTotD.tot:Math.round(cumTA2*univ2));
    });
    let cum=0,cumV=0;
    weeks.forEach((wk,wi)=>{
      cum=Math.min(0.98,cum+(wk.reach||0)-cum*(wk.reach||0));
      const vwkR=wkVRUreach[wi]||0;
      cumV=Math.min(0.98,cumV+vwkR-cumV*vwkR);
      const days=Math.round(wk.frac*7);
      dat(ws.getCell(row,1),'W'+wk.week,{align:'center',bold:true});
      dat(ws.getCell(row,2),fdate(weekStartDate(wk.week)),{align:'center'});
      dat(ws.getCell(row,3),days,{align:'center',color:days<7?AMBER:'000000'});
      dat(ws.getCell(row,4),pct((wk.reach||0)*100),{align:'center',color:MID_BLUE,bold:true});
      dat(ws.getCell(row,5),pct(cum*100),{align:'center',color:DARK,bold:true});
      dat(ws.getCell(row,6),taUsersCumWk[wi],{align:'right'});
      dat(ws.getCell(row,7),pct(vwkR*100),{align:'center',color:'C00000',bold:true});
      dat(ws.getCell(row,8),pct(cumV*100),{align:'center',color:DARK,bold:true});
      row++;
    });
  }

  // Assemble workbook
  const wb=new ExcelJS.Workbook();
  wb.creator='Digital Planning Engine';
  wb.created=new Date();
  buildSummarySheet(wb);
  if(mode==='flight'){
    const fl=toExport[0];const r=allResults[fl.id];
    if(r)buildDetailSheet(wb,fl,r,null,'Weekly Detail');
  } else {
    toExport.forEach(fl=>{
      const r=allResults[fl.id];if(!r)return;
      const nm=(fl.brand.slice(0,10)+'-'+fl.label.slice(0,8)).replace(/[^\w]/g,'_').slice(0,28);
      buildDetailSheet(wb,fl,r,targetId,nm);
    });
  }

  // Download
  const buf=await wb.xlsx.writeBuffer();
  const blob=new Blob([buf],{type:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
  const url=URL.createObjectURL(blob);
  const a=document.createElement('a');a.href=url;
  a.download='MediaPlan_'+sheetTitle.replace(/[^\w ]/g,'_').slice(0,30)+'_'+new Date().toISOString().slice(0,10)+'.xlsx';
  document.body.appendChild(a);a.click();document.body.removeChild(a);URL.revokeObjectURL(url);
}

// ─── EXPORT MODAL ─────────────────────────────────────────────────────────────
function ExportMediaPlanModal({flights,allResults,onClose}){
  const[mode,setMode]=useState('flight');
  const[flightId,setFlightId]=useState(flights[0]?.id||'');
  const[monthIdx,setMonthIdx]=useState(null);

  // Compute which months have activity across all flights
  const activeMonths=useMemo(()=>{
    const months=new Set();
    flights.forEach(fl=>{const r=allResults[fl.id];if(!r)return;r.weekly.forEach(wk=>months.add(weekMonth(wk.week)));});
    return [...months].sort((a,b)=>a-b);
  },[flights,allResults]);

  // Set default month
  React.useEffect(()=>{if(activeMonths.length&&monthIdx===null)setMonthIdx(activeMonths[0]);},[activeMonths]);

  function doExport(){
    exportClientMediaPlan(flights,allResults,mode,mode==='flight'?flightId:monthIdx);
    onClose();
  }

  return(<div className="modal-overlay" onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
    <div className="modal-box" style={{maxWidth:440}} onClick={e=>e.stopPropagation()}>
      <div className="modal-hdr">
        <span style={{fontWeight:700,fontSize:13}}>⬇ Export Client Media Plan</span>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
      </div>
      <div style={{padding:'16px 20px',display:'flex',flexDirection:'column',gap:14}}>
        <div style={{fontSize:11,color:'var(--text3)'}}>Select what to include in the export. Each option generates a <b>Summary</b> sheet + <b>Weekly Detail</b> sheet.</div>
        <div style={{display:'flex',gap:8}}>
          {[['flight','✈ By Flight'],['month','📅 By Month']].map(([v,l])=>(
            <button key={v} onClick={()=>setMode(v)} style={{flex:1,padding:'8px',borderRadius:8,border:'2px solid '+(mode===v?'var(--teal)':'var(--border)'),background:mode===v?'#00807015':'var(--surface2)',fontWeight:mode===v?700:400,fontSize:11,cursor:'pointer',color:mode===v?'var(--teal)':'var(--text)'}}>
              {l}
            </button>
          ))}
        </div>
        {mode==='flight'&&(
          <div>
            <div style={{fontSize:10,fontWeight:600,color:'var(--text3)',marginBottom:6}}>Select flight:</div>
            <div style={{display:'flex',flexDirection:'column',gap:4}}>
              {flights.map(fl=>{
                const r=allResults[fl.id];
                return(<div key={fl.id} onClick={()=>setFlightId(fl.id)} style={{padding:'8px 12px',borderRadius:6,border:'2px solid '+(flightId===fl.id?'var(--gold-dark)':'var(--border)'),background:flightId===fl.id?'var(--gold-bg)':'var(--surface2)',cursor:'pointer',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div>
                    <div style={{fontWeight:600,fontSize:11,color:'var(--text)'}}>{fl.brand} — {fl.label}</div>
                    <div style={{fontSize:9,color:'var(--text3)'}}>{fdate(fl.startDate)} – {fdate(fl.endDate)}</div>
                  </div>
                  <div style={{fontWeight:700,fontSize:11,color:'var(--gold-dark)'}}>{r?fe(r.tot.budget):'—'}</div>
                </div>);
              })}
            </div>
          </div>
        )}
        {mode==='month'&&(
          <div>
            <div style={{fontSize:10,fontWeight:600,color:'var(--text3)',marginBottom:6}}>Select month (all active flights included):</div>
            <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
              {activeMonths.map(mi=>(
                <button key={mi} onClick={()=>setMonthIdx(mi)} style={{padding:'6px 14px',borderRadius:6,border:'2px solid '+(monthIdx===mi?'var(--teal)':'var(--border)'),background:monthIdx===mi?'#00807015':'var(--surface2)',fontWeight:monthIdx===mi?700:400,fontSize:11,cursor:'pointer',color:monthIdx===mi?'var(--teal)':'var(--text)'}}>
                  {MN[mi]} {YEAR}
                </button>
              ))}
            </div>
            <div style={{fontSize:9,color:'var(--text3)',marginTop:8}}>
              Active flights in selected month: <b>{monthIdx!==null?flights.filter(fl=>{const r=allResults[fl.id];return r&&r.weekly.some(wk=>weekMonth(wk.week)===monthIdx);}).map(f=>f.brand+' — '+f.label).join(', '):'—'}</b>
            </div>
          </div>
        )}
      </div>
      <div className="modal-footer">
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={doExport}>⬇ Download Excel</button>
      </div>
    </div>
  </div>);
}

function SummaryTab({flights,allResults,onExportExcel}){
  const brands=[...new Set(flights.map(f=>f.brand||'Brand 1'))];

  // Build per-brand data: {groupKey: [{platformRow}, ...], totals}
  function buildData(bf){
    // groupKey -> { label, platforms: {id -> {budget,impr,vimpr,views,clicks,reach,cpm,frq,vtr,audComp}} }
    const groups={};
    SUMMARY_GROUPS.forEach(g=>{groups[g.key]={label:g.label,types:g.types,rows:{},groupReach:0};});
    let totTrk=0,totAf=0;
    flights.filter(f=>bf==='__TOTAL__'||(f.brand||'Brand 1')===bf).forEach(f=>{
      const r=allResults[f.id];if(!r)return;
      totTrk+=r.tot.trackerCost||0;totAf+=r.tot.agencyFee||0;
      r.platforms.forEach(pr=>{
        const t=pr.p?.type||'banner';
        const gk=t==='video'?'video':t==='sem'?'sem':t==='social'?'social':t==='banner'?'display_std':'display_ns';
        const g=groups[gk];if(!g)return;
        const pid=pr.p.id,plabel=pr.p.label,seller=pr.p.seller||'';
        if(!g.rows[pid])g.rows[pid]={pid,label:plabel,seller,type:t,buyingType:pr.pe.discount>0?'Direct Deal':'Programmatic',budget:0,impr:0,vimpr:0,views:0,clicks:0,reach:0,cpm:pr.cpm||0,frq:pr.frq||0,vtr:pr.p.vtr||0,audComp:pr.audComp||pr.p?.audience_comp||0};
        const row=g.rows[pid];
        row.budget+=pr.bud;row.impr+=pr.impr;row.vimpr+=pr.vimpr;row.views+=pr.views;row.clicks+=pr.clicks;
        row.reach=sbN([row.reach,pr.reach||0]);
      });
      // Accumulate group-level reach using Sainsbury
      SUMMARY_GROUPS.forEach(sg=>{
        const pool=r.platforms.filter(pr=>sg.types.includes(pr.p?.type)).map(pr=>pr.reach||0);
        groups[sg.key].groupReach=sb2(groups[sg.key].groupReach,sbN(pool));
      });
    });
    return{groups,totTrk,totAf};
  }

  const HDR_STYLE={...TH,fontSize:9,background:'#1A3A5C',color:'#fff'};

  const[exportModal,setExportModal]=useState(false);
  return(<div style={{padding:'16px 24px'}}>
    <div style={{display:'flex',justifyContent:'flex-end',marginBottom:10}}>
      <button className="btn btn-primary btn-sm" onClick={()=>setExportModal(true)}>⬇ Export Client Media Plan</button>
    </div>
    {exportModal&&<ExportMediaPlanModal flights={flights} allResults={allResults} onClose={()=>setExportModal(false)}/>}
    {[...brands,'__TOTAL__'].map(brand=>{
      const{groups,totTrk,totAf}=buildData(brand);
      const isT=brand==='__TOTAL__';
      const totBudAll=Object.values(groups).reduce((s,g)=>s+Object.values(g.rows).reduce((ss,r)=>ss+r.budget,0),0);
      if(totBudAll===0&&!isT)return null;
      return(<div key={brand} style={{marginBottom:32}}>
        <div style={{padding:'7px 14px',background:isT?'var(--text)':'var(--surface2)',color:isT?'var(--gold)':'var(--text)',fontWeight:700,fontSize:11,borderRadius:'6px 6px 0 0',border:'1px solid var(--border)',letterSpacing:'.04em',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <span>{isT?'TOTAL — ALL BRANDS':brand}</span>
          <span style={{fontWeight:400,fontSize:10,color:isT?'#C8A86B':'var(--text3)'}}>Budget excl. VAT · eCPM · KPI% = viewability rate</span>
        </div>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:10.5,minWidth:900}}>
            <thead>
              <tr style={{background:'#1A3A5C'}}>
                <th style={{...HDR_STYLE,color:'#fff',textAlign:'left',minWidth:160,position:'sticky',left:0,background:'#1A3A5C',zIndex:2}}>Platform / Format</th>
                <th style={{...HDR_STYLE,color:'#fff',width:90}}>Buying Type</th>
                <th style={{...HDR_STYLE,color:'#fff',width:52}}>Unit</th>
                <th style={{...HDR_STYLE,color:'#fff',width:80}}>Paid Units</th>
                <th style={{...HDR_STYLE,color:'#fff',width:68}}>CPM / CPC €</th>
                <th style={{...HDR_STYLE,color:'#fff',width:100}}>Main KPI</th>
                <th style={{...HDR_STYLE,color:'#fff',width:80}}>KPI abs.</th>
                <th style={{...HDR_STYLE,color:'#fff',width:60}}>KPI %</th>
                <th style={{...HDR_STYLE,color:'#fff',width:72}}>Cost/KPI €</th>
                <th style={{...HDR_STYLE,color:'#fff',width:90}}>Budget €</th>
                <th style={{...HDR_STYLE,color:'#fff',width:68}}>SHR%</th>
              </tr>
            </thead>
            <tbody>
              {SUMMARY_GROUPS.map(sg=>{
                const g=groups[sg.key];
                const rows=Object.values(g.rows);
                if(!rows.length)return null;
                const gBud=rows.reduce((s,r)=>s+r.budget,0);
                const gImpr=rows.reduce((s,r)=>s+r.impr,0);
                const gVimpr=rows.reduce((s,r)=>s+r.vimpr,0);
                const gViews=rows.reduce((s,r)=>s+r.views,0);
                const gClicks=rows.reduce((s,r)=>s+r.clicks,0);
                const gEcpm=gImpr>0?(gBud/gImpr)*1000:0;
                const shr=totBudAll>0?gBud/totBudAll:0;
                const dot=AD_TYPES.find(t=>sg.types.includes(t.key));
                const isVideo=sg.key==='video',isSem=sg.key==='sem';
                // Group header row
                return[
                  <tr key={'gh_'+sg.key} style={{background:'#E8F0F8'}}>
                    <td style={{...TD,fontWeight:700,fontSize:10,color:dot?.color||'#333',paddingLeft:8,position:'sticky',left:0,background:'#E8F0F8',zIndex:1}}>
                      <div style={{display:'flex',alignItems:'center',gap:6}}><span style={{width:9,height:9,borderRadius:'50%',background:dot?.color||'#ccc',display:'inline-block',flexShrink:0}}/>{g.label}</div>
                    </td>
                    <td style={{...TDc,fontSize:9,color:'var(--text3)'}}>Mixed</td>
                    <td style={{...TDc,fontSize:9,color:'var(--text3)'}}>CPM</td>
                    <td style={{...TDr,fontWeight:600}}>{fn(gImpr)}</td>
                    <td style={{...TDc,color:'var(--gold-dark)',fontWeight:700}}>{gEcpm?'€'+gEcpm.toFixed(2):'—'}</td>
                    <td style={{...TDc,fontSize:9,color:'var(--text3)'}}>{isVideo?'Views':isSem?'Clicks':'Viewable Impr.'}</td>
                    <td style={{...TDr,fontWeight:600}}>{fn(isVideo?gViews:isSem?gClicks:gVimpr)}</td>
                    <td style={{...TDc,fontWeight:700,color:'var(--gold-dark)'}}>{fp(g.groupReach)}</td>
                    <td style={{...TDc,color:'var(--text3)',fontSize:9}}>—</td>
                    <td style={{...TDr,fontWeight:700,color:'var(--gold-dark)'}}>{fe(gBud)}</td>
                    <td style={{...TDc,fontWeight:700,color:'#1A3A5C'}}>{(shr*100).toFixed(1)}%</td>
                  </tr>,
                  // Platform rows
                  ...rows.map(row=>{
                    const isV=row.type==='video',isS=row.type==='sem';
                    const mainKpiAbs=isV?row.views:isS?row.clicks:row.vimpr;
                    const mainKpiLabel=isV?'Views':isS?'Clicks':'Viewable Impr.';
                    const kpiPct=row.impr>0?mainKpiAbs/row.impr:0;
                    const costPerKpi=mainKpiAbs>0?(row.budget/mainKpiAbs)*1000:0;
                    return(<tr key={row.pid} style={{background:'#fff'}}>
                      <td style={{...TD,paddingLeft:22,fontWeight:500,position:'sticky',left:0,background:'#fff',zIndex:1}}>
                        <div style={{fontSize:10}}>{row.label}</div>
                        <div style={{fontSize:8,color:'var(--text3)'}}>{row.seller}</div>
                      </td>
                      <td style={{...TDc,fontSize:9,color:'var(--text3)'}}>{row.buyingType}</td>
                      <td style={{...TDc,fontSize:9,fontWeight:600}}>CPM</td>
                      <td style={{...TDr,fontSize:10}}>{fn(row.impr)}</td>
                      <td style={{...TDc,color:'var(--gold-dark)',fontWeight:600,fontSize:10}}>{row.cpm?'€'+row.cpm.toFixed(2):isS?'CPC':'—'}</td>
                      <td style={{...TDc,fontSize:9,color:'var(--text3)'}}>{mainKpiLabel}</td>
                      <td style={{...TDr,fontSize:10}}>{fn(mainKpiAbs)}</td>
                      <td style={{...TDc,fontWeight:600,color:'#1A3A5C'}}>{fp(kpiPct)}</td>
                      <td style={{...TDr,fontSize:9,color:'var(--text3)'}}>{costPerKpi>0?'€'+(costPerKpi/1000).toFixed(2):'—'}</td>
                      <td style={{...TDr,fontWeight:600,color:'var(--gold-dark)'}}>{fe(row.budget)}</td>
                      <td style={{...TDc,fontSize:9,color:'var(--text3)'}}>{totBudAll>0?((row.budget/totBudAll)*100).toFixed(1)+'%':'—'}</td>
                    </tr>);
                  }),
                ];
              })}
              {/* TOTAL ROW */}
              <tr style={{background:'#1A3A5C',color:'#fff',fontWeight:700}}>
                <td style={{...TD,color:'#fff',fontWeight:700,position:'sticky',left:0,background:'#1A3A5C',zIndex:1}}>TOTAL ONLINE</td>
                <td colSpan={3} style={{...TDc,color:'#C8A86B',fontSize:9}}>All formats combined</td>
                <td style={{...TDc,color:'#C8A86B',fontWeight:700}}>{totBudAll>0?'€'+(totBudAll/Object.values(groups).reduce((s,g)=>s+Object.values(g.rows).reduce((ss,r)=>ss+r.impr,0),0)*1000).toFixed(2):'—'}</td>
                <td colSpan={2} style={TDc}/>
                <td style={{...TDc,color:'#C8A86B',fontWeight:700}}>{fp(sbN(Object.values(groups).map(g=>g.groupReach)))}</td>
                <td style={TDc}/>
                <td style={{...TDr,color:'#FFD700',fontWeight:800,fontSize:12}}>{fe(totBudAll)}</td>
                <td style={{...TDc,color:'#C8A86B',fontWeight:700}}>100%</td>
              </tr>
              {totTrk>0&&<tr style={{background:'#FFF3E0'}}><td style={{...TD,color:'#E07000',fontStyle:'italic',paddingLeft:8,position:'sticky',left:0,background:'#FFF3E0',zIndex:1}}>3rd Party Trackers (Gemius / DV / IAS)</td><td colSpan={8} style={{...TDc,color:'var(--text3)',fontSize:9,fontStyle:'italic'}}>Monitoring cost</td><td style={{...TDr,color:'#E07000',fontWeight:600}}>{fe(totTrk)}</td><td/></tr>}
              {totAf>0&&<tr style={{background:'#F3E8FF'}}><td style={{...TD,color:'#7030A0',fontStyle:'italic',paddingLeft:8,position:'sticky',left:0,background:'#F3E8FF',zIndex:1}}>Agency Fee</td><td colSpan={8} style={{...TDc,color:'var(--text3)',fontSize:9,fontStyle:'italic'}}>Included in media budget</td><td style={{...TDr,color:'#7030A0',fontWeight:600}}>{fe(totAf)}</td><td/></tr>}
              {(totTrk>0||totAf>0)&&<tr style={{background:'var(--gold-bg)',fontWeight:700}}><td style={{...TD,fontWeight:700,paddingLeft:8,position:'sticky',left:0,background:'var(--gold-bg)',zIndex:1}}>TOTAL INCL. FEES</td><td colSpan={8}/><td style={{...TDr,color:'var(--gold-dark)',fontWeight:800,fontSize:12}}>{fe(totBudAll+totTrk)}</td><td/></tr>}
            </tbody>
          </table>
        </div>
      </div>);
    })}
  </div>);
}

// ─── DETAILS TAB — matching Excel: weekly tables per metric type ──────────────
function DetailsTab({flights,allResults,catalogue,onExportExcel}){
  const[selFlight,setSelFlight]=useState(null);
  const f=selFlight?flights.find(x=>x.id===selFlight):flights[0];
  const r=f&&allResults[f?.id];
  const[metric,setMetric]=useState('impressions');

  const cumRch=useMemo(()=>{
    if(!r||!r.weekly)return[];
    const univ=r.universe||DEF_UNIVERSE;
    // Weekly viewable real users — use platWkBud to respect mix overrides
    const wkOverridesG=f?.weeklyOverrides||{};
    const fl_platformsG=f?.platforms||[];
    function platWkBudG(pr,wi){
      const wb=r.wkBudgets?.[wi]??0;
      const wkMixPlats=wkOverridesG[wi]?.platforms||null;
      const activePlats=wkMixPlats||fl_platformsG;
      const pe=activePlats.find(x=>x.platformId===pr.p.id);
      if(!pe)return 0;
      return wb*((pe.shr||0)/100);
    }
    const wkVRU=r.weekly.map((wk,wi)=>{
      const pools={yt:[],social:[],other:[]};
      r.platforms.forEach(pr=>{
        const wb=platWkBudG(pr,wi);if(wb===0)return;
        const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));
        const cpm=(pr.p.base_cpm||0)*(1-disc);
        const wi_impr=cpm>0?(wb/cpm)*1000:0;
        const wi_ck=pr.frq>0?wi_impr/pr.frq:0;
        const ac=pr.audComp||pr.p?.audience_comp||0.65;
        const ruc=pr.p.real_users_coef||2.0;
        const view=pr.p.viewability||0.7;
        const pool=pr.p?.reach_pool==='yt'?'yt':pr.p?.reach_pool==='social'?'social':'other';
        pools[pool].push({cookies:wi_ck*view,ruc,audComp:ac});
      });
      const rYT=poolReach(pools.yt,univ);
      const rSoc=poolReach(pools.social,univ);
      const rOth=poolReach(pools.other,univ);
      return Math.min(0.98,1-(1-rYT)*(1-rSoc)*(1-rOth));
    });
    let cum=0,cumVRU=0;
    return r.weekly.map((wk,i)=>{
      cum=sb2(cum,wk.reach||0);
      cumVRU=sb2(cumVRU,wkVRU[i]||0);
      return{week:wk.week,frac:wk.frac,cumReach:cum,weekReach:wk.reach||0,weekVRU:wkVRU[i]||0,cumVRU,cumVRUabs:Math.round(cumVRU*univ),weekVRUabs:Math.round((wkVRU[i]||0)*univ)};
    });
  },[r]);
  const wFT=r?(r.weekly||[]).reduce((s,wk)=>s+wk.frac,0)||1:1;

  // Build per-platform weekly values
  function platWeekly(field, transform){
    if(!r)return[];
    const wkBuds=r.wkBudgets||[];
    const totBud=r.tot.budget||1;
    const fl_platforms=f?.platforms||[];
    const wkOverrides=f?.weeklyOverrides||{};
    return r.platforms.map(pr=>{
      const raw=pr[field]||0;
      const tot=transform?transform(raw,pr):raw;
      // Per-week: use actual platform share for that week (respects mix overrides)
      const weeks=(r.weekly||[]).map((wk,wi)=>{
        const wb=wkBuds[wi]??0;
        // Find this platform's share for this week
        const wkMixPlats=wkOverrides[wi]?.platforms||null;
        const activePlats=wkMixPlats||fl_platforms;
        const pe=activePlats.find(x=>x.platformId===pr.p.id);
        if(!pe)return 0; // platform not in this week's mix
        const platBud=wb*((pe.shr||0)/100);
        const disc=Math.max(0,Math.min(0.90,pe.discount||0));
        const cpm=(pr.p.base_cpm||0)*(1-disc);
        if(field==='impr')return cpm>0?(platBud/cpm)*1000:0;
        if(field==='vimpr')return cpm>0?(platBud/cpm)*1000*(pr.p.viewability||0.7):0;
        if(field==='views')return cpm>0?(platBud/cpm)*1000*(pr.p.vtr||0):0;
        if(field==='clicks')return cpm>0?(platBud/cpm)*1000*(pr.p.ctr||0):0;
        // For other fields: proportional to platform budget share this week
        return tot*(platBud/(totBud||1));
      });
      return{pid:pr.p.id,label:pr.p.label,seller:pr.p.seller||'',type:pr.p.type,tot,weeks};
    });
  }

  // Helper: get platform's effective budget for a specific week (respects mix overrides)
  function platWkBud(pr,wi){
    const wb=r.wkBudgets?.[wi]??0;
    const wkMixPlats=f?.weeklyOverrides?.[wi]?.platforms||null;
    const activePlats=wkMixPlats||(f?.platforms||[]);
    const pe=activePlats.find(x=>x.platformId===pr.p.id);
    if(!pe)return 0;
    return wb*((pe.shr||0)/100);
  }

  const METRIC_SECTIONS=[
    {key:'impressions', label:'Impressions',         color:'#4472C4', getRows:()=>platWeekly('impr')},
    {key:'frq', label:'FRQ (avg/wk)', color:'#ED7D31', isFixed:true, getRows:()=>r.platforms.map(pr=>({pid:pr.p.id,label:pr.p.label,seller:pr.p.seller||'',type:pr.p.type,tot:pr.frq||0,weeks:(r.weekly||[]).map(()=>pr.frq||0)})), totFn:()=>{const totI=r.platforms.reduce((s,pr)=>s+(pr.impr||0),0);return totI>0?r.platforms.reduce((s,pr)=>s+(pr.frq||0)*(pr.impr||0),0)/totI:0;}},
    {key:'cookies', label:'Unique Cookies/wk', color:'#70AD47', getRows:()=>{return r.platforms.map(pr=>{const totCk=Math.round(pr.frq>0?(pr.impr||0)/pr.frq:0);const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));const cpm=(pr.p.base_cpm||0)*(1-disc);const weeks=(r.weekly||[]).map((_,wi)=>{const wb=platWkBud(pr,wi);const wi_impr=cpm>0?(wb/cpm)*1000:0;return Math.round(pr.frq>0?wi_impr/pr.frq:0);});return{pid:pr.p.id,label:pr.p.label,seller:pr.p.seller||'',type:pr.p.type,tot:totCk,weeks};});}},
    {key:'taUsers', label:'TA Real Users/wk', color:'#FF0066', note:'Cookies ÷ RUC × AudComp', getRows:()=>{const nWks=r.weekly&&r.weekly.length||1;return r.platforms.map(pr=>{const ac=pr.audComp||pr.p?.audience_comp||0.65;const ruc=pr.p.real_users_coef||2.0;const univ=r.universe||DEF_UNIVERSE;const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));const cpm=(pr.p.base_cpm||0)*(1-disc);const baseWkRU=Math.round((pr.uniqueCookies||0)/ruc*ac);const wkRch=Math.min(0.999,baseWkRU/univ);let cum=0;for(let i=0;i<nWks;i++){cum=cum+wkRch-cum*wkRch;}const totRU=Math.round(Math.min(0.98,cum)*univ);const weeks=(r.weekly||[]).map((_,wi)=>{const wb=platWkBud(pr,wi);if(wb===0)return 0;const wi_impr=cpm>0?(wb/cpm)*1000:0;const wi_ck=pr.frq>0?wi_impr/pr.frq:0;return Math.round(wi_ck/ruc*ac);});return{pid:pr.p.id,label:pr.p.label,seller:pr.p.seller||'',type:pr.p.type,tot:totRU,monthVal:totRU,weeks,noMonthSum:true};});}},
    {key:'vRealUsers',  label:'Viewable Real Users/wk', color:'#C00000', note:'(Cookies ÷ RUC × AudComp) × Viewability — users who actually saw the ad', getRows:()=>{const nWks=r.weekly&&r.weekly.length||1;return r.platforms.map(pr=>{const ac=pr.audComp||pr.p?.audience_comp||0.65;const ruc=pr.p.real_users_coef||2.0;const view=pr.p.viewability||0.7;const univ=r.universe||DEF_UNIVERSE;const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));const cpm=(pr.p.base_cpm||0)*(1-disc);const baseWkVRU=Math.round((pr.uniqueCookies||0)/ruc*ac*view);const wkRch=Math.min(0.999,baseWkVRU/univ);let cum=0;for(let i=0;i<nWks;i++){cum=cum+wkRch-cum*wkRch;}const totVRU=Math.round(Math.min(0.98,cum)*univ);const weeks=(r.weekly||[]).map((_,wi)=>{const wb=platWkBud(pr,wi);if(wb===0)return 0;const wi_impr=cpm>0?(wb/cpm)*1000:0;const wi_ck=pr.frq>0?wi_impr/pr.frq:0;return Math.round(wi_ck/ruc*ac*view);});return{pid:pr.p.id,label:pr.p.label,seller:pr.p.seller||'',type:pr.p.type,tot:totVRU,monthVal:totVRU,weeks,noMonthSum:true};});}},
    {key:'clicks',      label:'Clicks',               color:'#00B0F0', getRows:()=>platWeekly('clicks')},
    {key:'conversions', label:'Conversions',           color:'#1B5E8C', note:'Post-click & post-view (set conversion rate per platform)', getRows:()=>{const wkBuds=r.wkBudgets||[];const totBud=r.tot.budget||1;return r.platforms.map(pr=>{const cvr=pr.p.cvr||0;const tot=pr.clicks*(cvr||0);const weeks=wkBuds.length>0?wkBuds.map(wb=>tot*(wb/totBud)):(r.weekly||[]).map(wk=>tot*(wk.frac/wFT));return{pid:pr.p.id,label:pr.p.label,seller:pr.p.seller||'',type:pr.p.type,tot,weeks};});}},
    {key:'views',       label:'Views',                color:'#7030A0', getRows:()=>platWeekly('views')},
    {key:'vimpr',       label:'Viewable Impressions', color:'#C19A6B', getRows:()=>platWeekly('vimpr')},
  ];

  function WeeklyTable({sectionKey,label,color,note}){
    const sec=METRIC_SECTIONS.find(s=>s.key===sectionKey);if(!sec||!r)return null;
    const rows=sec.getRows();
    // For FRQ/Cookies/RealUsers: weighted avg; others: sum
    let totRow;
    if(sec.key==='frq'){
      const totI=r.platforms.reduce((s,pr)=>s+(pr.impr||0),0)||1;
      const wAvg=r.platforms.reduce((s,pr)=>s+(pr.frq||0)*(pr.impr||0),0)/totI;
      totRow={pid:'__tot',label:'Total',tot:wAvg,weeks:(r.weekly||[]).map(()=>wAvg),noMonthSum:true};
    } else if(sec.key==='cookies'){
      const totI=r.platforms.reduce((s,pr)=>s+(pr.impr||0),0)||1;
      const avgFrq=r.platforms.reduce((s,pr)=>s+(pr.frq||0)*(pr.impr||0),0)/totI||1;
      const totCk=Math.round(totI/avgFrq);
      // Per-week: sum cookies across all platforms using platWkBud
      const wkCkArr=(r.weekly||[]).map((_,wi)=>rows.reduce((s,row)=>s+(row.weeks[wi]||0),0));
      totRow={pid:'__tot',label:'Total',tot:totCk,monthVal:totCk,weeks:wkCkArr,noMonthSum:true};
    } else if(sec.key==='taUsers'){
      const universe=r.universe||DEF_UNIVERSE;
      // Per-week Sainsbury using platWkBud for each week
      const wkRUArr=(r.weekly||[]).map((_,wi)=>{
        const pools={yt:[],social:[],other:[]};
        r.platforms.forEach(pr=>{
          const wb=platWkBud(pr,wi);if(wb===0)return;
          const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));
          const cpm=(pr.p.base_cpm||0)*(1-disc);
          const wi_impr=cpm>0?(wb/cpm)*1000:0;
          const wi_ck=pr.frq>0?wi_impr/pr.frq:0;
          const ac=pr.audComp||pr.p?.audience_comp||0.65;
          const ruc=pr.p.real_users_coef||2.0;
          const pool=pr.p?.reach_pool==='yt'?'yt':pr.p?.reach_pool==='social'?'social':'other';
          pools[pool].push({cookies:wi_ck,ruc,audComp:ac});
        });
        return Math.round(Math.min(0.98,1-(1-poolReach(pools.yt,universe))*(1-poolReach(pools.social,universe))*(1-poolReach(pools.other,universe)))*universe);
      });
      // Total: cumulative Sainsbury using average week pools
      const wPools={yt:[],social:[],other:[]};
      r.platforms.forEach(pr=>{const pool=pr.p?.reach_pool==='yt'?'yt':pr.p?.reach_pool==='social'?'social':'other';wPools[pool].push({cookies:pr.uniqueCookies||0,ruc:pr.p.real_users_coef||2.0,audComp:pr.audComp||pr.p?.audience_comp||0.65});});
      const wkReach=Math.min(0.98,1-(1-poolReach(wPools.yt,universe))*(1-poolReach(wPools.social,universe))*(1-poolReach(wPools.other,universe)));
      const nWksRU=r.weekly&&r.weekly.length||1;let cumRU=0;for(let i=0;i<nWksRU;i++){cumRU=cumRU+wkReach-cumRU*wkReach;}
      const totRU=Math.round(Math.min(0.98,cumRU)*universe);
      totRow={pid:'__tot',label:'Total',tot:totRU,monthVal:totRU,weeks:wkRUArr,noMonthSum:true};
    } else if(sec.key==='vRealUsers'){
      const universe=r.universe||DEF_UNIVERSE;
      const nWksVRU=r.weekly&&r.weekly.length||1;
      // Per-week Sainsbury using platWkBud (respects mix overrides)
      const wkVRUArr=(r.weekly||[]).map((_,wi)=>{
        const pools={yt:[],social:[],other:[]};
        r.platforms.forEach(pr=>{
          const wb=platWkBud(pr,wi);if(wb===0)return;
          const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));
          const cpm=(pr.p.base_cpm||0)*(1-disc);
          const wi_impr=cpm>0?(wb/cpm)*1000:0;
          const wi_ck=pr.frq>0?wi_impr/pr.frq:0;
          const ac=pr.audComp||pr.p?.audience_comp||0.65;
          const ruc=pr.p.real_users_coef||2.0;
          const view=pr.p.viewability||0.7;
          const pool=pr.p?.reach_pool==='yt'?'yt':pr.p?.reach_pool==='social'?'social':'other';
          pools[pool].push({cookies:wi_ck*view,ruc,audComp:ac});
        });
        return Math.round(Math.min(0.98,1-(1-poolReach(pools.yt,universe))*(1-poolReach(pools.social,universe))*(1-poolReach(pools.other,universe)))*universe);
      });
      // Total: cumulative Sainsbury
      const wPools={yt:[],social:[],other:[]};
      r.platforms.forEach(pr=>{
        const ac=pr.audComp||pr.p?.audience_comp||0.65;
        const ruc=pr.p.real_users_coef||2.0;
        const view=pr.p.viewability||0.7;
        const pool=pr.p?.reach_pool==='yt'?'yt':pr.p?.reach_pool==='social'?'social':'other';
        wPools[pool].push({cookies:(pr.uniqueCookies||0)*view,ruc,audComp:ac});
      });
      const wYT=poolReach(wPools.yt,universe);
      const wSoc=poolReach(wPools.social,universe);
      const wOth=poolReach(wPools.other,universe);
      const wkReach=Math.min(0.98,1-(1-wYT)*(1-wSoc)*(1-wOth));
      let cumVRU2=0;for(let i=0;i<nWksVRU;i++){cumVRU2=cumVRU2+wkReach-cumVRU2*wkReach;}
      const totVRU=Math.round(Math.min(0.98,cumVRU2)*universe);
      totRow={pid:'__tot',label:'Total',tot:totVRU,monthVal:totVRU,weeks:wkVRUArr,noMonthSum:true};
    } else {
      totRow={pid:'__tot',label:'Total',tot:rows.reduce((s,r)=>s+r.tot,0),weeks:(r.weekly||[]).map((_,i)=>rows.reduce((s,r2)=>s+(r2.weeks[i]||0),0))};
    }

    return(<div style={{marginBottom:24}}>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
        <div style={{width:10,height:10,borderRadius:'50%',background:color,flexShrink:0}}/>
        <span style={{fontWeight:700,fontSize:12,color}}>{label}</span>
        {note&&<span style={{fontSize:9,color:'var(--text3)',fontStyle:'italic'}}>{note}</span>}
      </div>
      <div style={{overflowX:'auto'}}>
        <table style={{borderCollapse:'collapse',fontSize:10}}>
          <thead><tr style={{background:color+'22',borderBottom:'2px solid '+color}}>
            <th style={{...TH,background:color+'22',textAlign:'left',minWidth:140,position:'sticky',left:0,zIndex:2}}>Platform</th>
            <th style={{...TH,background:color+'22',width:60}}>Format</th>
            {(r.weekly||[]).map(wk=><th key={wk.week} style={{...TH,background:color+'22',width:44,fontSize:8}}>
              W{wk.week}
              {wk.frac<0.99&&<span style={{color:'#E07000'}}>*</span>}
              <div style={{fontWeight:400,color:'var(--text3)',fontSize:7,whiteSpace:'nowrap'}}>{fdate(weekStartDate(wk.week))}</div>
            </th>)}
            <th style={{...TH,background:color,color:'#fff',width:72}}>TOTAL</th>
            {/* Monthly summary cols */}
            {MONTHS.map((m,mi)=>{const ws=MONTH_WEEKS[mi];const hasData=r.weekly&&r.weekly.some(wk=>ws.includes(wk.week));return hasData?<th key={m} style={{...TH,background:color+'44',width:64,fontSize:8}}>{MN[mi]}</th>:null;})}
          </tr></thead>
          <tbody>
            {[...rows,totRow].map((row,ri)=>{
              const isTotal=row.pid==='__tot';
              const typ=AD_TYPES.find(t=>t.key===row.type);
              const monthTots=MONTHS.map((_,mi)=>{
                const ws=MONTH_WEEKS[mi];
                const mWks=(r.weekly||[]).filter(wk=>ws.includes(wk.week));
                if(!mWks.length)return 0;
                if(sec.isFixed){
                  // FRQ: simple average of weekly values for that month
                  return mWks.reduce((s,wk)=>{const idx=(r.weekly||[]).findIndex(x=>x.week===wk.week);return s+(row.weeks[idx]||0);},0)/mWks.length;
                }
                if(row.noMonthSum){
                  const univ2=r.universe||DEF_UNIVERSE;
                  if(sec.key==='cookies'&&row.pid==='__tot'){
                    // cookies TOTAL monthly = monthImpressions / weightedAvgFrq (same as flight total)
                    const totI2=r.platforms.reduce((s,pr)=>s+(pr.impr||0),0)||1;
                    const avgFrq2=r.platforms.reduce((s,pr)=>s+(pr.frq||0)*(pr.impr||0),0)/totI2||1;
                    const mTotImpr=mWks.reduce((s,wk)=>{const idx=(r.weekly||[]).findIndex(x=>x.week===wk.week);return s+r.platforms.reduce((ss,pr)=>ss+(pr.impr||0)*(wk.frac||1)/((r.weekly||[]).reduce((sf,w)=>sf+(w.frac||1),0)||1),0);},0);
                    return Math.round(mTotImpr/avgFrq2);
                  }
                  if(sec.key==='cookies'){
                    // per-platform cookies monthly = sum of weekly values
                    return mWks.reduce((s,wk)=>{const idx=(r.weekly||[]).findIndex(x=>x.week===wk.week);return s+(row.weeks[idx]||0);},0);
                  }
                  // taUsers/vRealUsers: Sainsbury cumulative for this month's weeks
                  let cumM=0;
                  mWks.forEach(wk=>{
                    const idx=(r.weekly||[]).findIndex(x=>x.week===wk.week);
                    const wkAbs=row.weeks[idx]||0;
                    const wkR=Math.min(0.999,wkAbs/univ2);
                    cumM=Math.min(0.98,cumM+wkR-cumM*wkR);
                  });
                  return Math.round(cumM*univ2);
                }
                return mWks.reduce((s,wk)=>{const idx=(r.weekly||[]).findIndex(x=>x.week===wk.week);return s+(row.weeks[idx]||0);},0);
              });
              return(<tr key={row.pid} style={{background:isTotal?color+'11':'#fff',fontWeight:isTotal?700:400}}>
                <td style={{...TD,fontWeight:isTotal?700:500,position:'sticky',left:0,background:isTotal?color+'11':'#fff',zIndex:1}}>
                  {isTotal?<span style={{color}}>Total</span>:<div><div style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:6,height:6,borderRadius:'50%',background:typ?.color||'#ccc',display:'inline-block',flexShrink:0}}/>{row.label}</div><div style={{fontSize:8,color:'var(--text3)'}}>{row.seller}</div></div>}
                </td>
                <td style={{...TDc,fontSize:8,color:'var(--text3)'}}>{isTotal?'':typ?.label||''}</td>
                {row.weeks.map((v,wi)=>(
                  <td key={wi} style={{...TDr,color:isTotal?color:'var(--text2)',fontSize:9}}>
                    {sec.isFixed?ff(v):fn(Math.round(v))}
                  </td>
                ))}
                <td style={{...TDr,fontWeight:700,color,fontSize:10}}>{sec.isFixed?ff(row.tot):fn(Math.round(row.tot))}</td>
                {monthTots.map((mv,mi)=>{const ws=MONTH_WEEKS[mi];const hasData=r.weekly&&r.weekly.some(wk=>ws.includes(wk.week));if(!hasData)return null;const dispV=row.noMonthSum?monthTots[mi]:mv;return<td key={mi} style={{...TDr,color,fontSize:9,fontWeight:isTotal?700:400}}>{sec.isFixed?ff(dispV):fn(Math.round(dispV))}</td>;})}
              </tr>);
            })}
          </tbody>
        </table>
      </div>
    </div>);
  }

  if(!f||!r)return<div style={{padding:24,color:'var(--text3)'}}>Select a flight.</div>;

  return(<div style={{padding:'16px 24px'}}>
    <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:16,flexWrap:'wrap'}}>
      <div className="lbl" style={{marginBottom:0,whiteSpace:'nowrap'}}>Flight:</div>
      <select className="sel" style={{minWidth:240}} value={f.id} onChange={e=>setSelFlight(e.target.value)}>
        {flights.map(fl=><option key={fl.id} value={fl.id}>{fl.brand} — {fl.label} ({fdate(fl.startDate)}–{fdate(fl.endDate)})</option>)}
      </select>
      <div style={{flex:1}}/>
      <button className="btn btn-ghost btn-sm" onClick={()=>onExportExcel(f.id)}>⬇ Export Excel</button>
    </div>

    {/* Campaign summary KPIs */}
    <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:20,padding:'10px 12px',background:'var(--gold-bg)',border:'1px solid var(--gold-border)',borderRadius:6}}>
      {[
        {l:'Budget',v:fe(r.tot.budget)},
        {l:'TA Impressions',v:fn(r.tot.impressions)},
        {l:'VRCH%',v:fp(r.tot.reach),hi:true},
        {l:'vRCH abs.',v:fn(Math.round(r.tot.reach*((flights.find(x=>x.id===f.id)?.budget||10000))))||'—'},
        {l:'eCPM',v:fe2(r.tot.ecpm)},
        {l:'vCPM',v:fe2(r.tot.vcpm)},
        {l:'Avg Freq.',v:ff(r.tot.frequency)},
        {l:'Views',v:fn(r.tot.views)},
        {l:'Clicks',v:fn(r.tot.clicks)},
      ].map(k=>(
        <div key={k.l} style={{background:k.hi?'var(--gold)':'#fff',border:'1px solid '+(k.hi?'#B8860B':'var(--border)'),borderRadius:5,padding:'4px 10px',textAlign:'center',minWidth:72}}>
          <div style={{fontSize:8,color:k.hi?'#fff':'var(--text3)',textTransform:'uppercase',letterSpacing:'.04em',marginBottom:1}}>{k.l}</div>
          <div style={{fontSize:12,fontWeight:800,color:k.hi?'#fff':'var(--text)'}}>{k.v}</div>
        </div>
      ))}
    </div>

    {METRIC_SECTIONS.map(sec=><WeeklyTable key={sec.key} sectionKey={sec.key} label={sec.label} color={sec.color} note={sec.note}/>)}

    {/* Cumulative vRCH curve */}
    <div style={{marginBottom:24}}>
      <div style={{fontWeight:700,fontSize:12,marginBottom:8,color:'var(--gold-dark)'}}>📈 Cumulative Reach & Viewable Real Users — {f.label}</div>
      <div style={{overflowX:'auto'}}>
        <table style={{borderCollapse:'collapse',fontSize:10,width:'100%'}}>
          <thead><tr style={{background:'var(--gold-bg)'}}>
            <th style={{...TH,textAlign:'left',width:120}}>Week</th>
            <th style={{...TH,width:50}}>Days</th>
            <th style={{...TH,width:70}}>+RCH%/wk</th>
            <th style={{...TH,width:80}}>Cum. RCH%</th>
            <th style={{...TH,width:80,color:'#C00000'}}>+View. Users/wk</th>
            <th style={{...TH,width:90,color:'#C00000'}}>Cum. View. Users</th>
            <th style={TH}>Progress (View. Users)</th>
          </tr></thead>
          <tbody>
            {cumRch.map((cr,i)=>{
              const days=Math.round(cr.frac*7);
              const taUsers=Math.round(cr.cumReach*(r.universe||DEF_UNIVERSE));
              return(<tr key={i} style={{background:i%2===0?'#fff':'#FAFAFA'}}>
                <td style={{...TD,fontSize:9,color:'var(--text3)',fontWeight:600}}>W{cr.week} · {fdate(weekStartDate(cr.week))}{days<7&&<span style={{color:'#E07000'}}> ({days}d)</span>}</td>
                <td style={{...TDc,fontSize:9,color:days<7?'#E07000':'var(--text3)'}}>{days}</td>
                <td style={{...TDc,color:'var(--text3)',fontSize:9}}>+{fp(cr.weekReach)}</td>
                <td style={{...TDc,color:'var(--gold-dark)',fontWeight:800,fontSize:12}}>{fp(cr.cumReach)}</td>
                <td style={{...TDc,color:'#C00000',fontSize:9,fontWeight:600}}>{fn(cr.weekVRUabs||0)}</td>
                <td style={{...TDc,color:'#C00000',fontWeight:800,fontSize:11}}>{fn(cr.cumVRUabs||0)}</td>
                <td style={TD}>
                  <div style={{position:'relative',height:20,background:'var(--surface3)',borderRadius:8,overflow:'hidden',minWidth:120}}>
                    <div style={{position:'absolute',left:0,top:0,height:'100%',width:Math.min(100,(cr.cumVRU!=null?cr.cumVRU:cr.cumReach)*100).toFixed(1)+'%',background:'linear-gradient(90deg,#C00000 0%,#FF4444 100%)',borderRadius:8,transition:'width .3s'}}/>
                    <div style={{position:'absolute',left:0,top:0,height:'100%',width:Math.min(100,cr.cumReach*100).toFixed(1)+'%',background:'linear-gradient(90deg,var(--gold) 0%,var(--gold-dark) 100%)',borderRadius:8,opacity:0.5,transition:'width .3s'}}/>
                    <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,color:'#fff',fontWeight:700,textShadow:'0 1px 2px rgba(0,0,0,.5)'}}>{fp(cr.cumVRU!=null?cr.cumVRU:cr.cumReach)}</div>
                  </div>
                </td>
              </tr>);
            })}
          </tbody>
        </table>
        <div style={{display:'flex',gap:16,marginTop:6,fontSize:9,color:'var(--text3)'}}>
          <span><span style={{display:'inline-block',width:12,height:8,background:'linear-gradient(90deg,#C00000,#FF4444)',borderRadius:2,marginRight:4,verticalAlign:'middle'}}/>Viewable Real Users reach (bar)</span>
          <span><span style={{display:'inline-block',width:12,height:8,background:'linear-gradient(90deg,var(--gold),var(--gold-dark))',borderRadius:2,marginRight:4,verticalAlign:'middle',opacity:0.5}}/>Regular reach vRCH% (overlay)</span>
          <span style={{color:'var(--text3)'}}>Progress bar = Viewable Users reach; gold overlay = regular RCH (without viewability)</span>
        </div>
      </div>
    </div>
  </div>);
}

// ─── AUDIENCE MANAGER — with per-platform audience_comp overrides ─────────────
function AudienceManager({audiences,setAudiences,catalogue,onClose,clientId}){
  const[selId,setSelId]=useState(audiences[0]?.id||null);
  const[editName,setEditName]=useState('');
  const[editUniv,setEditUniv]=useState(1887226);
  const[editComp,setEditComp]=useState({});
  const[editMode,setEditMode]=useState(false);
  const[newScope,setNewScope]=useState('client'); // scope for next new audience
  const[saving,setSaving]=useState(false);

  // On mount: fetch DB audiences and merge with plan snapshot
  React.useEffect(()=>{
    const url=clientId?`${API}/audiences?client_id=${clientId}`:`${API}/audiences`;
    fetch(url).then(r=>r.ok?r.json():[]).then(dbAuds=>{
      if(!dbAuds.length)return;
      // Map DB audiences to frontend format
      const dbMapped=dbAuds.map(a=>({
        id:'db_'+a.id, _db_id:a.id, name:a.name, universe:a.universe,
        platComp:a.plat_comp||{}, scope:a.scope, client_id:a.client_id,
      }));
      // Build label->dbId map for deduplication
      const dbByName={};
      dbMapped.forEach(a=>{dbByName[a.name.toLowerCase().trim()]=a;});
      // Merge: keep plan-only audiences (no _db_id, not matching any DB name)
      setAudiences(prev=>{
        const planOnly=prev.filter(a=>!a._db_id&&!dbByName[a.name.toLowerCase().trim()]);
        return [...dbMapped,...planOnly];
      });
    }).catch(()=>{});
  },[]);

  function startEdit(a){setSelId(a.id);setEditName(a.name);setEditUniv(a.universe);setEditComp({...(a.platComp||{})});setEditMode(true);}

  function saveEdit(){
    const a=audiences.find(x=>x.id===selId);
    const updated={...a,name:editName,universe:editUniv,platComp:editComp,_has_plan_override:true};
    setAudiences(p=>p.map(x=>x.id===selId?updated:x));
    setEditMode(false);
    // Persist to DB if has _db_id
    if(a?._db_id){
      // Convert platComp keys: if they're frontend ids (db_X), store as db_id strings
      const platCompForDb={};
      Object.entries(editComp).forEach(([k,v])=>{
        const dbId=k.startsWith('db_')?k.replace('db_',''):k;
        platCompForDb[dbId]=v;
      });
      fetch(`${API}/audiences/${a._db_id}`,{method:'PATCH',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({name:editName,universe:editUniv,plat_comp:platCompForDb})
      }).catch(()=>{});
    }
  }

  function addNew(){
    const tempId='aud_'+Date.now();
    const na={id:tempId,name:'New Audience',universe:1887226,platComp:{},scope:newScope};
    setAudiences(p=>[...p,na]);
    startEdit(na);
    // POST to DB unless plan-only
    if(newScope!=='plan'){
      setSaving(true);
      fetch(`${API}/audiences`,{method:'POST',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({name:'New Audience',universe:1887226,plat_comp:{},scope:newScope,client_id:newScope==='client'?clientId:null})
      }).then(r=>r.ok?r.json():null).then(d=>{
        if(d){
          const finalA={...na,_db_id:d.id,id:'db_'+d.id,scope:d.scope};
          setAudiences(p=>p.map(x=>x.id===tempId?finalA:x));
          setSelId('db_'+d.id);
        }
        setSaving(false);
      }).catch(()=>setSaving(false));
    }
  }

  function del(id){
    if(!window.confirm('Delete audience?'))return;
    const a=audiences.find(x=>x.id===id);
    setAudiences(p=>p.filter(x=>x.id!==id));
    setSelId(audiences.find(x=>x.id!==id)?.id||null);
    setEditMode(false);
    if(a?._db_id){fetch(`${API}/audiences/${a._db_id}`,{method:'DELETE'}).catch(()=>{});}
  }

  const sel=audiences.find(a=>a.id===selId);

  return(<div className="modal-overlay" onClick={ev=>{if(ev.target===ev.currentTarget)onClose();}}>
    <div className="modal-box" style={{maxWidth:780,height:'80vh',display:'flex',flexDirection:'column'}}>
      <div className="modal-hdr"><span style={{fontWeight:700,fontSize:13}}>👥 Target Audience Database</span><button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button></div>
      <div style={{display:'flex',flex:1,overflow:'hidden'}}>
        {/* LEFT: audience list */}
        <div style={{width:220,borderRight:'1px solid var(--border)',padding:12,overflowY:'auto',flexShrink:0}}>
          <div style={{fontSize:10,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:8}}>Audiences</div>
          {audiences.map(a=>{
            const scopeColor=a.scope==='global'?'var(--teal)':a.scope==='client'?'#7030A0':'var(--gold-dark)';
            const scopeLabel=a.scope==='global'?'🌐':a.scope==='client'?'🏢':'📋';
            return(<div key={a.id} onClick={()=>{setSelId(a.id);setEditMode(false);}} style={{padding:'7px 10px',marginBottom:4,borderRadius:5,cursor:'pointer',background:selId===a.id?'var(--gold-bg)':'transparent',border:'1px solid '+(selId===a.id?'var(--gold-border)':'transparent')}}>
              <div style={{display:'flex',alignItems:'center',gap:4}}>
                <span style={{fontSize:9,color:scopeColor,fontWeight:700}}>{scopeLabel}</span>
                <span style={{fontWeight:600,fontSize:11}}>{a.name}</span>
              </div>
              <div style={{fontSize:9,color:'var(--text3)'}}>Universe: {a.universe.toLocaleString()}</div>
              <div style={{fontSize:9,color:'var(--gold-dark)'}}>{Object.keys(a.platComp||{}).length} comp. overrides</div>
            </div>);
          })}
          <div style={{marginTop:8,borderTop:'1px solid var(--border)',paddingTop:8}}>
            <div style={{fontSize:9,color:'var(--text3)',marginBottom:4,fontWeight:600}}>New audience scope:</div>
            <div style={{display:'flex',gap:4,marginBottom:6}}>
              {[['global','🌐 Global','var(--teal)'],['client','🏢 Client','#7030A0'],['plan','📋 Plan','var(--gold-dark)']].map(([s,l,c])=>(
                <button key={s} onClick={()=>setNewScope(s)} style={{flex:1,fontSize:9,padding:'2px 4px',borderRadius:8,border:'1px solid '+(newScope===s?c:'var(--border)'),background:newScope===s?c+'22':'transparent',color:newScope===s?c:'var(--text3)',fontWeight:newScope===s?700:400,cursor:'pointer'}}>{l}</button>
              ))}
            </div>
            <button className="btn btn-ghost btn-sm" style={{width:'100%'}} onClick={addNew} disabled={saving}>{saving?'Saving…':'+ New audience'}</button>
          </div>
        </div>
        {/* RIGHT: editor */}
        <div style={{flex:1,padding:'16px 24px',overflowY:'auto'}}>
          {sel&&(<>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
              <div style={{fontWeight:700,fontSize:13}}>{editMode?'Editing:':''} {sel.name}</div>
              <div style={{display:'flex',gap:6}}>
                {!editMode&&<button className="btn btn-ghost btn-sm" onClick={()=>startEdit(sel)}>✎ Edit</button>}
                {editMode&&<button className="btn btn-primary btn-sm" onClick={saveEdit}>✓ Save</button>}
                {editMode&&<button className="btn btn-ghost btn-sm" onClick={()=>setEditMode(false)}>Cancel</button>}
                <button className="btn btn-danger btn-sm" onClick={()=>del(sel.id)}>Delete</button>
              </div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:16}}>
              <div><div className="lbl">Audience name</div>
                {editMode?<input className="inp" value={editName} style={{width:'100%'}} onChange={e=>setEditName(e.target.value)}/>:<div style={{fontWeight:600,padding:'5px 8px',background:'var(--surface2)',borderRadius:4,border:'1px solid var(--border)'}}>{sel.name}</div>}
              </div>
              <div><div className="lbl">Universe (population)</div>
                {editMode?<input className="inn" type="number" value={editUniv} style={{width:'100%'}} onChange={e=>setEditUniv(+e.target.value)}/>:<div style={{fontWeight:600,padding:'5px 8px',background:'var(--surface2)',borderRadius:4,border:'1px solid var(--border)'}}>{sel.universe.toLocaleString()}</div>}
              </div>
            </div>
            <div style={{marginBottom:8}}>
              <div style={{fontWeight:700,fontSize:11,marginBottom:4}}>Platform Audience Composition Overrides</div>
              <p style={{fontSize:10,color:'var(--text3)',marginBottom:10}}>Set a custom <strong>audience_comp</strong> for each platform when using this audience. Leave blank to use the platform default. This affects reach calculations — when this audience is selected for a flight, these values replace the defaults automatically.</p>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:10}}>
                <thead><tr style={{background:'var(--gold-bg)'}}>
                  <th style={{...TH,textAlign:'left',minWidth:160}}>Platform</th>
                  <th style={{...TH,width:80}}>Type</th>
                  <th style={{...TH,width:100}}>Default comp.</th>
                  <th style={{...TH,width:120}}>Override for this audience</th>
                  <th style={{...TH,width:80}}>Δ vs default</th>
                </tr></thead>
                <tbody>
                  {catalogue.filter(p=>p.active!==false).map(p=>{
                    const typ=AD_TYPES.find(t=>t.key===p.type);
                    const def=p.audience_comp||0.65;
                    const ov=editMode?editComp[p.id]:sel.platComp?.[p.id];
                    const hasOv=ov!=null&&ov!=='';
                    const delta=hasOv?(+ov-def):0;
                    return(<tr key={p.id}>
                      <td style={{...TD,fontWeight:500}}>{p.label}<div style={{fontSize:8,color:'var(--text3)'}}>{p.seller}</div></td>
                      <td style={{...TDc}}><span style={{color:typ?.color,fontWeight:600,fontSize:9}}>{typ?.label}</span></td>
                      <td style={{...TDc,color:'var(--text3)'}}>{(def*100).toFixed(1)}%</td>
                      <td style={TDc}>
                        {editMode
                          ?<input className="inn" type="number" min={0} max={100} step={0.1} placeholder={(def*100).toFixed(1)} key={p.id+'_'+selId} defaultValue={editComp[p.id]!=null?(editComp[p.id]*100):''} style={{width:'100%',textAlign:'center',background:hasOv?'var(--gold-bg)':'transparent'}}
                            onBlur={e=>{const v=e.target.value;setEditComp(prev=>{if(v===''||v===null||isNaN(+v)){const n={...prev};delete n[p.id];return n;}return{...prev,[p.id]:parseFloat(v)/100};});}}/>
                          :<span style={{fontWeight:hasOv?700:400,color:hasOv?'var(--gold-dark)':'var(--text3)'}}>{hasOv?(+ov*100).toFixed(1)+'%':'—'}</span>
                        }
                      </td>
                      <td style={{...TDc,color:delta>0.01?'#2E8B57':delta<-0.01?'#C00000':'var(--text3)',fontWeight:Math.abs(delta)>0.01?700:400}}>
                        {hasOv?(delta>=0?'+':'')+((delta)*100).toFixed(1)+'pp':'—'}
                      </td>
                    </tr>);
                  })}
                </tbody>
              </table>
            </div>
          </>)}
          {!sel&&<div style={{color:'var(--text3)',padding:20}}>Select or create an audience.</div>}
        </div>
      </div>
      <div className="modal-footer"><div style={{flex:1}}/><button className="btn btn-primary btn-sm" onClick={onClose}>Done</button></div>
    </div>
  </div>);
}

// ─── INPUTS DRAWER ────────────────────────────────────────────────────────────
function InputsDrawer({open,onClose,catalogue,setCatalogue,universe,setUniverse,clientId,planStatus,audiences,setAudiences}){
  const[tab,setTab]=useState('pricing');const tmr=useRef({});
  const[catScope,setCatScope]=useState('plan'); // 'global'|'client'|'plan'

  // updP: patch catalogue + persist to correct layer based on catScope
  function updP(id,patch,scope){
    const s=scope||catScope;
    setCatalogue(prev=>{
      const upd=prev.map(c=>c.id===id?{...c,...patch}:c);
      clearTimeout(tmr.current[id]);
      tmr.current[id]=setTimeout(()=>{
        const p=upd.find(c=>c.id===id);if(!p)return;
        if(s==='global'&&p._db_id){
          // Global: PATCH the DB platform directly
          fetch(`${API}/platforms/${p._db_id}`,{method:'PATCH',headers:{'Content-Type':'application/json'},body:JSON.stringify({name:p.label,seller:p.seller,cpm_eur:p.base_cpm||null,cpc_eur:p.cpc||null,viewability:p.viewability,audience_comp:p.audience_comp,real_users_coef:p.real_users_coef,default_ctr:p.ctr,default_vtr:p.vtr||null,default_weekly_frq:p.weekly_frq,default_monthly_frq:p.monthly_frq,active:p.active!==false,max_impressions:p.max_impressions||null,cvr:p.cvr||null,seasonal_cpm:p.seasonal_cpm||null})}).catch(()=>{});
        } else if(s==='client'&&p._db_id&&clientId){
          // Client: PATCH client catalogue overrides
          const dbKey=String(p._db_id);
          const ovrPatch={};
          if('base_cpm' in patch)ovrPatch.cpm_eur=patch.base_cpm;
          if('cpc' in patch)ovrPatch.cpc_eur=patch.cpc;
          if('viewability' in patch)ovrPatch.viewability=patch.viewability;
          if('audience_comp' in patch)ovrPatch.audience_comp=patch.audience_comp;
          if('real_users_coef' in patch)ovrPatch.real_users_coef=patch.real_users_coef;
          if('ctr' in patch)ovrPatch.default_ctr=patch.ctr;
          if('vtr' in patch)ovrPatch.default_vtr=patch.vtr;
          if('weekly_frq' in patch)ovrPatch.default_weekly_frq=patch.weekly_frq;
          if('monthly_frq' in patch)ovrPatch.default_monthly_frq=patch.monthly_frq;
          if('active' in patch)ovrPatch.active=patch.active;
          if('base_discount' in patch)ovrPatch.base_discount=patch.base_discount;
          if('seasonal_cpm' in patch)ovrPatch.seasonal_cpm=patch.seasonal_cpm;
          if('max_impressions' in patch)ovrPatch.max_impressions=patch.max_impressions;
          if('label' in patch)ovrPatch.name=patch.label;
          if(Object.keys(ovrPatch).length){
            fetch(`${API}/clients/${clientId}/catalogue-overrides`,{method:'PATCH',headers:{'Content-Type':'application/json'},body:JSON.stringify({[dbKey]:ovrPatch})}).catch(()=>{});
          }
          // Mark as having client override
          setCatalogue(prev2=>prev2.map(c=>c.id===id?{...c,_client_overrides:{...(c._client_overrides||{}),...ovrPatch}}:c));
        }
        // Plan scope: no API call — changes live in catalogue state, saved with plan on next Save
      },800);
      return upd;
    });
  }

  function resetClientOverride(p){
    if(!p._db_id||!clientId)return;
    fetch(`${API}/clients/${clientId}/catalogue-overrides/${p._db_id}`,{method:'DELETE'}).catch(()=>{});
    setCatalogue(prev=>prev.map(c=>c.id===p.id?{...c,_client_overrides:{},_has_plan_override:false}:c));
  }
  return(<><div className={'drawer-overlay'+(open?' open':'')} onClick={onClose}/>
    <div className={'inp-drawer'+(open?' open':'')}>
      <div className="inp-drawer-hdr"><span style={{fontWeight:700,fontSize:13}}>⚙ M1 Reference Data</span><button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button></div>
      <div className="inp-drawer-tabs">{[['pricing','CPM & Pricing'],['seasonal','Seasonal CPM'],['audience','Audience & Reach'],['frequency','Frequency'],['catalogue','Catalogue'],['bulk','⬆⬇ Bulk Import']].map(([t,l])=><button key={t} className={'tab-btn'+(tab===t?' on':'')} onClick={()=>setTab(t)}>{l}</button>)}</div>
      {tab!=='bulk'&&<div style={{display:'flex',alignItems:'center',gap:6,padding:'6px 12px',background:'var(--surface3)',borderBottom:'1px solid var(--border)'}}>
        <span style={{fontSize:10,color:'var(--text3)',fontWeight:600}}>Editing scope:</span>
        {[['plan','📋 Plan','Changes affect this plan only, saved with plan'],['client','🏢 Client','Changes apply to all plans for this client'],['global','🌐 Global','Changes affect all clients and plans — use with care']].map(([s,l,tip])=>(
          <button key={s} title={tip} onClick={()=>setCatScope(s)} style={{fontSize:10,padding:'2px 10px',borderRadius:12,border:'1px solid '+(catScope===s?s==='global'?'var(--red)':s==='client'?'var(--gold-dark)':'var(--teal)':'var(--border)'),background:catScope===s?s==='global'?'#ff000015':s==='client'?'var(--gold-bg)':'#00807015':'transparent',color:catScope===s?s==='global'?'var(--red)':s==='client'?'var(--gold-dark)':'var(--teal)':'var(--text3)',fontWeight:catScope===s?700:400,cursor:'pointer'}}>{l}</button>
        ))}
        {catScope==='global'&&<span style={{fontSize:9,color:'var(--red)',marginLeft:4}}>⚠ Global edits affect all clients</span>}
        {catScope==='client'&&!clientId&&<span style={{fontSize:9,color:'var(--text3)',marginLeft:4}}>No client context — open from a plan</span>}
      </div>}
      <div className="inp-drawer-body">
        <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:12,padding:'8px 12px',background:'var(--gold-bg)',border:'1px solid var(--gold-border)',borderRadius:6}}>
          <label style={{fontSize:11,fontWeight:700,color:'var(--text3)',whiteSpace:'nowrap'}}>Default Universe:</label>
          <input className="inn" type="number" step={1000} value={universe} style={{width:130}} onChange={e=>setUniverse(+e.target.value)}/>
          <span style={{fontSize:10,color:'var(--text3)'}}>Default universe (no audience selected): {universe.toLocaleString()}</span>
        </div>
        {tab==='pricing'&&<table className="out-table"><thead><tr><th>Platform</th><th>Type</th><th>Base CPM €</th><th>CPC €</th><th>Base Disc%</th><th>Eff. CPM</th></tr></thead><tbody>{catalogue.map(p=>{const typ=AD_TYPES.find(t=>t.key===p.type);const eff=p.base_cpm?p.base_cpm*(1-(p.base_discount||0)):null;return<tr key={p.id}><td style={{fontWeight:500,fontSize:10}}>{p.label}</td><td><span style={{color:typ?.color,fontWeight:600,fontSize:9}}>{typ?.label}</span></td><td><input className="inn" type="text" inputMode="decimal" value={p.base_cpm||''} placeholder="manual" style={{width:70}} onBlur={e=>{const v=parseFloat(e.target.value);updP(p.id,{base_cpm:isNaN(v)?0:v});}} onChange={e=>updP(p.id,{base_cpm:e.target.value})}/></td><td><input className="inn" type="text" inputMode="decimal" value={p.cpc||''} placeholder="—" style={{width:60}} onBlur={e=>{const v=parseFloat(e.target.value);updP(p.id,{cpc:isNaN(v)?0:v});}} onChange={e=>updP(p.id,{cpc:e.target.value})}/></td><td><input className="inn" type="number" min={0} max={100} step={1} value={Math.round((p.base_discount||0)*100)} style={{width:52}} onChange={e=>updP(p.id,{base_discount:+e.target.value/100})}/></td><td style={{color:'var(--gold-dark)',fontWeight:600,fontSize:10}}>{eff?'€'+eff.toFixed(2):'—'}</td></tr>;})}</tbody></table>}
        {tab==='seasonal'&&<div><p style={{fontSize:10,color:'var(--text3)',marginBottom:8}}>Monthly CPM multipliers per platform (1.0 = base price, 1.2 = +20%, 0.8 = -20%)</p><div style={{overflowX:'auto'}}><table className="out-table"><thead><tr><th>Platform</th>{['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map(m=><th key={m} style={{minWidth:42}}>{m}</th>)}</tr></thead><tbody>{catalogue.map(p=>{const sc=p.seasonal_cpm||Array(12).fill(1);return<tr key={p.id}><td style={{fontWeight:500,fontSize:10,whiteSpace:'nowrap'}}>{p.label}</td>{sc.map((v,mi)=><td key={mi}><input className="inn" type="text" inputMode="decimal" value={v} style={{width:42,fontSize:9,textAlign:'center'}} onFocus={e=>e.target.select()} onChange={e=>{const s=[...sc];s[mi]=e.target.value;updP(p.id,{seasonal_cpm:s});}} onBlur={e=>{const s=[...sc];const v=parseFloat(e.target.value);s[mi]=isNaN(v)||v<=0?1:Math.round(v*100)/100;updP(p.id,{seasonal_cpm:s});}}/></td>)}</tr>;})} </tbody></table></div></div>}
        {tab==='audience'&&<table className="out-table"><thead><tr><th>Platform</th><th>Reach Pool</th><th>Viewability</th><th>Audience Comp (default)</th><th>Real Users Coef</th></tr></thead><tbody>{catalogue.map(p=>{const gc=p.reach_pool==='yt'?'#FF0000':p.reach_pool==='social'?'#7030A0':'#4472C4';const gl=p.reach_pool==='yt'?'C—YouTube':p.reach_pool==='social'?'B—Social':'A—Other';return<tr key={p.id}><td style={{fontWeight:500,fontSize:10}}>{p.label}</td><td><select className="sel" value={p.reach_pool||'display'} style={{width:80,fontSize:9}} onChange={e=>updP(p.id,{reach_pool:e.target.value})}><option value="display">A—Display</option><option value="video">A—Video</option><option value="social">B—Social</option><option value="yt">C—YouTube</option></select></td><td><input className="inn" type="number" min={0} max={1} step={0.01} value={p.viewability} style={{width:60}} onChange={e=>updP(p.id,{viewability:+e.target.value})}/></td><td><input className="inn" type="number" min={0} max={1} step={0.01} value={p.audience_comp} style={{width:60}} onChange={e=>updP(p.id,{audience_comp:+e.target.value})}/></td><td><input className="inn" type="number" min={0.1} max={5} step={0.1} value={p.real_users_coef||1} style={{width:60}} onChange={e=>updP(p.id,{real_users_coef:+e.target.value})}/></td></tr>;})} </tbody></table>}
        {tab==='frequency'&&<div><p style={{fontSize:10,color:'var(--text3)',marginBottom:8}}>Weekly FRQ is the average daily ad exposures per user per week. Monthly FRQ is derived automatically: avg of weekly values across weeks in month (ML aggregation method).</p><table className="out-table"><thead><tr><th>Platform</th><th>Type</th><th>Weekly FRQ</th><th>Monthly FRQ (auto)</th></tr></thead><tbody>{catalogue.filter(p=>p.type!=='sem').map(p=>{const typ=AD_TYPES.find(t=>t.key===p.type);const mfrq=+(p.weekly_frq||3).toFixed(1);return<tr key={p.id}><td style={{fontWeight:500,fontSize:10}}>{p.label}</td><td><span style={{color:typ?.color,fontWeight:600,fontSize:9}}>{typ?.label}</span></td><td><input className="inn" type="number" min={1} max={30} step={0.5} value={p.weekly_frq||3} style={{width:70}} onChange={e=>updP(p.id,{weekly_frq:+e.target.value,monthly_frq:+e.target.value})}/></td><td style={{textAlign:'center',color:'var(--text2)',fontWeight:600,fontSize:11}}>{mfrq}<span style={{fontSize:9,color:'var(--text3)',marginLeft:4}}>avg/wk</span></td></tr>;})} </tbody></table></div>}
        {tab==='catalogue'&&<div>
          <table className="out-table"><thead><tr><th>Name</th><th>Seller</th><th>Type</th><th>CTR</th><th>CVR</th><th>VTR</th><th>Max Impr/mo</th><th>Active</th><th>Ovr</th><th/></tr></thead>
          <tbody>{catalogue.map(p=>{
            const typ=AD_TYPES.find(t=>t.key===p.type);
            const hasClientOvr=p._client_overrides&&Object.keys(p._client_overrides).length>0;
            const hasPlanOvr=p._has_plan_override;
            const rowBg=hasPlanOvr?'var(--gold-bg)':hasClientOvr?'#7030A008':'transparent';
            return<tr key={p.id} style={{background:rowBg}}>
              <td><input className="inn" value={p.label} style={{width:'100%',minWidth:120}} onChange={e=>updP(p.id,{label:e.target.value})}/></td>
              <td><input className="inn" value={p.seller||''} style={{width:80}} onChange={e=>updP(p.id,{seller:e.target.value})}/></td>
              <td><select className="sel" value={p.type} style={{width:90,fontSize:10}} onChange={e=>updP(p.id,{type:e.target.value})}>{AD_TYPES.map(t=><option key={t.key} value={t.key}>{t.label}</option>)}</select></td>
              <td><input className="inn" type="number" min={0} max={1} step={0.0001} value={(p.ctr||0).toFixed(4)} style={{width:70}} onChange={e=>updP(p.id,{ctr:+e.target.value})}/></td>
              <td><input className="inn" type="text" inputMode="decimal" value={p.cvr!=null?p.cvr:''} placeholder="0.01" style={{width:60}} title="CVR" onBlur={e=>{const v=parseFloat(e.target.value);updP(p.id,{cvr:isNaN(v)?null:v});}} onChange={e=>updP(p.id,{cvr:e.target.value})}/></td>
              <td><input className="inn" type="number" min={0} max={1} step={0.01} value={p.vtr||0} style={{width:60}} onChange={e=>updP(p.id,{vtr:+e.target.value})}/></td>
              <td><input className="inn" type="number" min={0} step={100000} value={p.max_impressions||''} placeholder="∞" style={{width:80}} onChange={e=>updP(p.id,{max_impressions:e.target.value?+e.target.value:null})}/></td>
              <td style={{textAlign:'center'}}><input type="checkbox" checked={p.active!==false} onChange={e=>updP(p.id,{active:e.target.checked})}/></td>
              <td style={{textAlign:'center',minWidth:36}}>
                {hasClientOvr&&<span title={'Client overrides: '+Object.keys(p._client_overrides).join(', ')} style={{fontSize:9,color:'#7030A0',fontWeight:700,cursor:'pointer'}} onClick={()=>{if(window.confirm('Reset client overrides for '+p.label+'?'))resetClientOverride(p);}}>🏢{Object.keys(p._client_overrides).length}</span>}
                {hasPlanOvr&&!hasClientOvr&&<span title="Plan-level override active" style={{fontSize:9,color:'var(--gold-dark)',fontWeight:700}}>📋</span>}
              </td>
              <td style={{textAlign:'center'}}><button style={{color:'var(--red)',border:'none',background:'transparent',cursor:'pointer',fontWeight:700,fontSize:13}} onClick={()=>{if(window.confirm('Delete?')){if(p._db_id){fetch(`${API}/platforms/${p._db_id}`,{method:'DELETE'}).catch(()=>{});}setCatalogue(prev=>prev.filter(c=>c.id!==p.id));}}}>✕</button></td>
            </tr>;
          })}</tbody></table>
          <div style={{marginTop:10,display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
            <button className="btn btn-ghost btn-sm" onClick={()=>{const newP={id:'custom_'+Date.now(),label:'New Platform',seller:'',type:'banner',base_cpm:0,viewability:0.70,audience_comp:0.65,ctr:0.0008,vtr:null,weekly_frq:5,monthly_frq:20,real_users_coef:2.5,reach_pool:'display',active:true,priority:{awareness:5,views:1,traffic:7,conversion:4}};const tempId=newP.id;setCatalogue(prev=>[...prev,newP]);fetch(`${API}/platforms/`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({name:newP.label,seller:newP.seller||'',buying_type:newP.type==='social'?'social':'programmatic',format:newP.label,format_group:newP.type==='video'?'video':newP.type==='sem'?'sem':newP.type==='nonstandard'?'non_standard_display':'standard_display',reach_group:newP.reach_pool==='yt'?'c_youtube':newP.reach_pool==='social'?'b_social':'a_others',reach_pool:newP.reach_pool||'display',cpm_eur:newP.base_cpm||null,cpc_eur:newP.cpc||null,viewability:newP.viewability||0.7,audience_comp:newP.audience_comp||0.65,real_users_coef:newP.real_users_coef||2.5,default_ctr:newP.ctr||0.0008,default_vtr:newP.vtr||null,default_weekly_frq:newP.weekly_frq||5,default_monthly_frq:newP.monthly_frq||20,active:true})}).then(r=>r.ok?r.json():null).then(d=>{if(d){const finalP={...newP,_db_id:d.id,id:'db_'+d.id};setCatalogue(prev=>prev.map(x=>x.id===tempId?finalP:x));}}).catch(()=>{});}}>+ Add platform</button>
            <button className="btn btn-ghost btn-sm" onClick={()=>{if(window.confirm('Reset to defaults?'))setCatalogue(DEFAULT_PLATFORMS.map(p=>({...p})));}}>↺ Reset defaults</button>
            <div style={{marginLeft:'auto',fontSize:9,color:'var(--text3)',display:'flex',gap:8}}>
              <span>📋 Plan override</span><span>🏢 Client override (click to reset)</span>
            </div>
          </div>
        </div>}
        {tab==='bulk'&&<BulkInputsTab catalogue={catalogue} setCatalogue={setCatalogue} updP={updP} audiences={audiences} setAudiences={setAudiences}/>}
      </div>
    </div>
  </>);
}

// ─── BULK INPUTS IMPORT/EXPORT ────────────────────────────────────────────────
function BulkInputsTab({catalogue,setCatalogue,updP,audiences,setAudiences}){
  const[status,setStatus]=useState('');
  const[errors,setErrors]=useState([]);
  const[importing,setImporting]=useState(false);
  // Expose audiences for downloadTemplate (runs outside React tree via async)
  React.useEffect(()=>{window._dpe_audiences=audiences;},[audiences]);

  async function downloadTemplate(){
    let XLSX;
    try{XLSX=await import('https://cdn.sheetjs.com/xlsx-0.20.3/package/xlsx.mjs');}catch(e){}
    if(!XLSX){
      // Fallback: use the same XLSX already loaded in the page (from exportToExcel)
      try{XLSX=window._XLSX;}catch(e){}
    }
    if(!XLSX){alert('Excel library not available. Try exporting a plan first, then retry.');return;}
    const wb=XLSX.utils.book_new();
    const months=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

    // Sheet 1: Pricing
    const pricingRows=[['Platform','Seller','Type','Base CPM (EUR)','CPC (EUR)','Base Discount %','Effective CPM']];
    catalogue.forEach(p=>{const eff=p.base_cpm?+(p.base_cpm*(1-(p.base_discount||0))).toFixed(2):'';pricingRows.push([p.label,p.seller||'',p.type,p.base_cpm||0,p.cpc||'',Math.round((p.base_discount||0)*100),eff]);});
    XLSX.utils.book_append_sheet(wb,XLSX.utils.aoa_to_sheet(pricingRows),'Pricing');

    // Sheet 2: Seasonal CPM
    const seasonRows=[['Platform','Seller',...months]];
    catalogue.forEach(p=>{seasonRows.push([p.label,p.seller||'',...(p.seasonal_cpm||Array(12).fill(1))]);});
    XLSX.utils.book_append_sheet(wb,XLSX.utils.aoa_to_sheet(seasonRows),'Seasonal CPM');

    // Sheet 3: Audience & Reach
    const audReachRows=[['Platform','Seller','Reach Pool','Viewability','Audience Comp','Real Users Coef']];
    catalogue.forEach(p=>{audReachRows.push([p.label,p.seller||'',p.reach_pool||'display',p.viewability||0.7,p.audience_comp||0.65,p.real_users_coef||2.0]);});
    XLSX.utils.book_append_sheet(wb,XLSX.utils.aoa_to_sheet(audReachRows),'Audience & Reach');

    // Sheet 4: Frequency
    const frqRows=[['Platform','Seller','Weekly FRQ','Monthly FRQ']];
    catalogue.filter(p=>p.type!=='sem').forEach(p=>{frqRows.push([p.label,p.seller||'',p.weekly_frq||3,p.monthly_frq||12]);});
    XLSX.utils.book_append_sheet(wb,XLSX.utils.aoa_to_sheet(frqRows),'Frequency');

    // Sheet 5: Catalogue
    const catRows=[['Platform','Seller','Type','CTR','CVR','VTR','Max Impressions/mo','Active']];
    catalogue.forEach(p=>{catRows.push([p.label,p.seller||'',p.type,p.ctr||0,p.cvr!=null?p.cvr:'',p.vtr||'',p.max_impressions||'',p.active!==false?'Yes':'No']);});
    XLSX.utils.book_append_sheet(wb,XLSX.utils.aoa_to_sheet(catRows),'Catalogue');

    // Sheet 6: Audience Composition (platComp per audience)
    // Columns: Audience | Universe | Platform1 | Platform2 | ...
    const platLabels=catalogue.map(p=>p.label);
    const audCompHeader=['Audience','Universe',...platLabels];
    const audCompRows=[audCompHeader];
    // Pass audiences via prop — use window as bridge since we're inside InputsDrawer
    const auds=window._dpe_audiences||[];
    auds.forEach(a=>{
      const row=[a.name,a.universe];
      catalogue.forEach(p=>{
        // platComp keys may be frontend ids (db_X) or legacy ids
        const v=a.platComp?.[p.id]??a.platComp?.[p._legacy_id]??'';
        row.push(v!==''?(+v*100).toFixed(1):'');
      });
      audCompRows.push(row);
    });
    XLSX.utils.book_append_sheet(wb,XLSX.utils.aoa_to_sheet(audCompRows),'Audience Composition');

    // Sheet 7: Instructions
    const instrRows=[
      ['PLATFORM INPUTS BULK TEMPLATE'],[''],
      ['Sheets: Pricing | Seasonal CPM | Audience & Reach | Frequency | Catalogue | Audience Composition'],[''],
      ['RULES:'],
      ['- Do NOT change Platform/Seller/Audience columns — used for matching'],
      ['- Leave cells blank to keep existing values (blank = no change)'],
      ['- Pricing: Base Discount % as integer (e.g. 15 for 15%)'],
      ['- Seasonal CPM: multipliers (1.0=base, 1.2=+20%, 0.8=-20%)'],
      ['- Reach Pool: display | video | social | yt'],
      ['- Active: Yes or No'],
      ['- Audience Composition: % as decimal (e.g. 72.5 for 72.5%)'],
      ['- Import only updates matching rows; unmatched rows are skipped with a warning'],
    ];
    XLSX.utils.book_append_sheet(wb,XLSX.utils.aoa_to_sheet(instrRows),'Instructions');

    XLSX.writeFile(wb,'PlatformInputs_'+new Date().toISOString().slice(0,10)+'.xlsx');
    setStatus('✓ Template downloaded');
  }

  async function handleImport(e){
    const file=e.target.files[0];if(!file)return;
    setImporting(true);setStatus('Parsing…');setErrors([]);
    const XLSX=await import('https://cdn.sheetjs.com/xlsx-0.20.3/package/xlsx.mjs').catch(()=>null);
    if(!XLSX){setStatus('✗ SheetJS not available');setImporting(false);return;}
    const buf=await file.arrayBuffer();
    const wb=XLSX.read(buf,{type:'array'});
    const errs=[];let updated=0;

    // Helper: find platform in catalogue by name (case-insensitive partial match)
    function findPlat(name){if(!name)return null;const n=String(name).toLowerCase().trim();return catalogue.find(p=>p.label.toLowerCase().trim()===n||p.label.toLowerCase().includes(n)||n.includes(p.label.toLowerCase().trim()));}

    // Parse each sheet
    const months=['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
    const patches={}; // {platformId: patch}
    const addPatch=(p,patch)=>{if(!p)return;if(!patches[p.id])patches[p.id]={};Object.assign(patches[p.id],patch);};

    wb.SheetNames.forEach(sname=>{
      const sheet=wb.Sheets[sname];
      if(!sheet)return;
      const rows=XLSX.utils.sheet_to_json(sheet,{header:1,defval:''});
      if(rows.length<2)return;
      const hdr=rows[0].map(h=>String(h).toLowerCase().trim());
      const sn=sname.toLowerCase();

      rows.slice(1).forEach((row,ri)=>{
        const get=i=>row[i]!=null&&row[i]!==''?row[i]:null;
        const plat=findPlat(get(0));
        if(!plat){if(get(0))errs.push(`Row ${ri+2} in "${sname}": platform "${get(0)}" not matched`);return;}

        if(sn.includes('pricing')){
          const cpm=get(3),cpc=get(4),disc=get(5);
          if(cpm!==null)addPatch(plat,{base_cpm:+cpm});
          if(cpc!==null)addPatch(plat,{cpc:+cpc});
          if(disc!==null)addPatch(plat,{base_discount:+disc/100});
        } else if(sn.includes('seasonal')){
          const sc=row.slice(2,14).map(v=>v!==''&&v!=null?+v:null);
          const existing=plat.seasonal_cpm||Array(12).fill(1);
          const merged=existing.map((v,i)=>sc[i]!=null?sc[i]:v);
          addPatch(plat,{seasonal_cpm:merged});
        } else if(sn.includes('audience')){
          const pool=get(2),view=get(3),ac=get(4),ruc=get(5);
          if(pool!==null)addPatch(plat,{reach_pool:String(pool).toLowerCase().trim()});
          if(view!==null)addPatch(plat,{viewability:+view});
          if(ac!==null)addPatch(plat,{audience_comp:+ac});
          if(ruc!==null)addPatch(plat,{real_users_coef:+ruc});
        } else if(sn.includes('frequency')){
          const wfrq=get(2),mfrq=get(3);
          if(wfrq!==null)addPatch(plat,{weekly_frq:+wfrq});
          if(mfrq!==null)addPatch(plat,{monthly_frq:+mfrq});
        } else if(sn.includes('catalogue')){
          const ctr=get(3),cvr=get(4),vtr=get(5),maxI=get(6),active=get(7);
          if(ctr!==null)addPatch(plat,{ctr:+ctr});
          if(cvr!==null)addPatch(plat,{cvr:+cvr});
          if(vtr!==null)addPatch(plat,{vtr:+vtr});
          if(maxI!==null)addPatch(plat,{max_impressions:+maxI||null});
          if(active!==null)addPatch(plat,{active:String(active).toLowerCase().trim()!=='no'});
        } else if(sn.includes('audience composition')){
          // Row format: Audience | Universe | Plat1% | Plat2% | ...
          // hdr[0]=audience, hdr[1]=universe, hdr[2..]=platform labels
          const audName=String(get(0)||'').trim();
          if(!audName)return;
          const aud=(audiences||[]).find(a=>a.name.toLowerCase().trim()===audName.toLowerCase().trim());
          if(!aud){errs.push(`Audience "${audName}" not found — skipped`);return;}
          const newPlatComp={...(aud.platComp||{})};
          hdr.slice(2).forEach((platLabel,ci)=>{
            const val=get(ci+2);
            if(val===null||val==='')return;
            const p=catalogue.find(x=>x.label.toLowerCase().trim()===String(platLabel).toLowerCase().trim());
            if(!p)return;
            newPlatComp[p.id]=+val/100;
          });
          // Update audience in state and DB if has _db_id
          setAudiences(prev=>prev.map(a=>a.id===aud.id?{...a,platComp:newPlatComp}:a));
          if(aud._db_id){
            const platCompForDb={};
            Object.entries(newPlatComp).forEach(([k,v])=>{const dbId=k.startsWith('db_')?k.replace('db_',''):k;platCompForDb[dbId]=v;});
            fetch(`${API}/audiences/${aud._db_id}`,{method:'PATCH',headers:{'Content-Type':'application/json'},body:JSON.stringify({plat_comp:platCompForDb})}).catch(()=>{});
          }
          updated++;
        }
      });
    });

    // Apply all patches
    Object.entries(patches).forEach(([id,patch])=>{updP(id,patch);updated++;});
    setErrors(errs);
    setStatus(`✓ Updated ${updated} platforms${errs.length?' with '+errs.length+' warnings':''}`);
    setImporting(false);
    e.target.value='';
  }

  return(<div style={{padding:'8px 0'}}>
    <div style={{marginBottom:16,padding:'12px 16px',background:'var(--surface3)',borderRadius:8,border:'1px solid var(--border)'}}>
      <div style={{fontWeight:700,fontSize:12,marginBottom:6}}>⬇ Download Template</div>
      <p style={{fontSize:11,color:'var(--text3)',marginBottom:10}}>Download an Excel file with all platforms and all parameter sheets (Pricing, Seasonal CPM, Audience & Reach, Frequency, Catalogue). Fill in your changes and import back.</p>
      <button className="btn btn-primary btn-sm" onClick={downloadTemplate}>⬇ Download Excel Template</button>
    </div>
    <div style={{marginBottom:16,padding:'12px 16px',background:'var(--surface3)',borderRadius:8,border:'1px solid var(--border)'}}>
      <div style={{fontWeight:700,fontSize:12,marginBottom:6}}>⬆ Import Updated Template</div>
      <p style={{fontSize:11,color:'var(--text3)',marginBottom:10}}>Upload the filled-in Excel template. All sheets are processed — blank cells are skipped (keep existing values). Platform matching is by name.</p>
      <label className="btn btn-ghost btn-sm" style={{cursor:'pointer'}}>{importing?'⏳ Importing…':'⬆ Choose Excel File (.xlsx)'}<input type="file" accept=".xlsx,.xls" style={{display:'none'}} onChange={handleImport} disabled={importing}/></label>
    </div>
    {status&&<div style={{padding:'8px 12px',borderRadius:6,background:status.startsWith('✓')?'#e8f5e9':'#fff3e0',border:'1px solid '+(status.startsWith('✓')?'#c8e6c9':'#ffe0b2'),fontSize:11,marginBottom:8}}>{status}</div>}
    {errors.length>0&&<div style={{padding:'8px 12px',borderRadius:6,background:'#fff3e0',border:'1px solid #ffe0b2',fontSize:10,maxHeight:120,overflowY:'auto'}}>
      <div style={{fontWeight:700,marginBottom:4}}>Warnings:</div>
      {errors.map((e,i)=><div key={i} style={{color:'#E07000'}}>{e}</div>)}
    </div>}
  </div>);
}

// ─── ACTUALS / PACING TAB ─────────────────────────────────────────────────────
// Per-platform weekly actuals + CSV/paste upload + benchmark recalculation

const ACTUAL_FIELDS=[
  {key:'impr',   label:'Impressions',    color:'#4472C4'},
  {key:'vimpr',  label:'View. Impr.',    color:'#C19A6B'},
  {key:'views',  label:'Views',          color:'#7030A0'},
  {key:'clicks', label:'Clicks',         color:'#00B0F0'},
  {key:'budget', label:'Budget Spent €', color:'#2E8B57'},
];

function computePlatformBenchmarks(platActuals,planPlatResult,planWeekly,nWeeks=12){
  if(!planPlatResult||!planWeekly)return null;
  const wFT=planWeekly.reduce((s,w)=>s+w.frac,0)||1;
  const pairs=[];
  planWeekly.forEach((pw,wi)=>{
    const a=(platActuals||{})[wi];if(!a)return;
    const planImpr=(planPlatResult.impr||0)*(pw.frac/wFT);
    const planBud=(planPlatResult.bud||0)*(pw.frac/wFT);
    if(!planImpr)return;
    pairs.push({viewability:a.vimpr&&a.impr?a.vimpr/a.impr:null,vtr:a.views&&a.impr?a.views/a.impr:null,ctr:a.clicks&&a.impr?a.clicks/a.impr:null,impIndex:planImpr>0?a.impr/planImpr:null,budIndex:planBud>0&&a.budget?a.budget/planBud:null});
  });
  const last=pairs.slice(-nWeeks).filter(p=>p.impIndex!=null);
  if(!last.length)return null;
  const avg=fn=>{const v=last.map(fn).filter(x=>x!=null);return v.length?v.reduce((s,x)=>s+x,0)/v.length:null;};
  return{viewability:avg(p=>p.viewability),vtr:avg(p=>p.vtr),ctr:avg(p=>p.ctr),impIndex:avg(p=>p.impIndex),budIndex:avg(p=>p.budIndex),n:last.length};
}

function UploadModal({flight,result,onClose,onImport}){
  const[tab,setTab]=useState('paste');
  const[text,setText]=useState('');
  const[parsed,setParsed]=useState(null);
  const[error,setError]=useState('');
  const fl=flight;
  const r=result;

  function matchPlatform(name,platforms){
    const n=name.toLowerCase().trim();
    const direct=platforms.find(p=>p.p.label.toLowerCase()===n);
    if(direct)return direct;
    const ALIASES={dv360_yt:['youtube','dv360 youtube','dv 360 youtube'],eskimi_video:['eskimi in-stream','eskimi in stream','eskimi video'],teads_video:['teads out-stream','teads out stream','teads'],simpals_skin:['simpals fluid skin','fluid skin'],meteofor_brand:['meteofor branding','meteofor.md branding','meteofor'],dv360_display:['dv360 display','dv 360 display'],eskimi_rm:['eskimi rich media','eskimi rich'],simpals_rm:['simpals rich media','simpals rich'],meta_multi:['meta','facebook','fb+ig','meta multiformat'],gads_search:['google search','gads search','google ads search']};
    for(const[pid,aliases]of Object.entries(ALIASES)){if(aliases.some(a=>n.includes(a))){const found=platforms.find(p=>p.p.id===pid||p.p.id.includes(pid.split('_')[0]));if(found)return found;}}
    return platforms.find(p=>n.includes(p.p.label.toLowerCase().slice(0,5))||p.p.label.toLowerCase().includes(n.slice(0,5)));
  }

  function parseData(raw){
    if(!raw.trim()){return;}
    const lines=raw.trim().split('\n').map(l=>l.split(/[\t,;]/).map(c=>c.trim().replace(/^["']|["']$/g,'')));
    if(lines.length<2){setError('Need at least a header row and one data row.');return;}
    const hdr=lines[0].map(h=>h.toLowerCase());
    const ci={};
    const find=kws=>hdr.findIndex(h=>kws.some(k=>h.includes(k)));
    ci.platform=find(['platform','format','ad format','placement','source','channel','ad name']);
    ci.week=find(['week','date','period','start date','start']);
    ci.impr=find(['impression','impr','served impressions','total impressions']);
    ci.vimpr=find(['viewable imp','viewable_imp','view. imp','viewable impressions']);
    ci.views=find(['completed view','video view','views','vtr']);
    ci.clicks=find(['click']);
    ci.budget=find(['cost','spend','budget','eur','amount','revenue']);
    if(ci.impr===-1){setError('Cannot find Impressions column. Check headers.');return;}
    const platforms=r?.platforms||[];
    const weeklyMap={};const unmapped=new Set();
    lines.slice(1).forEach(row=>{
      if(!row[ci.impr]||row[ci.impr]==='')return;
      const platName=ci.platform>=0?row[ci.platform]:'';
      const pr=matchPlatform(platName,platforms);
      if(!pr){if(platName.trim())unmapped.add(platName);return;}
      const pid=pr.p.id;
      let weekIdx=0;
      if(ci.week>=0&&row[ci.week]){
        const rw=row[ci.week].toLowerCase();
        const wm=rw.match(/w(\d+)/i)||rw.match(/week\s*(\d+)/i);
        if(wm){const wn=parseInt(wm[1]);weekIdx=(r.weekly||[]).findIndex(w=>w.week===wn);}
        else{const d=new Date(row[ci.week]);if(!isNaN(d)){const wn=dateToWeek(d.toISOString().slice(0,10));weekIdx=(r.weekly||[]).findIndex(w=>w.week===wn);}}
        if(weekIdx<0)weekIdx=0;
      }
      if(!weeklyMap[pid])weeklyMap[pid]={};
      if(!weeklyMap[pid][weekIdx])weeklyMap[pid][weekIdx]={};
      const e=weeklyMap[pid][weekIdx];
      const n=s=>s&&s!==''?parseFloat(String(s).replace(/[^\d.-]/g,''))||0:0;
      if(ci.impr>=0)e.impr=(e.impr||0)+n(row[ci.impr]);
      if(ci.vimpr>=0)e.vimpr=(e.vimpr||0)+n(row[ci.vimpr]);
      if(ci.views>=0)e.views=(e.views||0)+n(row[ci.views]);
      if(ci.clicks>=0)e.clicks=(e.clicks||0)+n(row[ci.clicks]);
      if(ci.budget>=0)e.budget=(e.budget||0)+n(row[ci.budget]);
    });
    const rowCount=Object.values(weeklyMap).reduce((s,p)=>s+Object.keys(p).length,0);
    if(!rowCount){setError('No rows could be matched to platforms. Check platform names or use the template.');return;}
    setParsed({weeklyMap,unmapped:[...unmapped],rowCount,colsFound:Object.entries(ci).filter(([k,v])=>v>=0).map(([k])=>k)});
    setError('');
  }

  function handleFile(e){
    const f=e.target.files[0];if(!f)return;
    const reader=new FileReader();
    reader.onload=ev=>{const t=ev.target.result;setText(t);parseData(t);};
    reader.readAsText(f);
  }

  function downloadTemplate(){
    const platforms=r?.platforms||[];const weeks=r?.weekly||[];
    const rows=[['Platform','Week','Week Start Date','Impressions','Viewable Impressions','Views','Clicks','Budget Spent (EUR)']];
    platforms.forEach(pr=>{weeks.forEach(w=>{rows.push([pr.p.label,'W'+w.week,weekStartDate(w.week),'','','','','']);});});
    const csv=rows.map(row=>row.join('\t')).join('\n');
    const blob=new Blob([csv],{type:'text/tab-separated-values'});
    const url=URL.createObjectURL(blob);
    const a=document.createElement('a');a.href=url;a.download='actuals_template_'+flight.label.replace(/\s+/g,'_')+'.tsv';a.click();
  }

  return(<div className="modal-overlay" onClick={ev=>{if(ev.target===ev.currentTarget)onClose();}}>
    <div className="modal-box" style={{maxWidth:720,height:'82vh',display:'flex',flexDirection:'column'}}>
      <div className="modal-hdr"><span style={{fontWeight:700,fontSize:13}}>⬆ Upload Actuals — {fl.label}</span><button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button></div>
      <div className="inp-drawer-tabs">{[['paste','Paste / Upload'],['template','Download Template'],['instructions','How It Works']].map(([t,l])=><button key={t} className={'tab-btn'+(tab===t?' on':'')} onClick={()=>setTab(t)}>{l}</button>)}</div>
      <div style={{flex:1,overflowY:'auto',padding:'16px 20px'}}>
        {tab==='paste'&&<>
          <p style={{fontSize:11,color:'var(--text3)',marginBottom:12}}>Paste data directly from Excel / Google Sheets, or upload a CSV/TSV exported from DV360, Meta Ads Manager, Google Ads, or any ad server. Columns are auto-detected; platforms are matched by name.</p>
          <div style={{display:'flex',gap:8,marginBottom:12,alignItems:'center'}}>
            <label className="btn btn-ghost btn-sm" style={{cursor:'pointer'}}>
              📂 Upload file (CSV / TSV)
              <input type="file" accept=".csv,.tsv,.txt" style={{display:'none'}} onChange={handleFile}/>
            </label>
            <span style={{fontSize:10,color:'var(--text3)'}}>or paste below ↓</span>
          </div>
          <textarea value={text} onChange={e=>{setText(e.target.value);if(e.target.value.length>30)parseData(e.target.value);}}
            placeholder={"Paste from Excel or ad server report...\n\nPlatform\tWeek\tImpressions\tViewable Impressions\tViews\tClicks\tCost\nDV 360 YouTube\tW1\t545000\t462250\t381500\t820\t600\nEskimi In-Stream\tW1\t163000\t122250\t114100\t195\t500"}
            style={{width:'100%',height:160,padding:'10px 12px',background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:6,fontSize:11,fontFamily:'monospace',resize:'vertical',outline:'none',boxSizing:'border-box'}}
          />
          {error&&<div style={{marginTop:8,padding:'8px 12px',background:'#FFE8E8',border:'1px solid #FFB0B0',borderRadius:5,fontSize:11,color:'var(--red)'}}>{error}</div>}
          {parsed&&<div style={{marginTop:12,padding:'12px 14px',background:'var(--gold-bg)',border:'1px solid var(--gold-border)',borderRadius:6}}>
            <div style={{fontWeight:700,fontSize:12,marginBottom:6,color:'var(--gold-dark)'}}>✓ {parsed.rowCount} platform-week rows parsed</div>
            <div style={{fontSize:10,color:'var(--text3)',marginBottom:4}}>Columns: {parsed.colsFound.join(' · ')}</div>
            {parsed.unmapped.length>0&&<div style={{fontSize:10,color:'var(--red)',marginBottom:8}}>⚠ Unmatched platforms (skipped): {parsed.unmapped.join(', ')}</div>}
            <div style={{overflowX:'auto',maxHeight:180}}>
              <table style={{borderCollapse:'collapse',fontSize:9,width:'100%'}}>
                <thead><tr style={{background:'var(--surface3)'}}><th style={TH}>Platform</th><th style={TH}>Week</th>{ACTUAL_FIELDS.map(f=><th key={f.key} style={TH}>{f.label}</th>)}</tr></thead>
                <tbody>{Object.entries(parsed.weeklyMap).flatMap(([pid,wkMap])=>{
                  const pr=(r?.platforms||[]).find(p=>p.p.id===pid);if(!pr)return[];
                  return Object.entries(wkMap).slice(0,6).map(([wi,vals])=>(
                    <tr key={pid+wi}><td style={{...TD,fontSize:9}}>{pr.p.label}</td><td style={{...TDc,fontSize:9}}>W{(r?.weekly||[])[+wi]?.week||+wi+1}</td>{ACTUAL_FIELDS.map(f=><td key={f.key} style={{...TDr,fontSize:9}}>{vals[f.key]?fn(Math.round(vals[f.key])):'—'}</td>)}</tr>
                  ));
                })}</tbody>
              </table>
            </div>
          </div>}
        </>}
        {tab==='template'&&<>
          <p style={{fontSize:11,color:'var(--text3)',marginBottom:14}}>Download a pre-filled template with all platforms and weeks for this flight. Fill from your ad server reports and upload it back.</p>
          <button className="btn btn-primary btn-sm" onClick={downloadTemplate}>⬇ Download Template (.TSV / Excel)</button>
          <div style={{marginTop:18,padding:'12px 14px',background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:6}}>
            <div style={{fontWeight:700,fontSize:11,marginBottom:8}}>Template columns:</div>
            <table style={{borderCollapse:'collapse',fontSize:10,width:'100%'}}>
              <thead><tr><th style={TH}>Column</th><th style={TH}>Source</th><th style={TH}>Notes</th></tr></thead>
              <tbody>{[['Platform','Ad server report','Must match platform name or known alias'],['Week','Flight week number','W1, W2… or date YYYY-MM-DD'],['Impressions','Ad server','Total served impressions'],['Viewable Impressions','Ad server','MRC viewable impressions'],['Views','Ad server','Completed video views (video only)'],['Clicks','Ad server','Total clicks'],['Budget Spent (EUR)','Ad server','Net media cost in EUR']].map(([c,s,n])=><tr key={c}><td style={{...TD,fontWeight:600}}>{c}</td><td style={{...TD,color:'var(--text3)'}}>{s}</td><td style={{...TD,color:'var(--text3)',fontSize:9}}>{n}</td></tr>)}</tbody>
            </table>
          </div>
        </>}
        {tab==='instructions'&&<div style={{fontSize:11,lineHeight:1.8,color:'var(--text2)'}}>
          <p><strong>Supported sources:</strong> DV360, Meta Ads Manager, Google Ads, Eskimi, Teads, Simpals — any platform that exports a CSV/TSV with impressions and spend per week.</p>
          <p><strong>DV360:</strong> Reports → Standard → Insertion Order + Week + Impressions + Viewable Impressions + Clicks + Revenue → Download CSV</p>
          <p><strong>Meta Ads Manager:</strong> Campaigns → Export → Columns: Impressions, Clicks, Amount Spent → Breakdown: Week</p>
          <p><strong>Column detection:</strong> Headers are matched automatically. Supported keywords: "impression", "viewable", "click", "cost", "spend", "week", "date", "platform".</p>
          <p><strong>Platform matching:</strong> Matched by name (partial). If not matched, appears in the warning and is skipped. Use the template for guaranteed matching.</p>
          <p><strong>Multiple uploads:</strong> Each import merges with existing data — it doesn't overwrite previously entered weeks.</p>
        </div>}
      </div>
      <div className="modal-footer">
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <div style={{flex:1}}/>
        {parsed&&parsed.rowCount>0&&<button className="btn btn-primary btn-sm" onClick={()=>onImport(parsed.weeklyMap)}>✓ Import {parsed.rowCount} platform-week rows</button>}
      </div>
    </div>
  </div>);
}

function ActualsTab({flights,allResults,actualsData,setActualsData,catalogue}){
  const[selId,setSelId]=useState(flights[0]?.id||null);
  const[showUpload,setShowUpload]=useState(false);
  const[benchmarkWeeks,setBenchmarkWeeks]=useState(12);
  const[selMetric,setSelMetric]=useState('impr');
  const[viewMode,setViewMode]=useState('table');

  const fl=flights.find(f=>f.id===selId)||flights[0];
  const r=fl&&allResults[fl.id];
  const flActuals=actualsData[selId]||{};
  const wFT=r?(r.weekly||[]).reduce((s,w)=>s+w.frac,0)||1:1;
  const today=new Date();

  function handleImport(weeklyMap){
    setActualsData(prev=>({...prev,[selId]:{...(prev[selId]||{}),...Object.fromEntries(Object.entries(weeklyMap).map(([pid,wkMap])=>[pid,{...((prev[selId]||{})[pid]||{}),...wkMap}]))}}));
    setShowUpload(false);
  }

  const platformBenchmarks=useMemo(()=>{
    if(!r)return{};
    const out={};
    (r.platforms||[]).forEach(pr=>{out[pr.p.id]=computePlatformBenchmarks(flActuals[pr.p.id],pr,r.weekly,benchmarkWeeks);});
    return out;
  },[flActuals,r,benchmarkWeeks]);

  const platTotals=useMemo(()=>{
    if(!r)return{};
    const out={};
    (r.platforms||[]).forEach(pr=>{
      const pid=pr.p.id;const acts=flActuals[pid]||{};
      const totA={impr:0,vimpr:0,views:0,clicks:0,budget:0};
      Object.values(acts).forEach(w=>{ACTUAL_FIELDS.forEach(f=>{totA[f.key]+=(w[f.key]||0);});});
      out[pid]={act:totA,plan:{impr:pr.impr||0,vimpr:pr.vimpr||0,views:pr.views||0,clicks:pr.clicks||0,budget:pr.bud||0},weeksWithData:Object.keys(acts).filter(i=>acts[i]&&acts[i].impr>0).length};
    });
    return out;
  },[flActuals,r]);

  if(!fl||!r)return<div style={{padding:24,color:'var(--text3)'}}>No flights.</div>;

  const metric=ACTUAL_FIELDS.find(f=>f.key===selMetric)||ACTUAL_FIELDS[0];
  const weeksTotal=(r.weekly||[]).length;
  const weeksPast=(r.weekly||[]).filter(w=>new Date(weekStartDate(w.week))<today).length;
  const grandAct={impr:0,vimpr:0,views:0,clicks:0,budget:0};
  const grandPlan={impr:r.tot.impressions,vimpr:r.tot.vimpressions,views:r.tot.views,clicks:r.tot.clicks,budget:fl.budget||0};
  Object.values(platTotals).forEach(pt=>ACTUAL_FIELDS.forEach(f=>{grandAct[f.key]+=(pt.act[f.key]||0);}));

  return(<div style={{padding:'16px 24px'}}>
    <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:14,flexWrap:'wrap'}}>
      <select className="sel" style={{minWidth:260}} value={selId||''} onChange={e=>setSelId(e.target.value)}>
        {flights.map(f=><option key={f.id} value={f.id}>{f.brand} — {f.label} ({fdate(f.startDate)}–{fdate(f.endDate)})</option>)}
      </select>
      <div style={{fontSize:10,color:'var(--text3)'}}>{weeksPast} of {weeksTotal} weeks elapsed</div>
      <div style={{flex:1}}/>
      {[['table','Weekly Table'],['summary','By Platform']].map(([v,l])=>(
        <button key={v} className={'btn btn-sm'+(viewMode===v?' btn-primary':' btn-ghost')} onClick={()=>setViewMode(v)}>{l}</button>
      ))}
      <button className="btn btn-ghost btn-sm" onClick={()=>setShowUpload(true)}>⬆ Upload Actuals</button>
    </div>

    {/* Grand total progress */}
    <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:16}}>
      {ACTUAL_FIELDS.map(f=>{
        const pv=grandPlan[f.key]||0,av=grandAct[f.key]||0;
        const pct=pv>0?av/pv:0;
        const expPct=weeksTotal>0?weeksPast/weeksTotal:0;
        const isLow=weeksPast>0&&pct<expPct*0.85,isHigh=weeksPast>0&&pct>expPct*1.15;
        return(<div key={f.key} onClick={()=>setSelMetric(f.key)} style={{flex:1,minWidth:95,background:selMetric===f.key?'var(--gold-bg)':'#fff',border:'1px solid '+(selMetric===f.key?'var(--gold-border)':'var(--border)'),borderRadius:7,padding:'8px 10px',cursor:'pointer'}}>
          <div style={{fontSize:9,color:f.color,fontWeight:700,textTransform:'uppercase',letterSpacing:'.05em',marginBottom:3}}>{f.label}</div>
          <div style={{fontSize:12,fontWeight:800}}>{f.key==='budget'?fe(av):fn(Math.round(av))}</div>
          <div style={{fontSize:9,color:'var(--text3)',marginBottom:4}}>/ {f.key==='budget'?fe(pv):fn(Math.round(pv))}</div>
          <div style={{height:4,background:'var(--surface3)',borderRadius:3,overflow:'hidden'}}>
            <div style={{height:'100%',borderRadius:3,background:isLow?'var(--red)':isHigh?'var(--amber)':f.color,width:Math.min(100,pct*100)+'%'}}/>
          </div>
          <div style={{fontSize:9,fontWeight:700,marginTop:2,color:isLow?'var(--red)':isHigh?'var(--amber)':'var(--text3)'}}>{(pct*100).toFixed(1)}%{isLow?' ⚠ Under':isHigh?' ↑ Over':''}</div>
        </div>);
      })}
    </div>

    {viewMode==='summary'&&<>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8,flexWrap:'wrap'}}>
        <span style={{fontSize:11,fontWeight:700}}>Platform totals vs plan</span>
        <label style={{fontSize:10,color:'var(--text3)',marginLeft:8,display:'flex',alignItems:'center',gap:4}}>
          Benchmarks over <select className="sel" value={benchmarkWeeks} style={{width:52,fontSize:10}} onChange={e=>setBenchmarkWeeks(+e.target.value)}>{[4,8,12].map(n=><option key={n}>{n}</option>)}</select> weeks
        </label>
      </div>
      <div style={{overflowX:'auto'}}>
        <table style={{borderCollapse:'collapse',fontSize:10,width:'100%'}}>
          <thead>
            <tr style={{background:'#1A3A5C'}}>
              <th style={{...TH,color:'#fff',textAlign:'left',minWidth:160,position:'sticky',left:0,background:'#1A3A5C',zIndex:2}}>Platform</th>
              <th style={{...TH,color:'#fff',width:52}}>Weeks</th>
              {ACTUAL_FIELDS.map(f=><th key={f.key} style={{...TH,color:'#fff'}} colSpan={2}>{f.label}</th>)}
              <th style={{...TH,color:'#C8A86B',width:70}}>View. idx</th>
              <th style={{...TH,color:'#C8A86B',width:60}}>Imp. idx</th>
            </tr>
            <tr style={{background:'#2A4A6C'}}>
              <th style={{...TH,position:'sticky',left:0,background:'#2A4A6C',zIndex:2,color:'#C8A86B'}}/>
              <th style={{...TH,color:'#C8A86B',fontSize:8}}>w/data</th>
              {ACTUAL_FIELDS.map(f=>[<th key={f.key+'p'} style={{...TH,color:'#C8A86B',fontSize:8}}>Plan</th>,<th key={f.key+'a'} style={{...TH,color:'#fff',fontSize:8}}>Actual</th>])}
              <th style={{...TH,color:'#C8A86B',fontSize:8}}>vs plan</th>
              <th style={{...TH,color:'#C8A86B',fontSize:8}}>vs plan</th>
            </tr>
          </thead>
          <tbody>
            {(r.platforms||[]).map(pr=>{
              const pt=platTotals[pr.p.id];const bm=platformBenchmarks[pr.p.id];if(!pt)return null;
              const typ=AD_TYPES.find(t=>t.key===pr.p.type);
              return(<tr key={pr.p.id} style={{background:'#fff',borderBottom:'1px solid var(--border)'}}>
                <td style={{...TD,fontWeight:500,position:'sticky',left:0,background:'#fff',zIndex:1}}>
                  <div style={{display:'flex',alignItems:'center',gap:5}}><span style={{width:7,height:7,borderRadius:'50%',background:typ?.color,display:'inline-block',flexShrink:0}}/><span>{pr.p.label}</span></div>
                  <div style={{fontSize:8,color:'var(--text3)'}}>{pr.p.seller}</div>
                </td>
                <td style={{...TDc,color:pt.weeksWithData>0?'var(--green)':'var(--red)',fontWeight:700}}>{pt.weeksWithData}/{weeksTotal}</td>
                {ACTUAL_FIELDS.map(f=>{const pv=pt.plan[f.key]||0,av=pt.act[f.key]||0;const delta=pv>0&&av>0?(av-pv)/pv:null;const isOff=delta!=null&&Math.abs(delta)>0.1;return[<td key={f.key+'p'} style={{...TDr,color:'var(--text3)',fontSize:9}}>{f.key==='budget'?fe(pv):fn(Math.round(pv))}</td>,<td key={f.key+'a'} style={{...TDr,fontWeight:av>0?700:400,color:isOff?(delta>0?'var(--green)':'var(--red)'):'var(--text)'}}>{av>0?(f.key==='budget'?fe(av):fn(Math.round(av))):'—'}{delta!=null&&<span style={{fontSize:8,marginLeft:2,opacity:.8}}>{delta>=0?'+':''}{(delta*100).toFixed(0)}%</span>}</td>];})}
                <td style={{...TDc,fontWeight:700,color:bm?.viewability!=null?(bm.viewability>=(pr.p.viewability||0.7)?'var(--green)':'var(--red)'):'var(--text3)',fontSize:10}}>{bm?.viewability!=null?(bm.viewability*100).toFixed(1)+'%':'—'}</td>
                <td style={{...TDc,fontWeight:700,color:bm?.impIndex!=null?(bm.impIndex>=0.9?'var(--green)':'var(--red)'):'var(--text3)',fontSize:10}}>{bm?.impIndex!=null?bm.impIndex.toFixed(2)+'×':'—'}</td>
              </tr>);
            })}
          </tbody>
          <tfoot><tr style={{background:'#1A3A5C'}}>
            <td style={{...TD,color:'#C8A86B',fontWeight:700,position:'sticky',left:0,background:'#1A3A5C',zIndex:1}}>TOTAL</td>
            <td style={TDc}/>
            {ACTUAL_FIELDS.map(f=>{const pv=grandPlan[f.key]||0,av=grandAct[f.key]||0;const delta=pv>0&&av>0?(av-pv)/pv:null;return[<td key={f.key+'p'} style={{...TDr,color:'#C8A86B',fontSize:10}}>{f.key==='budget'?fe(pv):fn(Math.round(pv))}</td>,<td key={f.key+'a'} style={{...TDr,color:delta==null?'#fff':Math.abs(delta)<0.05?'#90EE90':delta<0?'#FFB0A0':'#90EE90',fontWeight:700,fontSize:10}}>{av>0?(f.key==='budget'?fe(av):fn(Math.round(av))):'—'}{delta!=null&&<span style={{fontSize:8,marginLeft:2}}>{delta>=0?'+':''}{(delta*100).toFixed(0)}%</span>}</td>];})}
            <td colSpan={2}/>
          </tr></tfoot>
        </table>
      </div>
    </>}

    {viewMode==='table'&&<>
      <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:8,flexWrap:'wrap'}}>
        <span style={{fontSize:11,fontWeight:700,color:metric.color}}>{metric.label} — weekly by platform · plan vs actual</span>
        <div style={{display:'flex',gap:3,marginLeft:'auto'}}>
          {ACTUAL_FIELDS.map(f=><button key={f.key} onClick={()=>setSelMetric(f.key)} style={{fontSize:9,padding:'2px 7px',borderRadius:4,border:'1px solid '+(selMetric===f.key?f.color:'var(--border)'),background:selMetric===f.key?f.color:'transparent',color:selMetric===f.key?'#fff':'var(--text3)',cursor:'pointer'}}>{f.label}</button>)}
        </div>
      </div>
      <div style={{overflowX:'auto'}}>
        <table style={{borderCollapse:'collapse',fontSize:10,minWidth:500}}>
          <thead><tr style={{background:metric.color+'22',borderBottom:'2px solid '+metric.color}}>
            <th style={{...TH,background:metric.color+'22',textAlign:'left',minWidth:150,position:'sticky',left:0,zIndex:2}}>Platform</th>
            <th style={{...TH,background:metric.color+'22',width:60}}>Plan</th>
            {(r.weekly||[]).map((wk,wi)=>{const isPast=new Date(weekStartDate(wk.week))<today;return(<th key={wi} style={{...TH,background:isPast?metric.color+'33':metric.color+'11',width:66,fontSize:8}}>W{wk.week}<div style={{fontWeight:400,fontSize:7,color:'var(--text3)'}}>{fdate(weekStartDate(wk.week))}</div></th>);})}
            <th style={{...TH,background:metric.color,color:'#fff',width:68}}>Actual</th>
            <th style={{...TH,background:metric.color+'44',width:52,fontSize:8}}>Δ</th>
          </tr></thead>
          <tbody>
            {(r.platforms||[]).map(pr=>{
              const pid=pr.p.id;const typ=AD_TYPES.find(t=>t.key===pr.p.type);
              const planTot=selMetric==='budget'?pr.bud:selMetric==='impr'?pr.impr:selMetric==='vimpr'?pr.vimpr:selMetric==='views'?pr.views:pr.clicks;
              const platActs=flActuals[pid]||{};
              const actTot=Object.values(platActs).reduce((s,w)=>s+(w[selMetric]||0),0);
              const delta=planTot>0&&actTot>0?(actTot-planTot)/planTot:null;
              const isOff=delta!=null&&Math.abs(delta)>0.1;
              return(<tr key={pid} style={{background:'#fff',borderBottom:'1px solid var(--border)'}}>
                <td style={{...TD,fontWeight:500,position:'sticky',left:0,background:'#fff',zIndex:1}}>
                  <div style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:6,height:6,borderRadius:'50%',background:typ?.color,display:'inline-block',flexShrink:0}}/>{pr.p.label}</div>
                  <div style={{fontSize:8,color:'var(--text3)'}}>{pr.p.seller}</div>
                </td>
                <td style={{...TDr,color:'var(--text3)',fontSize:9}}>{selMetric==='budget'?fe(planTot):fn(Math.round(planTot||0))}</td>
                {(r.weekly||[]).map((wk,wi)=>{
                  const planW=(planTot||0)*(wk.frac/wFT);
                  const actW=(platActs[wi]||{})[selMetric]||null;
                  const dw=actW!=null&&planW>0?(actW-planW)/planW:null;
                  const isPast=new Date(weekStartDate(wk.week))<today;
                  return(<td key={wi} style={{...TDc,padding:'2px 3px',background:actW!=null?(dw!=null&&Math.abs(dw)>0.1?(dw>0?'#EAFFE8':'#FFE8E8'):'#F0F8FF'):'transparent'}}>
                    <div style={{fontSize:8,color:'var(--text3)',lineHeight:1.2}}>{selMetric==='budget'?fe(planW):fn(Math.round(planW))}</div>
                    <div style={{fontWeight:actW!=null?700:400,fontSize:9,color:actW!=null?(dw!=null&&Math.abs(dw)>0.1?(dw>0?'var(--green)':'var(--red)'):'var(--text)'):'var(--text3)'}}>
                      {actW!=null?(selMetric==='budget'?fe(actW):fn(Math.round(actW))):(isPast?<span style={{color:'var(--red)',fontSize:8}}>?</span>:'—')}
                    </div>
                  </td>);
                })}
                <td style={{...TDr,fontWeight:700,color:metric.color,fontSize:10}}>{actTot>0?(selMetric==='budget'?fe(actTot):fn(Math.round(actTot))):'—'}</td>
                <td style={{...TDc,fontWeight:700,color:isOff?(delta>0?'var(--green)':'var(--red)'):'var(--text3)',fontSize:10}}>{delta!=null?(delta>=0?'+':'')+((delta*100).toFixed(0))+'%':'—'}</td>
              </tr>);
            })}
          </tbody>
        </table>
      </div>
      <div style={{marginTop:10,fontSize:9,color:'var(--text3)',display:'flex',gap:16,flexWrap:'wrap'}}>
        <span>Each cell: <em>top = plan, bottom = actual</em></span>
        <span style={{color:'var(--green)'}}>■ Green = actual &gt; plan by &gt;10%</span>
        <span style={{color:'var(--red)'}}>■ Red = under-delivering or missing</span>
      </div>
    </>}

    {showUpload&&<UploadModal flight={fl} result={r} onClose={()=>setShowUpload(false)} onImport={handleImport}/>}
  </div>);
}

// ─── EXCEL EXPORT ─────────────────────────────────────────────────────────────
// Client-side XLSX export using SheetJS (loaded dynamically)
async function exportToExcel(flights,allResults,audiences,universe,mode,targetFlightId){
  // Dynamic import of SheetJS
  const XLSX=await import('https://cdn.sheetjs.com/xlsx-0.20.3/package/xlsx.mjs').catch(()=>null);
  if(!XLSX){alert('SheetJS could not be loaded. Try again.');return;}
  const wb=XLSX.utils.book_new();

  function addSheet(name,rows){
    const ws=XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb,ws,name);
    return ws;
  }

  // ── SUMMARY SHEET ──────────────────────────────────────────────────────────
  const brands=[...new Set(flights.map(f=>f.brand||'Brand 1'))];
  const sumRows=[
    ['Platform / Format','Buying Type','Unit','Paid Units (Impr)','CPM / CPC €','Main KPI','KPI abs.','KPI %','Cost per KPI €','Budget €','SHR%'],
  ];
  function buildSummaryRows(bf){
    const groups={};SUMMARY_GROUPS.forEach(g=>{groups[g.key]={label:g.label,types:g.types,rows:{},groupReach:0};});
    let totTrk=0,totAf=0;
    flights.filter(f=>bf==='__TOTAL__'||(f.brand||'Brand 1')===bf).forEach(f=>{
      const r=allResults[f.id];if(!r)return;
      totTrk+=r.tot.trackerCost||0;totAf+=r.tot.agencyFee||0;
      r.platforms.forEach(pr=>{
        const t=pr.p?.type||'banner';
        const gk=t==='video'?'video':t==='sem'?'sem':t==='social'?'social':t==='banner'?'display_std':'display_ns';
        const g=groups[gk];if(!g)return;
        const pid=pr.p.id;
        if(!g.rows[pid])g.rows[pid]={pid,label:pr.p.label,seller:pr.p.seller||'',type:t,budget:0,impr:0,vimpr:0,views:0,clicks:0,reach:0,cpm:pr.cpm||0,frq:pr.frq||0};
        const row=g.rows[pid];row.budget+=pr.bud;row.impr+=pr.impr;row.vimpr+=pr.vimpr;row.views+=pr.views;row.clicks+=pr.clicks;row.reach=sbN([row.reach,pr.reach||0]);
      });
      SUMMARY_GROUPS.forEach(sg=>{const pool=r.platforms.filter(pr=>sg.types.includes(pr.p?.type)).map(pr=>pr.reach||0);groups[sg.key].groupReach=sb2(groups[sg.key].groupReach,sbN(pool));});
    });
    const totBudAll=Object.values(groups).reduce((s,g)=>s+Object.values(g.rows).reduce((ss,r)=>ss+r.budget,0),0);
    const result=[];
    SUMMARY_GROUPS.forEach(sg=>{
      const g=groups[sg.key];const rows=Object.values(g.rows);if(!rows.length)return;
      const gBud=rows.reduce((s,r)=>s+r.budget,0);
      const gImpr=rows.reduce((s,r)=>s+r.impr,0);
      const gVimpr=rows.reduce((s,r)=>s+r.vimpr,0);
      const gViews=rows.reduce((s,r)=>s+r.views,0);
      const gClicks=rows.reduce((s,r)=>s+r.clicks,0);
      const isVideo=sg.key==='video',isSem=sg.key==='sem';
      const mainKpi=isVideo?gViews:isSem?gClicks:gVimpr;
      const kpiLabel=isVideo?'Views':isSem?'Clicks':'Viewable Impr.';
      result.push([g.label,'Mixed','CPM',Math.round(gImpr),gImpr>0?+(gBud/gImpr*1000).toFixed(2):'',kpiLabel,Math.round(mainKpi),(g.groupReach*100).toFixed(1)+'%','',+(gBud).toFixed(2),(totBudAll>0?((gBud/totBudAll)*100).toFixed(1)+'%':'')]);
      rows.forEach(row=>{
        const isV=row.type==='video',isS=row.type==='sem';
        const mkpi=isV?row.views:isS?row.clicks:row.vimpr;
        const mkpiLabel=isV?'Views':isS?'Clicks':'Viewable Impr.';
        const costKpi=mkpi>0?(row.budget/mkpi*1000).toFixed(2):'';
        result.push(['  '+row.label+' ('+row.seller+')',row.buyingType||'Programmatic','CPM',Math.round(row.impr),row.cpm?+row.cpm.toFixed(2):'',mkpiLabel,Math.round(mkpi),(row.impr>0?(mkpi/row.impr*100).toFixed(1)+'%':''),costKpi,+row.budget.toFixed(2),(totBudAll>0?((row.budget/totBudAll)*100).toFixed(1)+'%':'')]);
      });
    });
    result.push(['TOTAL ONLINE','','','','','','','','',+totBudAll.toFixed(2),'100%']);
    if(totTrk>0)result.push(['3rd Party Trackers','','','','','','','','',+totTrk.toFixed(2),'']);
    if(totAf>0)result.push(['Agency Fee','','','','','','','','',+totAf.toFixed(2),'']);
    return result;
  }

  [...brands,'__TOTAL__'].forEach(brand=>{
    const bRows=buildSummaryRows(brand);
    if(!bRows.length)return;
    const sheetRows=[['Brand: '+(brand==='__TOTAL__'?'ALL BRANDS':brand)],[''],...sumRows,...bRows];
    addSheet(brand==='__TOTAL__'?'Summary-ALL':(brand.slice(0,20)+'-Sum'),sheetRows);
  });

  // ── DETAILS SHEETS — one per flight (or selected) ─────────────────────────
  const toExport=targetFlightId?flights.filter(f=>f.id===targetFlightId):flights;
  toExport.forEach(fl=>{
    const r=allResults[fl.id];if(!r||!r.platforms.length)return;
    const wkHeaders=['Platform','Format','Seller','TOTAL',...r.weekly.map(wk=>'W'+wk.week+' '+fdate(weekStartDate(wk.week))),...MONTHS.map((m,mi)=>{const ws=MONTH_WEEKS[mi];return r.weekly.some(wk=>ws.includes(wk.week))?MN[mi]:null;}).filter(Boolean)];
    const wFT=r.weekly.reduce((s,wk)=>s+wk.frac,0)||1;
    const metrics=[
      {label:'IMPRESSIONS',fn:pr=>({tot:pr.impr,weeks:r.weekly.map(wk=>pr.impr*(wk.frac/wFT))})},
      {label:'FRQ (weekly)',fn:pr=>({tot:pr.frq,weeks:r.weekly.map(()=>pr.frq)})},
      {label:'UNIQUE COOKIES',fn:pr=>({tot:pr.uniqueCookies||0,weeks:r.weekly.map(wk=>(pr.uniqueCookies||0)*(wk.frac/wFT))})},
      {label:'TA REAL USERS (cookies/ruc)',fn:pr=>({tot:(pr.uniqueCookies||0)/(pr.p.real_users_coef||2),weeks:r.weekly.map(wk=>((pr.uniqueCookies||0)/(pr.p.real_users_coef||2))*(wk.frac/wFT))})},
      {label:'CLICKS',fn:pr=>({tot:pr.clicks,weeks:r.weekly.map(wk=>pr.clicks*(wk.frac/wFT))})},
      {label:'VIEWS',fn:pr=>({tot:pr.views,weeks:r.weekly.map(wk=>pr.views*(wk.frac/wFT))})},
      {label:'VIEWABLE IMPRESSIONS',fn:pr=>({tot:pr.vimpr,weeks:r.weekly.map(wk=>pr.vimpr*(wk.frac/wFT))})},
    ];
    const detRows=[['Flight: '+fl.brand+' — '+fl.label],['Period: '+fdate(fl.startDate)+' – '+fdate(fl.endDate)],['Budget: '+(fl.budget||0)+' EUR'],['']];
    metrics.forEach(m=>{
      detRows.push([m.label]);
      detRows.push(wkHeaders);
      r.platforms.forEach(pr=>{
        const d=m.fn(pr);
        const monthTots=MONTHS.map((_,mi)=>{const ws=MONTH_WEEKS[mi];const hasData=r.weekly.some(wk=>ws.includes(wk.week));if(!hasData)return null;return r.weekly.filter(wk=>ws.includes(wk.week)).reduce((s,wk)=>s+d.weeks[r.weekly.findIndex(x=>x.week===wk.week)],0);}).filter(v=>v!==null);
        detRows.push([pr.p.label,pr.p.type,pr.p.seller||'',+d.tot.toFixed(1),...d.weeks.map(v=>+v.toFixed(1)),...monthTots.map(v=>+v.toFixed(1))]);
      });
      // total row
      const totTot=r.platforms.reduce((s,pr)=>s+m.fn(pr).tot,0);
      const totWeeks=r.weekly.map((_,wi)=>r.platforms.reduce((s,pr)=>s+m.fn(pr).weeks[wi],0));
      const totMo=MONTHS.map((_,mi)=>{const ws=MONTH_WEEKS[mi];return r.weekly.some(wk=>ws.includes(wk.week))?r.weekly.filter(wk=>ws.includes(wk.week)).reduce((s,wk)=>s+totWeeks[r.weekly.findIndex(x=>x.week===wk.week)],0):null;}).filter(v=>v!==null);
      detRows.push(['TOTAL','','',+totTot.toFixed(1),...totWeeks.map(v=>+v.toFixed(1)),...totMo.map(v=>+v.toFixed(1))]);
      detRows.push(['']);
    });
    // Cumulative reach
    detRows.push(['CUMULATIVE vRCH%']);
    detRows.push(['Week','Date','Days active','Weekly +RCH%','Cumulative RCH%','Est. TA Users reached']);
    let cum=0;
    r.weekly.forEach(wk=>{cum=sb2(cum,wk.reach||0);detRows.push(['W'+wk.week,weekStartDate(wk.week),Math.round(wk.frac*7),(wk.reach*100).toFixed(2)+'%',(cum*100).toFixed(2)+'%',Math.round(cum*universe)]);});
    addSheet((fl.brand.slice(0,10)+'-'+fl.label.slice(0,10)).replace(/[^\w]/g,'_').slice(0,30),detRows);
  });

  XLSX.writeFile(wb,'DigitalMediaplan_'+new Date().toISOString().slice(0,10)+'.xlsx');
}

// ─── M1 HELPERS ───────────────────────────────────────────────────────────────
function fgToType(fg){const m={video:'video',non_standard_display:'nonstandard',sem:'sem',social:'social'};return m[fg]||'banner';}
// Map client override fields (DB field names) to frontend catalogue field names
function _mapClientOvr(ovr){
  if(!ovr)return{};
  const map={cpm_eur:'base_cpm',cpc_eur:'cpc',default_ctr:'ctr',default_vtr:'vtr',default_weekly_frq:'weekly_frq',default_monthly_frq:'monthly_frq'};
  const out={};
  Object.entries(ovr).forEach(([k,v])=>{out[map[k]||k]=v;});
  return out;
}
function typePri(t){const P={video:{awareness:9,views:10,traffic:5,conversion:2},banner:{awareness:5,views:1,traffic:7,conversion:4},branding:{awareness:8,views:1,traffic:5,conversion:2},nonstandard:{awareness:7,views:1,traffic:6,conversion:3},social:{awareness:7,views:3,traffic:9,conversion:8},sem:{awareness:1,views:1,traffic:10,conversion:10}};return P[t]||P.banner;}

// ─── MAIN ENGINE ──────────────────────────────────────────────────────────────

function DigitalQuickWizard({onDone,onCancel,catalogue}){
  const[budget,setBudget]=React.useState(50000);
  const[brands,setBrands]=React.useState(1);
  const[objective,setObjective]=React.useState('awareness');
  const[flightMode,setFlightMode]=React.useState('4');
  function generate(){
    const brandNames=Array.from({length:brands},(_,i)=>'Brand '+(i+1));
    const budPerBrand=Math.round(budget/brands);
    const newFlights=[];let idx=0;
    brandNames.forEach(brand=>{
      if(flightMode==='allyear'){
        newFlights.push({...mkFlight(idx++,brand,catalogue),budget:budPerBrand,objective,startWeek:1,weeks:52,platforms:smartAllocate(objective,budPerBrand,catalogue)});
      } else {
        const n=parseInt(flightMode)||1,budPerFlight=Math.round(budPerBrand/n),spacing=Math.floor(52/n);
        for(let fi=0;fi<n;fi++)newFlights.push({...mkFlight(idx++,brand,catalogue),budget:budPerFlight,objective,startWeek:fi*spacing+1,weeks:4,platforms:smartAllocate(objective,budPerFlight,catalogue)});
      }
    });
    onDone(newFlights);
  }
  const IS={width:'100%',padding:'6px 10px',borderRadius:6,border:'1px solid var(--border)',background:'var(--surface2)',fontSize:12,color:'var(--text)',outline:'none'};
  const LS={fontSize:10,color:'var(--text3)',fontWeight:600,textTransform:'uppercase',letterSpacing:'.04em',marginBottom:4,display:'block'};
  return(<div>
    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
      <div><label style={LS}>Total Budget €</label><input type="text" inputMode="numeric" style={IS} value={budget} onFocus={e=>e.target.select()} onChange={e=>setBudget(parseFloat(e.target.value)||0)}/></div>
      <div><label style={LS}>Number of Brands</label><select style={IS} value={brands} onChange={e=>setBrands(+e.target.value)}>{[1,2,3,4,5,6].map(n=><option key={n} value={n}>{n}</option>)}</select></div>
    </div>
    <div style={{marginBottom:12}}><label style={LS}>Objective</label>
      <div style={{display:'flex',gap:6}}>{OBJECTIVES.map(o=><button key={o.id} onClick={()=>setObjective(o.id)} style={{flex:1,padding:'6px 4px',borderRadius:6,border:'1px solid '+(objective===o.id?o.color:'var(--border)'),background:objective===o.id?o.color:'var(--surface2)',color:objective===o.id?'#fff':'var(--text2)',fontSize:10,fontWeight:600,cursor:'pointer'}}>{o.label}</button>)}</div>
    </div>
    <div style={{marginBottom:20}}><label style={LS}>Flights per Brand</label>
      <div style={{display:'flex',gap:6}}>{[['allyear','All Year'],['1','1'],['2','2'],['3','3'],['4','4']].map(([v,l])=><button key={v} onClick={()=>setFlightMode(v)} style={{flex:1,padding:'6px 4px',borderRadius:6,border:'1px solid '+(flightMode===v?'var(--gold)':'var(--border)'),background:flightMode===v?'var(--gold)':'var(--surface2)',color:flightMode===v?'#fff':'var(--text2)',fontSize:10,fontWeight:600,cursor:'pointer'}}>{l}</button>)}</div>
    </div>
    <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
      <button onClick={onCancel} style={{padding:'7px 16px',borderRadius:6,border:'1px solid var(--border)',background:'var(--surface2)',fontSize:12,cursor:'pointer'}}>Cancel</button>
      <button onClick={generate} style={{padding:'7px 20px',borderRadius:6,border:'none',background:'var(--gold)',color:'#fff',fontSize:12,fontWeight:700,cursor:'pointer'}}>⚡ Generate</button>
    </div>
  </div>);
}

export default function DigitalPlanningEngine({initialData,onSave,clientId,planStatus,readOnly}){
  const[showWizard,setShowWizard]=useState(false);
  const[flights,setFlights]=useState(()=>{if(initialData?.flights?.length)return initialData.flights;return[mkFlight(0,initialData?.brands?.[0]?.name||'Brand 1',DEFAULT_PLATFORMS)];});
  const[selId,setSelId]=useState(()=>flights[0]?.id||null);
  const[rightTab,setRightTab]=useState('flowchart');
  const[saved,setSaved]=useState(false);
  const[inputsOpen,setInputsOpen]=useState(false);
  const[audMgrOpen,setAudMgrOpen]=useState(false);
  const[addingBrand,setAddingBrand]=useState(false);
  const[newBrand,setNewBrand]=useState('');
  const[catalogue,setCatalogue]=useState(()=>initialData?.catalogue&&initialData.catalogue.length>0?initialData.catalogue:DEFAULT_PLATFORMS.map(p=>({...p})));
  const[universe,setUniverse]=useState(()=>{const u=initialData?.universe||DEF_UNIVERSE;return u===531192?DEF_UNIVERSE:u;});
  const[audiences,setAudiences]=useState(()=>{const auds=(initialData?.audiences&&initialData.audiences.length>0)?initialData.audiences:DEFAULT_AUDIENCES.map(a=>({...a}));return auds.map(a=>({...a,universe:a.universe===531192?1887226:a.universe}));});
  const[actualsData,setActualsData]=useState(()=>initialData?.actualsData||{});

  useEffect(()=>{
    // Three-layer merge: Global DB → Client overrides → Plan snapshot
    const globalFetch=fetch(`${API}/platforms/?active_only=false`).then(r=>r.ok?r.json():[]).catch(()=>[]);
    const clientFetch=clientId?fetch(`${API}/clients/${clientId}/catalogue-overrides`).then(r=>r.ok?r.json():{}).catch(()=>({})):Promise.resolve({});
    // Also load audiences from DB on mount
    const audUrl=clientId?`${API}/audiences?client_id=${clientId}`:`${API}/audiences`;
    const audFetch=fetch(audUrl).then(r=>r.ok?r.json():[]).catch(()=>[]);

    Promise.all([globalFetch,clientFetch,audFetch]).then(([data,clientOverrides,dbAuds])=>{
      if(dbAuds&&dbAuds.length){
        const dbMapped=dbAuds.map(a=>({id:'db_'+a.id,_db_id:a.id,name:a.name,universe:a.universe,platComp:a.plat_comp||{},scope:a.scope,client_id:a.client_id}));
        const dbByName={};dbMapped.forEach(a=>{dbByName[a.name.toLowerCase().trim()]=a;});
        setAudiences(prev=>{
          // Plan snapshot always wins for universe + platComp — DB provides _db_id and scope
          const merged=dbMapped.map(dbA=>{
            const saved=prev.find(a=>a.name.toLowerCase().trim()===dbA.name.toLowerCase().trim());
            if(saved){
              return{
                ...dbA,                          // DB base (id, _db_id, scope)
                universe:saved.universe??dbA.universe,  // plan universe wins
                platComp:Object.keys(saved.platComp||{}).length>0?saved.platComp:dbA.platComp,  // plan platComp wins if set
                _has_plan_override:saved.universe!==dbA.universe||Object.keys(saved.platComp||{}).length>0,
              };
            }
            return dbA;
          });
          const planOnly=prev.filter(a=>!a._db_id&&!dbByName[a.name.toLowerCase().trim()]);
          return [...merged,...planOnly];
        });
      }
    }).catch(()=>{});

    Promise.all([globalFetch,clientFetch]).then(([data,clientOverrides])=>{
      if(!data||!data.length)return;
      const labelToLegacyId={};
      DEFAULT_PLATFORMS.forEach(dp=>{labelToLegacyId[dp.label.toLowerCase().trim()]=dp.id;});
      // Index saved plan catalogue by _db_id
      const savedById={};
      (initialData?.catalogue||[]).forEach(p=>{if(p._db_id)savedById[String(p._db_id)]=p;});
      const dbPlats=data.map(p=>{
        const legacyId=labelToLegacyId[p.name.toLowerCase().trim()]||null;
        // Layer 1: global
        const base={id:'db_'+p.id,_db_id:p.id,_legacy_id:legacyId,label:p.name,seller:p.seller||'',type:fgToType(p.format_group),base_cpm:p.cpm_eur||0,cpc:p.cpc_eur||null,viewability:p.viewability||0.7,audience_comp:p.audience_comp||0.65,real_users_coef:p.real_users_coef||2.0,ctr:p.default_ctr||0.001,vtr:p.default_vtr||null,weekly_frq:p.default_weekly_frq||4,monthly_frq:p.default_monthly_frq||16,reach_pool:p.reach_pool||'display',active:p.active!==false,priority:typePri(fgToType(p.format_group),p.reach_pool||null),seasonal_cpm:p.seasonal_cpm||null,max_impressions:p.max_impressions||null,cvr:p.cvr||null};
        // Layer 2: client overrides
        const cOvr=clientOverrides[String(p.id)]||{};
        const withClient=Object.keys(cOvr).length?{...base,..._mapClientOvr(cOvr),_client_overrides:cOvr}:base;
        // Layer 3: saved plan snapshot wins for existing platforms
        const saved=savedById[String(p.id)];
        if(saved)return{...withClient,...saved,id:'db_'+p.id,_db_id:p.id,_legacy_id:legacyId,_client_overrides:cOvr,_has_plan_override:true};
        return withClient;
      });
      const legacyToDbId={};
      dbPlats.forEach(p=>{if(p._legacy_id)legacyToDbId[p._legacy_id]=p.id;});
      const dbLabelToId={};
      dbPlats.forEach(p=>{dbLabelToId[p.label.toLowerCase().trim()]=p.id;});
      (initialData?.catalogue||[]).forEach(p=>{
        if(!p._db_id&&p.label){const dbId=dbLabelToId[p.label.toLowerCase().trim()];if(dbId)legacyToDbId[p.id]=dbId;}
      });
      setFlights(prev=>prev.map(f=>({...f,platforms:f.platforms.map(pe=>({...pe,platformId:legacyToDbId[pe.platformId]||pe.platformId}))})));
      const savedCustom=(initialData?.catalogue||[]).filter(x=>!x._db_id);
      // Deduplicate: if savedCustom has same label as a dbPlat, skip it
      const dbLabels=new Set(dbPlats.map(p=>p.label.toLowerCase().trim()));
      const trulyCustom=savedCustom.filter(p=>!dbLabels.has((p.label||'').toLowerCase().trim()));
      setCatalogue([...dbPlats,...trulyCustom]);
    });
  },[]);

  const allResults=useMemo(()=>{
    const res={};
    flights.forEach(f=>{
      const aud=audiences.find(a=>a.id===f.audienceId);
      res[f.id]=calcFlight(f,catalogue,aud?aud.universe:universe,aud||null);
    });
    return res;
  },[flights,catalogue,universe,audiences]);

  const planTot=useMemo(()=>{
    const t={budget:0,impressions:0,vimpressions:0,views:0,clicks:0,reach:0};
    Object.values(allResults).forEach(r=>{t.budget+=r.tot.budget;t.impressions+=r.tot.impressions;t.vimpressions+=r.tot.vimpressions;t.views+=r.tot.views;t.clicks+=r.tot.clicks;t.reach=sb2(t.reach,r.tot.reach);});
    return t;
  },[allResults]);

  function updFlight(id,patch){setFlights(prev=>prev.map(f=>f.id===id?{...f,...patch}:f));setSaved(false);}
  function addFlight(brand){setFlights(prev=>[...prev,mkFlight(prev.length,brand,catalogue)]);setSaved(false);}
  function cloneFlight(id){const f=flights.find(x=>x.id===id);if(!f)return;setFlights(prev=>[...prev,{...f,id:mkId(),label:f.label+' (copy)'}]);setSaved(false);}
  function removeFlight(id){setFlights(prev=>prev.filter(f=>f.id!==id));setSaved(false);}
  function renameBrand(old_,new_){setFlights(prev=>prev.map(f=>f.brand===old_?{...f,brand:new_}:f));setSaved(false);}
  function handleSave(){if(onSave)onSave({flights,actualsData,audiences,catalogue,universe});setSaved(true);setTimeout(()=>setSaved(false),2500);}
  const handleExportExcel=useCallback((flightId)=>{exportToExcel(flights,allResults,audiences,universe,'details',flightId);},[flights,allResults,audiences,universe]);
  const handleExportSummaryExcel=useCallback(()=>{exportToExcel(flights,allResults,audiences,universe,'summary',null);},[flights,allResults,audiences,universe]);

  const brands=[...new Set(flights.map(f=>f.brand||'Brand 1'))];

  return(<div className="dpe-root">
    {/* TOP BAR */}
    <div className="dpe-topbar">
      <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap',flex:1}}>
        {[{l:'Budget',v:fe(planTot.budget)},{l:'Impressions',v:fmil(planTot.impressions)},{l:'vImpressions',v:fmil(planTot.vimpressions)},{l:'vRCH%',v:fp(planTot.reach),hi:true},{l:'Views',v:fmil(planTot.views)},{l:'Clicks',v:fmil(planTot.clicks)}].map(k=>(
          <div key={k.l} className={'kpi-chip'+(k.hi?' kpi-hi':'')}><span className="kpi-label">{k.l}</span><span className="kpi-val">{k.v}</span></div>
        ))}
      </div>
      <div style={{display:'flex',gap:6,flexShrink:0,alignItems:'center'}}>
        <button className="btn btn-ghost btn-sm" onClick={()=>setAudMgrOpen(true)}>👥 Audiences</button>
        <button className="btn btn-ghost btn-sm" onClick={()=>setInputsOpen(true)}>⚙ Inputs</button>
        <button className={'btn btn-sm'+(saved?' btn-saved':' btn-primary')} onClick={handleSave}>{saved?'✓ Saved':'Save'}</button>
      </div>
    </div>

    <div className="dpe-body">
      {/* LEFT PANEL */}
      <div className="dpe-left">
        <div style={{padding:'6px 10px',display:'flex',gap:6,borderBottom:'1px solid var(--border)',alignItems:'center'}}>
          <span style={{fontSize:10,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'.06em',flex:1}}>Flights</span>
          {addingBrand
            ?<div style={{display:'flex',gap:4}}><input autoFocus className="inp" value={newBrand} placeholder="Brand name…" style={{width:110,fontSize:10}} onChange={e=>setNewBrand(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'&&newBrand.trim()){addFlight(newBrand.trim());setNewBrand('');setAddingBrand(false);}if(e.key==='Escape')setAddingBrand(false);}}/><button className="btn btn-xs btn-primary" onClick={()=>{if(newBrand.trim()){addFlight(newBrand.trim());setNewBrand('');setAddingBrand(false);}}}>✓</button><button className="btn btn-xs btn-ghost" onClick={()=>setAddingBrand(false)}>✕</button></div>
            :<><button className="btn btn-xs btn-ghost" onClick={()=>setAddingBrand(true)}>+ Brand</button>
            <button className="btn btn-ghost btn-xs" style={{background:"var(--gold)",color:"#fff",border:"none"}} onClick={()=>setShowWizard(true)}>⚡ Quick</button></>
          }
        </div>
        <div style={{overflowY:'auto',flex:1}}>
          {brands.map(brand=>(<div key={brand}>
            <BrandHeader brand={brand} onRename={n=>renameBrand(brand,n)} onAddFlight={()=>addFlight(brand)}/>
            {flights.filter(f=>f.brand===brand).map(f=>(
              <FlightCard key={f.id} flight={f} result={allResults[f.id]} isSelected={selId===f.id}
                onSelect={setSelId} onUpdate={p=>updFlight(f.id,p)} onClone={()=>cloneFlight(f.id)} onRemove={()=>removeFlight(f.id)}
                canRemove={flights.length>1} catalogue={catalogue} audiences={audiences}/>
            ))}
          </div>))}
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div className="dpe-right">
        <div className="dpe-tabs">
          {[['flowchart','📊 Flowchart'],['summary','📋 Summary'],['details','🔍 Details'],['actuals','📌 Actuals']].map(([t,l])=>(
            <button key={t} className={'tab-btn'+(rightTab===t?' on':'')} onClick={()=>setRightTab(t)}>{l}</button>
          ))}
        </div>
        <div style={{flex:1,overflowY:'auto'}}>
          {rightTab==='flowchart'&&<AnnualFlowchart flights={flights} allResults={allResults}/>}
          {rightTab==='summary'&&<SummaryTab flights={flights} allResults={allResults} onExportExcel={handleExportSummaryExcel}/>}
          {rightTab==='details'&&<DetailsTab flights={flights} allResults={allResults} catalogue={catalogue} onExportExcel={handleExportExcel}/>}
          {rightTab==='actuals'&&<ActualsTab flights={flights} allResults={allResults} actualsData={actualsData} setActualsData={setActualsData} catalogue={catalogue}/>}
        </div>
      </div>
    </div>

    {inputsOpen&&<InputsDrawer open={inputsOpen} onClose={()=>setInputsOpen(false)} catalogue={catalogue} setCatalogue={setCatalogue} universe={universe} setUniverse={setUniverse} clientId={clientId} planStatus={planStatus} audiences={audiences} setAudiences={setAudiences}/>}
    {audMgrOpen&&<AudienceManager audiences={audiences} setAudiences={setAudiences} catalogue={catalogue} onClose={()=>setAudMgrOpen(false)} clientId={clientId}/>}
    {showWizard&&(
      <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.7)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={e=>{if(e.target===e.currentTarget)setShowWizard(false);}}>
        <div style={{background:'var(--surface)',border:'1px solid var(--gold-border)',borderRadius:12,padding:28,width:480,maxWidth:'92vw'}}>
          <div style={{fontWeight:700,fontSize:15,color:'var(--gold)',marginBottom:4}}>⚡ Quick Setup</div>
          <div style={{fontSize:11,color:'var(--text3)',marginBottom:20}}>Auto-generate brands & flights. Existing flights will be replaced.</div>
          <DigitalQuickWizard catalogue={catalogue} onDone={(f)=>{setFlights(f);setShowWizard(false);setSaved(false);}} onCancel={()=>setShowWizard(false)}/>
        </div>
      </div>
    )}
  </div>);
}
